/* Serial Seal — seal compositor, PORTRAIT and LANDSCAPE.
 *
 * THE DIE: 0.75" × 1.77" (19.05 × 44.958 mm). The same die runs either way up:
 *   portrait  19.05 wide × 44.958 tall
 *   landscape 44.958 wide × 19.05 tall
 * Orientation is a customer choice, so every design has to compose both ways.
 * All geometry lives in layout() — nothing downstream hardcodes a coordinate,
 * which is what lets one template render either way round.
 *
 * THE STOCK IS THE DESIGN. Silver holographic vinyl, CMYK ink, NO WHITE INK.
 * Printed = dark, UNPRINTED = live rainbow foil. Two plate modes:
 *   'dark' — flood-inked plate; type light on ink.
 *   'holo' — no flood; ink lands only where drawn and type prints dark.
 * Foil INSIDE an inked area is a hole cut with an evenodd path — never a white
 * fill (there is no white ink, and svglib on the print side does evenodd, not
 * masks). The one white rect on the seal is the QR backing, for scan contrast.
 */
window.SS_SEAL = (function () {
  'use strict';

  /* ═══ LABEL SIZES — THE ONE SOURCE OF TRUTH (owner spec 2026-08-28) ═══
     Physical size data lives HERE and nowhere else. longMm/shortMm are the
     die; pxLong/pxShort the production raster at dpi; cornerIn/gapIn the
     physical die-cut. layoutsReady=false hides a size from every consumer
     until its layouts are deliberately composed - never auto-scaled. */
  var SIZES = {
    standard_177x075: {
      id: 'standard_177x075', displayName: 'Standard',
      longIn: 1.77, shortIn: 0.75, longMm: 44.958, shortMm: 19.05,
      pxLong: 1062, pxShort: 450, dpi: 600,
      cornerIn: 0.125, gapIn: 0.155, layoutsReady: true, isDefault: true
    },
    large_250x100: {
      id: 'large_250x100', displayName: 'Large',
      longIn: 2.5, shortIn: 1.0, longMm: 63.5, shortMm: 25.4,
      pxLong: 1500, pxShort: 600, dpi: 600,
      /* gap per the ordered TLP roll (0.2083in), NOT the 0.125in in the draft
         spec - verify on the physical roll at arrival */
      cornerIn: 0.125, gapIn: 0.2083, layoutsReady: true, isDefault: false
    }
  };
  function sizeOf(id) {
    var z = SIZES[String(id || '')];
    return (z && z.layoutsReady) ? z : SIZES.standard_177x075;
  }
  var LONG = SIZES.standard_177x075.longMm, SHORT = SIZES.standard_177x075.shortMm;
  /* rich black, not charcoal — the owner wants ink at 1000%: what prints
     dark must LAND dark, the contrast against live foil is the design */
  var INK = '#04050a';
  var LIGHT = '#e9eaf2';

  /* GROUND LUMINANCE - LEGIBILITY AS A MEASUREMENT
     ==============================================
     A full-bleed design draws the customer's type as a WHITE KNOCKOUT: on this
     stock #ffffff is NO INK, i.e. bare rainbow foil. Over dark artwork that is
     the whole design. Over PALE artwork there is nothing for it to read
     against, and the seal ships with invisible type (owner report 2026-09-14,
     order ss1110, Cosmic on the Toploader Banner: "too much white on light bg").

     So the ground stops being an assumption and becomes a MEASUREMENT. Every
     full-bleed background is reduced to a tiny luminance grid - 6x4 cells for a
     landscape crop, 4x6 for a portrait one, cover-cropped to the die exactly
     the way preserveAspectRatio="xMidYMid slice" crops it further down. Two
     numbers come off that grid:

       mean - how bright the artwork is overall
       hi   - the mean of its brightest THIRD, i.e. how bright the art GETS in
              the region where a line of type could actually land

     Either one over its threshold and the ground is LIGHT.

     WHERE THE NUMBERS COME FROM. ONE PLACE, ON PURPOSE:

       LUMA - baked from the published rasters by tools/bake_luma.py.
       Synchronous, deterministic, no network, no CORS, no decode timing,
       identical in the builder and in the press sidecar. groundLevel() reads
       this table and NOTHING ELSE; the reasons are written out beside it.

     sampleLuma() / warmLuma() below are the SAME sampler, exposed so that
     tools/bake_luma.py can drive it in a headless browser to produce the
     table - baked and sampled therefore cannot disagree by construction. They
     are NOT consulted by build(): a runtime sample lands at a different moment
     in the builder (which re-mounts on every keystroke) than in the press
     sidecar (which calls build() once per seal in a loop), so preferring it
     would let "what the customer approved" and "what came off the press"
     differ, and would split one batch into two kinds of seal.

     A DESIGN WITH NO BAKED ENTRY IS ITS OWN ANSWER, not a guess in either
     direction - see groundLevel(). It gets the half of the treatment that is
     harmless on dark artwork and never the half that is not, and it logs once
     to the console asking for a bake.

     THE TREATMENT IS NOT ONE THING, because the two families of full-bleed
     layout put different things under their type. A bg zone already draws a
     translucent ink panel and gradient washes, so its type stays a knockout
     and what changes is the DEPTH of that backing plus a small ink bloom
     hugging the letterforms. A banner deliberately draws nothing at all, so
     the only contrast available to it is the type's OWN polarity and on
     measured-light ground the knockout FLIPS TO INK. Which branch a run takes
     is settled per layout, with one exception that overrides both: a run
     marked data-onink sits on opaque ink of a known colour (the wordmark in
     the royal-blue domain bar, the GRADE tablet) and is left completely alone.

     NOTHING HERE ASSUMES WHITE INK. On this stock #ffffff is the ABSENCE of
     ink - bare rainbow foil - which is what the type on these layouts has
     always been. Everything added is INK, the press black the rest of the seal
     is already drawn in, and every colour still goes through inkSafe(). */
  var LUMA_GW = 6, LUMA_GH = 4;       /* landscape grid; portrait is 4x6 */
  /* THRESHOLDS, from the measurement and not from taste. tools/bake_luma.py
     over the whole live catalog (34 designs, 2026-09-14) separates cleanly into
     two clusters with a wide empty band between them:

       legible today, brightest of them    Surge   mean 104.7  hi 141.6
       ---- nothing lands in here ----
       the reported failures, darkest       Heat   mean 135.2  hi 181.6

     These sit in the middle of that band, ~15 of mean and ~19 of hi clear of
     both clusters, and they reproduce the owner's own hand triage exactly:
     the nine designs flagged on 2026-09-14 all read LIGHT, the nineteen called
     fine all read dark. The two DESIGNS PUBLISHED SINCE that triage - Pagoda
     Garden and Psychic Bloom - also read LIGHT, which is the whole point of
     measuring rather than listing. */
  var LUMA_T_MEAN = 120, LUMA_T_HI = 160;
  var LUMA_RUN = {};                  /* url -> cells, sampled at runtime */
  var LUMA_PEND = {};                 /* url -> true while a sample is in flight */

  /* the baked table: design key -> channel -> hex grid.
     REGENERATE with:  python tools/bake_luma.py --write
     The block between the markers is rewritten wholesale - do not hand-edit. */
  /* @luma-table */
  /* BAKED by tools/bake_luma.py - re-run it whenever the studio
     publishes or re-crops a design. key -> crop channel -> hex grid
     (6x4 landscape / 4x6 portrait, one byte of luminance per cell). */
  var LUMA = {
    'p_aqua_storm_211f': { ls:'7baccecdaa7872a5c9c9a1806792bdbf93705a7aa7ab7d6e', lf:'7baccecdaa7872a5c9c9a1806792bdbf93705a7aa7ab7d6e', pf:'597271556eb4b57181c5c689698d91705c6b6f5c63a7a36b', p:'597271556eb4b57181c5c689698d91705c6b6f5c63a7a36b' },
    'p_bloom_0bc7': { ls:'0714392e1006081b6c69190707195a93230706112d471a07', lf:'0714392e1006081b6c69190707195a93230706112e481a07', pf:'07070706131f1911346d66322f59884f101a211806080807', p:'07070706131f1911346d66322f59884f101a211806080807' },
    'p_bubble_2ea2': { ls:'1135336936112522375914123f3f2c3010302c27292e2d22', lf:'1135336936112522375914123f3f2c3010302c27292e2d22', pf:'0c0e110a2923372921345115382b382d231216131c0f0a19', p:'0c0e110a2923372921345115382b382d231216131c0f0a19' },
    'p_circuit_4efe': { ls:'111e2e332614192531322b1913213c342919161e31302e16', lf:'111e2e332614192531322b1913213c342919161e31302e16', pf:'15131619202422222834352b2d393929242f2e2510101216', p:'15131619202422222834352b2d393929242f2e2510101216' },
    'p_diamond_dust_4100': { ls:'23314b4a31232c365e60352b244a61604c26242d51532c22', lf:'23314b4a31232c365e60352b244a61604c26242d51532c22', pf:'16181818222627233958583a465c5c47232c2c2315151414', p:'16181818222627233958583a465c5c47232c2c2315151414' },
    'p_divine_b392': { ls:'acc7d6d4c5acb2cccfd3cdb2b2cdd0d1cfb2acc9d3d4caac', lf:'acc7d6d4c5acb2cccfd3cdb2b2cdd0d1cfb2acc9d3d4caac', pf:'abb0b0abc0c9c9c1c9dadbc9d1d2d2d3cacfcecbacb2b2ac', p:'abb0b0abc0c9c9c1c9dadbc9d1d2d2d3cacfcecbacb2b2ac' },
    'p_ember_d7b1': { ls:'0e151818150e111a22231a1112222d2c2113132736352514', lf:'0e151818150e111a22231a1113222d2c2113132736352514', pf:'0c0c0d0c131414141d20201d2c31312a313d3d302028291f', p:'0c0c0d0c131414151d20201d2c31312a313d3d302028291f' },
    'p_fire_phoenix_a157': { ls:'8a2c3374a341a747296f91779c89356994c3724e345aa9b2', lf:'912c38779c44a24d28728a828e90356a9abc774e355ba4b2', pf:'4c84b5a8478ec8b56f4a8f68745d82ad6c8dac79476b874c', p:'5ab6c5ba4087c1bf594986716e5471a66b88aca06c99a67c' },
    'p_flux_12e6': { ls:'0e2d3e552611112b524d371212304f4a3d150f3240583b17', lf:'0e2d3e552611112b524d371212304f4a3d150f3240583b17', pf:'171d151329313d2e42515843474c4f523043454a132a2916', p:'171d151329313d2e42515843474c4f523043454a132a2916' },
    'p_frost_9372': { ls:'171e2d2c211612283341301b142238342a1f112d2a2c2618', lf:'171e2d2c211612283341301b142238342a1f112d2a2c2618', pf:'17171811202d261e2a353c3a2d3331332d23252b15171218', p:'17171811202d261e2a353c3a2d3331332d23252b15171218' },
    'p_garden_be60': { ls:'44b7dfe3dbc75890dee5e0c095aedfebe18173d1e7dd9f75', lf:'44b7dfe3dbc75890dee5e0c095aedfebe18173d1e7dd9f75', pf:'235d353287b0808682d9db99a6dcdba993cdd377477e683f', p:'316746489ac0888c8adbdca8aaddddadabd4d58d53998353' },
    'p_gridiron_1bf9': { ls:'3840474641383e474f4e473e454e56554e45333c42413c33', lf:'3840474641383e474f4e473e454e56554e45333c42413c33', pf:'272727262f32312e474d4c47484e4d472e32312e26262625', p:'272727262f32312e474d4c47484e4d472e32312e26262625' },
    'p_halo_09f5': { ls:'0c174b4a190c0d1f7477210e0d1947471b0e0c152323150c', lf:'0c174b4a190c0d1f7477210e0d1947471b0e0c152323150d', pf:'0c0e0e0c1c2a291b3684843927434428161918160c0e0d0c', p:'0c0e0e0c1c2a291b3684843927434428161818160c0e0d0c' },
    'p_hardwood_prime_bca6': { ls:'3a3f4b4d44403f494a4c4b42414b4c4b493f3c414a483c37', lf:'3a3f4b4d44403f494a4c4b42414b4c4b493f3c414a483c37', pf:'2a2c2e3033363839484c4d4b494c4a46383735322d2b2926', p:'2a2c2e3033363839484c4d4b494c4a46383735322d2b2926' },
    'p_holo_storm_6dc5': { ls:'b1b5d5c0cdb4c5b2c0b8d5b7a8bcb9bbbcd3adcac3ccb1b5', lf:'b1b5d5c0cdb4c5b2c0b8d5b7a8bcb9bbbcd3adcac3ccb1b5', pf:'c1c3bfbcbdc9c9c0bac0c1c0b9c0bfc3cac3c1c6c3beb9c7', p:'c1c3bfbcbdc9c9c0bac0c1c0b9c0bfc3cac3c1c6c3beb9c7' },
    'p_inferno_93b8': { ls:'73281a21286a674c1a2a4b82516b1b277c44433a20293c48', lf:'73271a21286a674c1a2a4a82516b1b277c44433a20293c48', pf:'39472b323b301e376221255d552e2f52342d4b3a30413d31', p:'39472b323b301e376221255d552e2f52342d4b3a30413d31' },
    'p_kaleido_d392': { ls:'0e231f291c0c14272e291d10101d292e27140c1d291f230e', lf:'0e231f291c0c14262e291d10101d292e27140c1d291f230e', pf:'0c10140e1c1d272329292e1f1f2e292923271d1d0e14100c', p:'0c10140e1c1d272329292e1f1f2e292923271d1d0e14100c' },
    'p_lightning_b52d': { ls:'3a688db58143538689774f485c74507b8c522b74946b752d', lf:'3a688db58243538689774f485c74507a8c522b74946b752d', pf:'0e0f140f3664713e597b76593d6c693d34323232100d0f0f', p:'0e0f140f3664713e597b76593d6c693d34323232100d0f0f' },
    'p_mirage_c95d': { ls:'83c0c4bcb6939eb8c5d2c4898eb1d4d8aa8b85bbcdb0a996', lf:'83c0c4bcb6939fb8c5d3c4898eb1d4d8aa8b85bbcdb0a996', pf:'7b818f93b4c5bcadc1c5cccac8d9d2baadaba8b18a99a192', p:'7b818f93b4c5bcadc1c5cccac8d9d2baadaba8b18a99a192' },
    'p_moonlit_eclipse_0336': { ls:'161a533214131d0d1f0f1d0e1812162f270b0b2a0f291a0f', lf:'161a533214121d0d1f0f1d0e1812162f270b0b2a0f291a0f', pf:'08050f0a130e1b1c1b2820171a192c15170d291b0f0b120f', p:'08050f0a130e1b1c1b2820171a192c15170d291b0f0b120f' },
    'p_nova_b324': { ls:'32774836393b49593a3f433f52414449483c38464c4d4b3f', lf:'32774836393b49593a3f433f52414449483c38464c4d4b3f', pf:'1f1d1c3622497b875965493b454a4c4d8eb192465039201c', p:'1f1d1c3622497b875965493b454a4c4d8eb192465039201c' },
    'p_one_a381': { ls:'2c152813343c191831132921122c444b391d1c3f1b464313', lf:'2c152813343c191831132921122c444b391d1c3f1b464313', pf:'0608110617321f27302016343026312d2020222513222518', p:'0608110617321f27302016343026312d2020222513222518' },
    'p_onyx_4969': { ls:'1f353a392624203a46472d221d32473b2e211b313a3c271f', lf:'1f353a392624203a46472d221d32473b2e211b313a3c271f', pf:'232725233439312e3c484a3339483c352b343933191d2827', p:'232725233439312e3c484a3339483c352b343933191d2827' },
    'p_orbit_92ec': { ls:'5a623a3b545b5253393d4b287135393b646b86643b3b4a41', lf:'5a623a3b545b5253393d4b287135393b646b86643b3b4a41', pf:'5f51516a54494564593b3b4f663a3d57624c424b56515771', p:'5f51516a54494564593b3b4f663a3d57624c424b56515771' },
    'p_pagoda_garden_5996': { ls:'caedf3ee985ec1e5eef4d6755acbebecc76b24a9ebebc568', lf:'c4e9f3ed9353afe5eef3d5654bc4ebecc25d27a5ebebbb61', pf:'6ca9d7b1b6eae7c9c2ece88654e7e86f47b6b5693a5d6b4e', p:'6ca9d7b1b6eae7c9c2ece88654e7e86f47b6b5693a5d6b4e' },
    'p_pearl_450c': { ls:'b3b2abafb7afb5b2a6a9afaeadb4afadb6baaec5c6a8a9a1', lf:'b3b1abafb7afb5b2a6a9afaeadb4afadb6baadc5c6a8a9a1', pf:'a3b9b5afafb1bbcfaaa7a9acb7b3a9bad4c1b8aabba6ac98', p:'a3b9b5afafb1bbcfaaa7a9acb7b3a9b9d4c1b8aabba5ac98' },
    'p_prismatic_7264': { ls:'a2cbefecf1dfa4dbe8f1efdb81d8f0ecebba7bddf5f0e39b', lf:'a2cbefecf1dfa4dbe8f1efdb81d8f0ecebba7bddf5f0e39b', pf:'cbe4ecb3e0f1eee1c7edefe8bcefebbfbbe5e2a47fbeac92', p:'b1dbe6b2daf1eed4beecefe2b1eee9b9b8e1dc8a69a09a89' },
    'p_psychic_bloom_d6ba': { ls:'9ddff4eee59a8de0edeee5af7ddbedefd47875c5f1ebdf81', lf:'a8e2f4ede5afa2e2edeee5c38ce0edf0e17b76d1f1e9e48b', pf:'76ccca73b7eceab7b8f0ecc58de5ed9b5db6db993b698966', p:'98d9d885c4ecedc9c5f0ecd29ce8efa86dbee1aa3f7fa37b' },
    'p_rift_0596': { ls:'070c23120d08080d26150e09080e25130e08080d22120d08', lf:'070c23120d08080d26150e09080e25130e08080d22120d08', pf:'07220b090c2c110d0f321510103014100c201e0d08161809', p:'07220b090c2c110d0f321510103014100c201e0d08161809' },
    'p_sakura_a5f6': { ls:'91a1aea39493939eaca79d9a989e9bafa79e9593a9afac9d', lf:'91a1aea39493939eaca79d9a989e9bafa79e9593a9afac9d', pf:'939c9b91a5a8a198a6b0a7a092a5b0aba8abaeab93979c96', p:'939c9b91a5a8a198a6b0a7a092a5b0aba8abaeab93979c96' },
    'p_stadium_lights_a65e': { ls:'213432323421203337373320202d33332d201c252929251c', lf:'213432323421203337373320202d33332d201c252929251c', pf:'1c17171c2f22222f343434342c2f2f2c1b1e1d1b10101010', p:'1c17171c2f22222f343434342c2f2f2c1b1e1d1b10101010' },
    'p_static_34c7': { ls:'111c303c3418193c5156401b0e283b3f2f140815262c2b11', lf:'111c303c3418193c5156401b0e283b3f2f140815262c2b11', pf:'1d252f3525363d413a4d51472936383c202724212e3b362f', p:'1d252f3525363d413a4d51472936383c202724212e3b362f' },
    'p_venom_ffe2': { ls:'102638382611122c44442e13112c44432d130e2537372610', lf:'102638382611122c44442e13112c44432d130e2537372610', pf:'12131313262c2b293943443a39424339252a2b260f111110', p:'12131313262c2b293943443a39424339252a2b260f111110' },
    'p_vortex_0c99': { ls:'0b152020160b0d194343190d0d194444190d0b162020160b', lf:'0b152020160b0d194343190d0d194444190d0b162020160b', pf:'0b0d0d0b151919162142432020434420161919160b0d0d0b', p:'0b0d0d0b151919162142432020434420161919160b0d0d0c' }
  };
  /* @/luma-table */

  /* ───────────── WHAT A LIGHT GROUND CHANGES, AND WHY ─────────────
     There is no single answer, because the two families of full-bleed layout
     put different things under their type, and the right move follows THAT and
     not the design. Both branches were chosen by measuring, through
     tools/legibility_proof.py, not by taste - the first arrangement tried here
     flipped everything and made the zoned layouts measurably WORSE.

     The question is asked PER TEXT - "does this run already have ink under
     it?" - and the layout supplies the default answer:

     A. BANNERS - type straight on the artwork.
        topfold / topfold2 / duo-over-art. These layouts deliberately have NO
        backing: "no pills, no panels - white type with a contrasting ink
        OUTLINE straight over the art" (owner 2026-08-10), so the artwork stays
        fully visible. With nothing underneath, the only contrast available is
        the type's OWN polarity, so on light ground the knockout FLIPS TO INK.
        Measured on the Toploader Banner with the ss1110 content, worst run per
        design: 1.19-2.10:1 before, 6.4-7.6:1 after. The duo halves go from
        1.3-3.7:1 to 11-16:1. The artwork is not covered by so much as a pixel.

        The flip carries the white FURNITURE with it (TXT below) - brackets,
        column rules, diamonds, the motif - or half the seal turns and half
        stays. It does NOT carry type that has its OWN opaque ink under it: the
        wordmark reversed out of the royal-blue domain bar, the GRADE tablet.
        Those measure 9.7-11.6:1 as they are and flipping them would build the
        exact mirror of this bug. They say so at the one place that knows, with
        data-onink, instead of being re-derived by a rule out here - and a run
        that says it is SKIPPED, not re-routed. Branch B would give it a
        three-stroke ink bloom it has no use for, and on a 1.1mm bar that bloom
        eats the bar: see the note in lightGroundPass.

        The flipped type also gets a FOIL halo - stacked knockout strokes
        hugging the letters. A design that is light ON AVERAGE still has dark
        passages (Cosmic's spokes), and ink crossing one would vanish the other
        way. This is the self-targeting half: over the pale areas a foil halo
        is invisible, because the art is already bright there; it only shows
        where the art goes dark, which is exactly where it is needed.

     B. BG ZONES - type on the panel and washes the layout already draws.
        The single and fold shapes back their type with a translucent ink panel
        and gradient washes. Those already darken the ground, so the knockout
        is CORRECT there and must stay: measured, the zoned layouts run
        3.96-7.05:1 before, and flipping them to ink dropped them to 1.8-3.7:1.
        What they need is not a different polarity, it is MORE of the backing
        they already have. That comes from two places, in this order:
          1. the layout's OWN panel and washes deepen (the _zo table in build).
             This is the lever that does the heavy lifting, because it is
             already the right shape and the right size, and no amount of it
             can fuse two rows together;
          2. a small translucent INK bloom under the glyphs, widest and
             faintest first, capped at 0.24 em of reach so adjacent rows cannot
             meet. It closes the last of the gap on the weak tail of a wash
             without putting a sticker on the artwork.

     Neither branch assumes an ink this stock does not have. A knockout is the
     ABSENCE of ink (bare rainbow foil), which is what the type on these
     layouts has always been; everything added is INK, the press black the rest
     of the seal is already drawn in.

     HOW WIDE A HALO MAY BE IS SET BY THE ROW SPACING, not by taste. A stroke
     on fill:none reaches HALF its width outward, and these copies paint in
     document order - so a stroke wide enough to cross into the row above lands
     ON TOP of type already drawn there. The first widths tried here did exactly
     that: the brand's second line threw a 1.29 mm foil halo up into the first
     line and ate holes in it (measured: the ink core of NINE reading 0.18
     instead of 0.002, and the run scoring 3.1:1 instead of 13:1). The rows on
     these layouts sit about 1.2 em apart with a 0.72 em cap height, so the
     clear gap is ~0.48 em and nothing here may reach further than that.
     AND THE REAL BUDGET IS HALF THAT, because BOTH neighbours reach. A 0.45 em
     bloom on the row above and a 0.45 em bloom on the row below leave 0.48 em
     of gap to share: they meet in the middle and fuse. That is exactly what
     the first values here did - measured, near-black pixels over the whole die
     went 2.3% -> 18.7% on Cosmic/Landscape Flat and 3.6% -> 19.1% on
     Mint/Landscape Fold, with the outer edge scalloped into per-letter
     contours so it read as a badly die-cut sticker rather than a designed
     shadow. It was also 2-3x more correction than the floor asks for: those
     same layouts now clear it at 5.0-7.2:1 with 3.2-5.6% near-black.

     SO NOTHING HERE MAY REACH FURTHER THAN 0.24 em - half the clear gap - and
     both tables are capped there (0.48 and 0.46 em of stroke width). Within
     that cap the two do different jobs and are tuned differently. The FOIL
     halo is insurance against a dark passage inside a mostly-pale design: it
     is white on white over the pale majority of the art and costs nothing
     there, so it is drawn nearly opaque and only SHOWS where the art goes
     dark, which is the only place it is needed. The INK bloom is a shadow that
     lifts a knockout off a pale ground, and it is deliberately translucent -
     the artwork has to keep reading through it. The bloom no longer carries
     the whole contrast burden on the zoned layouts: the layout's own panel and
     washes deepen on light ground (see _zo in build), which is area-bounded
     furniture and cannot fuse between rows.

                      mult  opacity  cap mm  */
  /* THE ONE PLACE A 1.30mm HALO IS TOO WIDE IS THE SLAB, where a 2.8-3.6mm
     serial runs 0.4mm above the 1.02mm domain bar and the halo lit the art
     around it (SERIALSEAL.COM 3.31:1 -> 2.09:1). Capping the halo instead
     costs the flipped brand line on the folds most of its separation (12.4:1
     -> 4.7:1 on Cosmic/Portrait Fold), so the cap stays generous and the BAR
     is given its own ink mat instead - see _barMat in build(). Fix the thing
     that is thin, not the thing that is working. */
  var FOIL_HALO = [[0.48, 0.90, 1.30],     /* A: under flipped ink type */
                   [0.28, 0.96, 0.75],
                   [0.14, 1, 0.42]];
  var INK_BLOOM = [[0.46, 0.58, 1.25],     /* B: under kept knockout type */
                   [0.30, 0.76, 0.80],
                   [0.16, 0.90, 0.44]];
  /* B', THE BLIND CASE: a design with no baked entry. Same reach - it still
     may not meet the row above - but as deep as ink gets, because here the
     bloom is the ONLY thing holding the type up. On a measured-light layout it
     is one of three (the panel, the washes and the bloom) and it is tuned to
     leave the artwork reading through; on an unmeasured one there is no
     measurement to decide how much is enough, so it takes the most a
     non-merging bloom can give. On dark artwork this is still ink under a foil
     knockout and still invisible, which is what makes the blind case safe to
     over-serve: measured under an unbaked key, Onyx / Rift / Vortex / Nova all
     come back at or ABOVE their baked numbers on every shape tested.

     BE HONEST ABOUT WHAT IT BUYS ON LIGHT ART: not much. Cosmic/Toploader
     Banner reads 1.56:1 untreated, 2.06:1 here, and 9.59:1 once the design is
     baked. A bloom that may not reach the row above cannot fill the band the
     contrast is measured over, and on a banner there is no panel and no wash
     to help it. This is a HOLDING PATTERN, not a fix - it deepens the ink in
     the counters and the letter gaps and it never makes anything worse. The
     fix is the bake, and the console warning is what asks for it. */
  var INK_BLOOM_BLIND = [[0.46, 0.86, 1.25],
                         [0.30, 0.94, 0.80],
                         [0.16, 0.99, 0.44]];
  /* fills that are a foil knockout, i.e. type with no ink of its own */
  var _KNOCK_FILL = /fill="(?:#fff(?:fff)?|url\(#[^")]*_holoink\))"/i;
  /* attributes a halo copy must NOT inherit: its own paint, and the data-holo
     flag the print pass uses to decide what becomes bare foil. data-fit STAYS -
     ss-render.js re-fits type to its box after mount, and a copy that did not
     shrink with the original would print as a ghost behind it. */
  var _HALO_DROP = /\s(?:fill|stroke|stroke-width|stroke-opacity|stroke-linejoin|stroke-linecap|paint-order|data-holo|opacity|filter)="[^"]*"/g;

  /* ONE WALK, ONE QUESTION PER TEXT: does the layout already print ink under
     this run? `flipTo` non-empty says the un-marked type on this layout sits on
     BARE ARTWORK (a banner), so it flips; empty says the layout backs its type
     itself (a bg zone), so it keeps the knockout. data-onink answers the same
     question for one run against its layout's default - the wordmark owns the
     royal-blue bar it is reversed out of, whatever shape it is on - so it is
     never flipped and takes the ink treatment on every layout.

     `backAt` closes the gap between the two: on a zoned layout it reports how
     much ink the layout actually laid down at a point, so a run the layout
     forgot to back (SERIALSEAL.COM and VOID IF DAMAGED on the no-QR Landscape
     Flat live on bare art - the identity panel ends above them and the only
     other wash is a 0.75mm sliver at the die edge) takes the BANNER branch and
     flips, instead of getting a bloom smeared onto the customer's picture.
     Passed null on the banners, where nothing is backed by definition.

     A run inside a TRANSFORMED group cannot be located in die space by its x/y
     alone, so it is never asked - it keeps the layout's own default. Every run
     backAt exists to catch is plain die space.

     Halo copies are emitted BEFORE the element, so they paint underneath it. */
  function lightGroundPass(svg, flipTo, backAt, blind) {
    var re = /<g\b[^>]*>|<\/g>|<text\b[^>]*>/g, m, out = '', last = 0;
    var gstack = [], moved = 0;
    while ((m = re.exec(svg))) {
      var open = m[0], at = m.index;
      if (open.charAt(1) === 'g') {          /* <g ...> */
        var t = open.indexOf(' transform="') > -1 ? 1 : 0;
        gstack.push(t); moved += t; continue;
      }
      if (open.charAt(1) === '/') {          /* </g> */
        moved -= (gstack.pop() || 0); continue;
      }
      var end = svg.indexOf('</text>', re.lastIndex);
      if (end < 0) break;
      end += 7;
      re.lastIndex = end;
      if (!_KNOCK_FILL.test(open)) continue;           /* already prints dark */
      /* A RUN THAT BRINGS ITS OWN GROUND IS FINISHED - LEAVE IT ALONE.
         data-onink marks the runs reversed out of opaque ink of a known
         colour: SERIALSEAL.COM in the royal-blue domain bar, and the GRADE
         tablet. They are the most legible thing on the seal already (9.7-11.6:1
         measured, on every design in the catalog) and what they sit on is not
         artwork, so nothing here can improve it. Exempting them from the FLIP
         and then handing them branch B anyway - which is what this did until
         2026-09-14 - paints three ink strokes across a 1.1mm blue bar. Measured
         over the whole die on Cosmic/Toploader Banner, the share of pixels
         within 30 of the bar's royal blue went 1.3% -> 0.4% and came back to
         1.3% when the run was skipped: two thirds of the brand colour eaten,
         on every light design and every layout that draws the bar. It printed
         as a black lozenge with a blue fringe. */
      if (open.indexOf('data-onink') > -1) continue;
      var fsM = /font-size="([0-9.]+)"/.exec(open);
      var fs = fsM ? parseFloat(fsM[1]) : 2;
      var flip = !!flipTo;
      if (flip && backAt) {
        /* The run's own y is its baseline, so the band the eye reads it in is
           a third of a cap height above the caps to a third below the
           baseline. backAt answers 2 = the layout already backs the whole of
           that band to the floor, 1 = partly, 0 = bare artwork.
           1 IS ALSO THE FALLBACK, and deliberately: a run inside a transformed
           group cannot be placed in die space from its x/y, and the safe guess
           there is the zoned layout's own treatment. Guessing 0 would flip a
           run to ink without knowing what is under it, which is the one
           mistake on this page that cannot be recovered from. */
        var cls = 1;
        if (!moved) {
          var xM = /\sx="(-?[0-9.]+)"/.exec(open), yM = /\sy="(-?[0-9.]+)"/.exec(open);
          if (xM && yM) {
            var bx = parseFloat(xM[1]), by = parseFloat(yM[1]);
            cls = backAt(bx, Math.max(0, by - 1.07 * fs),
                         by - 0.35 * fs, by + 0.35 * fs);
          }
        }
        if (cls === 2) continue;      /* finished already - add nothing */
        flip = cls === 0;
      }
      var halo = flip ? FOIL_HALO : (blind ? INK_BLOOM_BLIND : INK_BLOOM);
      var haloColor = flip ? '#ffffff' : INK;
      var body = svg.slice(at + open.length, end);
      /* flipped: ink fill, and the ink outline the pass above gave it becomes
         foil - it separates ink from art now, not foil from art */
      var lit = flip
        ? open.replace(/fill="[^"]*"/, 'fill="' + flipTo + '"')
              .replace(/stroke="[^"]*"/, 'stroke="#ffffff"')
        : open;
      var attrs = open.slice(5, open.length - 1).replace(_HALO_DROP, '');
      var pre = '', i;
      for (i = 0; i < halo.length; i++) {
        var w = Math.min(halo[i][2], fs * halo[i][0]);
        pre += '<text' + attrs + ' fill="none" stroke="' + haloColor +
               '" stroke-width="' + r2(w) + '" stroke-opacity="' + halo[i][1] +
               '" stroke-linejoin="round" stroke-linecap="round">' + body;
      }
      out += svg.slice(last, at) + pre + lit;
      last = at + open.length;      /* body is emitted by the next slice */
    }
    return out + svg.slice(last);
  }

  function _lumaHex(h) {
    var out = [], i;
    for (i = 0; i + 1 < h.length; i += 2) out.push(parseInt(h.substr(i, 2), 16));
    return out;
  }
  /* mean, and the mean of the brightest third */
  function _lumaStats(cells) {
    if (!cells || !cells.length) return null;
    var n = cells.length, s = 0, i;
    for (i = 0; i < n; i++) s += cells[i];
    var srt = cells.slice().sort(function (a, b) { return a - b; });
    var k = Math.max(1, Math.round(n / 3)), hs = 0;
    for (i = n - k; i < n; i++) hs += srt[i];
    return { mean: s / n, hi: hs / k };
  }
  /* WHICH CROP a shape draws. Mirrors the bgImg resolution in build() exactly -
     same expression, same fallback - so the grid measured is the raster
     painted. */
  function _lumaChan(orient, style) {
    var fold = style === 'fold';
    return orient === 'landscape' ? (fold ? 'lf' : 'ls') : (fold ? 'pf' : 'p');
  }
  /* Sample a decoded raster into the same grid the bake produces. TWO-STEP
     downscale: one drawImage into a 6x4 canvas is a bilinear POINT sample of a
     2000px image - six pixels' worth of evidence - so the crop lands in a
     240x160 intermediate at high smoothing quality and the cells are averaged
     out of that by hand. */
  function _lumaFromImage(im, land) {
    var gw = land ? LUMA_GW : LUMA_GH, gh = land ? LUMA_GH : LUMA_GW;
    var dieAR = land ? (LONG / SHORT) : (SHORT / LONG);
    var iw = im.naturalWidth || im.width, ih = im.naturalHeight || im.height;
    if (!iw || !ih) throw new Error('luma: image has no intrinsic size');
    /* xMidYMid slice - the same cover-crop the <image> element performs */
    var sw = iw, sh = ih;
    if (iw / ih > dieAR) sw = ih * dieAR; else sh = iw / dieAR;
    var sx = (iw - sw) / 2, sy = (ih - sh) / 2;
    var mw = gw * 40, mh = gh * 40;
    var cv = document.createElement('canvas');
    cv.width = mw; cv.height = mh;
    var cx = cv.getContext('2d', { willReadFrequently: true });
    /* transparent artwork shows BARE FOIL, the brightest ground there is - so
       the alpha floor is white, never black */
    cx.fillStyle = '#ffffff'; cx.fillRect(0, 0, mw, mh);
    try { cx.imageSmoothingQuality = 'high'; } catch (_e) {}
    cx.drawImage(im, sx, sy, sw, sh, 0, 0, mw, mh);
    var d = cx.getImageData(0, 0, mw, mh).data;
    var cells = [], gx, gy, px, py;
    var cw = mw / gw, ch = mh / gh;
    for (gy = 0; gy < gh; gy++) for (gx = 0; gx < gw; gx++) {
      var x0 = Math.round(gx * cw), x1 = Math.round((gx + 1) * cw);
      var y0 = Math.round(gy * ch), y1 = Math.round((gy + 1) * ch);
      var acc = 0, cnt = 0;
      for (py = y0; py < y1; py++) for (px = x0; px < x1; px++) {
        var o4 = (py * mw + px) * 4;
        acc += 0.2126 * d[o4] + 0.7152 * d[o4 + 1] + 0.0722 * d[o4 + 2];
        cnt++;
      }
      cells.push(Math.max(0, Math.min(255, Math.round(acc / (cnt || 1)))));
    }
    return cells;
  }
  /* Async sampler. Resolves to the cell array and fills LUMA_RUN so the next
     synchronous build() is measured. tools/bake_luma.py drives THIS function to
     produce the table, so baked and sampled cannot disagree by construction. */
  function sampleLuma(url, land) {
    return new Promise(function (res, rej) {
      var im = new Image();
      im.crossOrigin = 'anonymous';
      im.onload = function () {
        try { var c = _lumaFromImage(im, land); LUMA_RUN[url] = c; res(c); }
        catch (e) { rej(e); }
      };
      im.onerror = function () { rej(new Error('luma: load failed')); };
      im.src = url;
    });
  }
  function warmLuma(url, land) {
    if (!url || LUMA_RUN[url] || LUMA_PEND[url]) return;
    LUMA_PEND[url] = true;
    try {
      sampleLuma(url, land).then(function () { delete LUMA_PEND[url]; },
                                 function () { delete LUMA_PEND[url]; });
    } catch (_e) { delete LUMA_PEND[url]; }
  }
  /* THE ONE QUESTION THIS SECTION ANSWERS: is the ground under the type light?

     IT READS THE BAKED TABLE AND NOTHING ELSE, ON PURPOSE. The obvious
     refinement - "if we happen to have sampled this exact raster at runtime,
     prefer that" - would make the answer depend on whether a decode had landed
     yet, and the two consumers of this file decode at different moments:

       · the builder mounts a preview on every keystroke, so a sample lands
         between one render and the next;
       · the press sidecar (tools/render_seals.py) inlines the background as a
         data: URL and calls build() once per seal in a loop, so seal 1 would
         answer before the sample and seals 2..N after it.

     Either one turns "what the customer approved" and "what came off the press"
     into different labels, and a batch into two kinds of seal. So the decision
     is a pure function of the design, the crop channel and the die: same answer
     in the builder, in the sidecar, on seal 1 and on seal 10,008.

     NO BAKED ENTRY => LIGHT. That is not a guess, it is the cheap side of an
     asymmetric bet: the local backing is ink drawn UNDER a foil knockout, so on
     dark artwork it is invisible and costs nothing, while skipping it on light
     artwork is the bug this whole section exists to kill. A design the studio
     publishes tomorrow therefore renders CORRECTLY the moment it goes live,
     before anyone measures anything; re-running tools/bake_luma.py only removes
     an ornament that was never visible on a dark newcomer.

     KNOWN LIMIT - a CUSTOMER-UPLOADED background on a design that has its own
     baked crops inherits the DESIGN's numbers rather than its own. f.bg cannot
     be told apart from an inlined studio crop inside the press render (the
     sidecar assigns the customer's data: URL to design.bg as well), and a
     signal that exists only in the builder would put the preview and the press
     on different answers - the one thing this function must never do. Closing
     it properly means the sidecar passing an explicit "this bg is the
     customer's" flag; until then an upload behaves exactly as it does today. */
  var _unbaked = {};
  function _warnUnbaked(k) {
    if (!k || _unbaked[k]) return;
    _unbaked[k] = true;
    try {
      if (window.console && console.warn)
        console.warn('[ss-seal] design "' + k + '" has no baked ground ' +
          'luminance: it renders on the SAFE treatment (local ink backing, ' +
          'never a polarity flip). Run: python tools/bake_luma.py --write');
    } catch (_e) {}
  }
  /* 0 = measured DARK | 1 = measured LIGHT | 2 = no baked entry.

     THREE STATES, NOT TWO, BECAUSE THE TWO BRANCHES DISAGREE ABOUT THE
     UNKNOWN. This used to return `true` for an unbaked design and call that
     "the cheap side of an asymmetric bet". It is only cheap on the BG-ZONE
     branch, where the treatment is ink drawn UNDER a foil knockout and really
     is invisible on dark art. On a BANNER the treatment is a polarity FLIP,
     and a flip on dark artwork is black type on black art: mounted under an
     unbaked key, Onyx/Toploader Banner fell 5.18:1 -> 3.37:1 and Rift/Toploader
     Banner 7.93:1 -> 2.65:1, with the corner brackets, column rules and
     diamonds flipping to ink and vanishing with them.

     So the unknown is reported as its own state and each branch reads it for
     itself: the caller draws the BACKING on 1 or 2 and FLIPS only on 1. A
     design the studio publishes tomorrow renders SAFELY before anyone measures
     anything - it simply does not get the full fix until tools/bake_luma.py
     has run, and it says so in the console the first time it is built. */
  function groundLevel(design, url, orient, style) {
    if (!url) return 0;                   /* no art: bare foil, not our case */
    var tab = LUMA[design && design.key];
    var hex = tab && (tab[_lumaChan(orient, style)] || tab.p);
    if (!hex) { _warnUnbaked(design && design.key); return 2; }
    var st = _lumaStats(_lumaHex(hex));
    return (st && (st.mean > LUMA_T_MEAN || st.hi > LUMA_T_HI)) ? 1 : 0;
  }
  /* The mean of the brightest THIRD of the crop - how bright the art GETS
     where a line of type could land. Used to work out how much ink has to go
     under a knockout before it clears the floor; 0 when nothing is baked. */
  function groundHi(design, url, orient, style) {
    if (!url) return 0;
    var tab = LUMA[design && design.key];
    var hex = tab && (tab[_lumaChan(orient, style)] || tab.p);
    if (!hex) return 0;
    var st = _lumaStats(_lumaHex(hex));
    return st ? st.hi : 0;
  }
  /* A white knockout on this stock is bare foil: 255 in the print-polarity
     raster. WCAG 4.5:1 against it puts the ground at relative luminance
     1.05/4.5 - 0.05 = 0.1833, i.e. sRGB 118. So the ink under a run has to
     take the art from its own brightness down to 118 or darker, and the alpha
     that does it is 1 - 118/hi. Anything at or above that needs NOTHING added;
     anything well under it is effectively bare art and wants the other
     polarity instead. */
  var GROUND_FLOOR_SRGB = 118;
  function needAlpha(hi) {
    return hi > 0 ? Math.max(0, Math.min(0.9, 1 - GROUND_FLOOR_SRGB / hi)) : 0.5;
  }
  /* kept for tools/legibility_proof.py, which asks the shipped code which
     designs it calls light instead of re-deriving the split */
  function groundIsLight(design, url, orient, style) {
    return groundLevel(design, url, orient, style) === 1;
  }
  var G = window.SSG;

  function r2(n) { return Math.round(n * 100) / 100; }
  /* 3dp, for a font-size the code DERIVES rather than authors — 2dp quantises
     a 0.9x step into visible jumps at this scale (0.87 vs 0.873). */
  function r3(n) { return Math.round(n * 1000) / 1000; }

  /* Default QR target: this seal's public record on the live verify page.
     The scheme is omitted (phone cameras prepend https:// themselves), which
     saves 8 characters of module density on an 8.4mm code — 37 chars total,
     inside the "scans easily" band. Do NOT shorten to a /v/ path unless a
     redirect for it actually exists; a dense code that resolves beats a
     sparse one that 404s. */
  function verifyUrl(serial) {
    var s = String(serial || '').toUpperCase().replace(/[^A-Z0-9-]/g, '');
    return s ? 'serialseal.com/pages/verify?s=' + s : '';
  }

  /* ——— SerialSeal Business: the dynamic QR ———
     A Business seal's code is OPAQUE and has nothing to do with its serial:
     six characters minted per seal at order time, resolved by the short host,
     and repointable afterwards — so the destination can change forever without
     reprinting the label. It is deliberately not the serial: serials run 6–8
     characters and an 8-char one would push this code to QR version 4.

     THE PRINTED STRING IS BUILT HERE AND NOWHERE ELSE. Callers hand this file
     the CODE ALONE (f.qr = 'business:K7DM4X'); they never hand it a URL. That
     is a density guarantee, not tidiness. The encoder runs byte mode at EC
     level H, where version 3 holds 24 bytes:

         's.serialseal.com/'  17  +  code  6  =  23     -> v3, 29x29 modules
         'https://s.serialseal.com/K7DM4X'      =  31   -> v4, 33x33 modules

     MEASURED, not assumed — rendered through this file and the theme's own
     encoder, module pitch read back off the drawn cells:

         portrait fold      9.76mm patch   0.314mm per module
         landscape single   9.16mm         0.295mm
         portrait single    8.36mm         0.269mm
         landscape fold     8.16mm         0.264mm   <- the tightest layout

     Version 4 would cost ~12% of every one of those figures, printed on
     holographic foil, for eight bytes nobody reads. (For scale, the legacy
     verify-record URL is 37-38 bytes = version 5, and prints at 0.214mm on the
     portrait-single patch — so a Business code WAS the least dense QR
     this product put on a seal -- until POS mode, whose payload is a bare
     5-14 character serial and lands at version 2. See skuCode below.) A renderer that assembled its own URL
     could not be stopped from prefixing a scheme, and the workers.dev admin
     host is 46 bytes with a code (version 6). So there is one host string in
     this codebase's print path and it is the constant below.

     An unrecognised code returns '' — the seal then prints with NO QR and its
     ordinary footer, rather than a code that resolves to nothing. Blank foil
     on a seal is recoverable by reprinting; a dead code in a buyer's hand is
     not. (The render sidecar refuses to print at all in that case; this is the
     backstop underneath it.) */
  var SHORT_HOST = 's.serialseal.com';
  /* The mint alphabet — no I/L/O/S/B/0/1/2/5/8, so a code cannot be misread
     off foil. Kept in step with ALPHABET/CODE_LEN in cloud/business-worker.js;
     change one and change the other. */
  var BIZ_CODE_RE = /^[ACDEFGHJKMNPRTUVWXY34679]{6}$/;
  function businessUrl(code) {
    var c = String(code || '').trim().toUpperCase();
    return BIZ_CODE_RE.test(c) ? SHORT_HOST + '/' + c : '';
  }

  /* --- POS item code: the seal's OWN SERIAL, as plain text ---
     A shop rings a card up by scanning the seal at the till. The till looks
     an item up by a code that matches a SKU in its own catalogue, so the
     payload has to be that code and NOTHING ELSE: no scheme, no host, no
     query, no padding. A keyboard-wedge 2D scanner types exactly these
     characters into the search box and appends its own Enter -- anything in
     front of them (even 's.serialseal.com/') is typed too and the lookup
     misses. This is the one QR mode whose payload is not a destination.

     The serial is already unique per seal and already printed on this foil,
     so it IS the code: nothing is minted and no per-seal channel is needed.
     f.serial is right here, and the duo rebuild hands each half its own, so
     two cut seals carry two different codes for free -- which the Business
     road cannot do (one code is shared across a duo die).

     VERBATIM, AND THAT IS THE WHOLE CONTRACT. One string has to appear in
     three places at once and be the same in all three:

         the QR payload == the serial printed on this foil == the SKU column
         of the shop's catalogue import (taken from Label Farm's _serials)

     so this function transforms NOTHING. No uppercasing, no charset filter,
     no trim. The foil draws the serial exactly as given (there is no
     text-transform anywhere in this file's output) and the ingest uses the
     buyer's prefix verbatim too -- core/shopify_client.py _seal_own_serials
     strips only commas and control characters, which would corrupt the
     comma-joined _serials list, and logs a warning when it has to. A filter
     here would mean a prefix like 'acme_' prints acme_K7D4Q on the foil,
     exports as acme_K7D4Q, and encodes ACMEK7D4Q: the scanner types nine
     characters that match no SKU and nothing reports an error. Silent, and
     unfixable from the till.

     A serial that is blank or whitespace prints NO code rather than an empty
     one -- an empty payload must be unreachable by construction, the same
     rule the verify branch follows. Emptiness is decided on the trimmed
     string; what gets encoded is still the untouched one.

     DENSITY (measured through this file and the theme's own encoder, byte
     mode at EC level H, the same way the businessUrl figures above were
     taken): a 9-character serial is version 2, 25x25 -- against version 3
     for a Business code and version 5 for the legacy verify URL. On the
     portrait-single patch that is 0.310mm per module against 0.270mm and
     0.214mm. So a POS item code, NOT a Business code, is now the least
     dense QR this product puts on a seal. */
  function skuCode(s) {
    var v = String(s == null ? '' : s);
    return v.trim() ? v : '';
  }
  function esc(s) {
    return String(s == null ? '' : s).replace(/[&<>"]/g, function (c) {
      return { '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;' }[c];
    });
  }
  /* ——— how wide will this line actually set? ———
     ss-render's fit pass measures for real in the browser, but the choice
     between ONE printed line and TWO has to be made HERE, while the string
     is still a string. A character COUNT is useless for that: uppercase
     Helvetica Bold runs 0.278em for I and 0.944em for W, so counting is off
     by more than 3x at the extremes and "30 characters" describes anything
     from 8.3mm to 28mm of type. These are the real advance widths (/1000 em)
     for every character the wordmark ladder can produce — [@A-Z0-9./_-]. */
  var _BWB = { '@': 975, '.': 278, '/': 278, '-': 333, '_': 556, ' ': 278 };
  (function () {
    var A = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ', i;
    var w = [722, 722, 722, 722, 667, 611, 778, 722, 278, 556, 722, 611, 833,
             722, 778, 667, 778, 722, 667, 611, 722, 667, 944, 667, 667, 611];
    for (i = 0; i < 26; i++) _BWB[A.charAt(i)] = w[i];
    for (i = 0; i < 10; i++) _BWB[String(i)] = 556;
  })();
  function emWidthBold(s) {
    s = String(s == null ? '' : s);
    var t = 0;
    for (var i = 0; i < s.length; i++) t += (_BWB[s.charAt(i)] || 722) / 1000;
    return t;
  }
  /* What size does `s` END UP at once the fit pass has squeezed it into a
     `cap` mm box — never larger than the authored `size`? That pass scales
     letter-spacing with the font size, so the tracking is scaled here too.
     DELIBERATELY PESSIMISTIC. ss-render's fit pass gives up tracking faster
     than it gives up size (measured: a 1.02/0.1 line that lands at 0.68mm
     comes back with 0.016 of tracking, not the 0.067 a proportional squeeze
     would leave), so a string always sets a little LARGER on the press than
     this predicts — ~6-8% across the band that matters. Every threshold
     compared against it is therefore a floor, never a ceiling: the code can
     decide a line is too small when it is merely small, and never the other
     way round. */
  function fitSize(s, cap, size, track) {
    var f = emWidthBold(s) + String(s).length * (track / size);
    return f > 0 ? Math.min(size, cap / f) : size;
  }
  /* Would `s` still be at least `floor` mm of type once the fit pass had
     squeezed it into a `cap` mm box? */
  function fitsAtSize(s, cap, floor, size, track) {
    return fitSize(s, cap, size, track) >= floor;
  }
  function roundRect(x, y, w, h, r) {
    return 'M' + r2(x + r) + ' ' + r2(y) +
      'H' + r2(x + w - r) + 'A' + r + ' ' + r + ' 0 0 1 ' + r2(x + w) + ' ' + r2(y + r) +
      'V' + r2(y + h - r) + 'A' + r + ' ' + r + ' 0 0 1 ' + r2(x + w - r) + ' ' + r2(y + h) +
      'H' + r2(x + r) + 'A' + r + ' ' + r + ' 0 0 1 ' + r2(x) + ' ' + r2(y + h - r) +
      'V' + r2(y + r) + 'A' + r + ' ' + r + ' 0 0 1 ' + r2(x + r) + ' ' + r2(y) + 'Z';
  }
  /* clipped (cut) corners — the bg-zone design language: squared octagonal
     frames and panels, no rounded capsules (owner spec 2026-08-02) */
  function cutRect(x, y, w, h, c) {
    return 'M' + r2(x + c) + ' ' + r2(y) + 'H' + r2(x + w - c) +
      'L' + r2(x + w) + ' ' + r2(y + c) + 'V' + r2(y + h - c) +
      'L' + r2(x + w - c) + ' ' + r2(y + h) + 'H' + r2(x + c) +
      'L' + r2(x) + ' ' + r2(y + h - c) + 'V' + r2(y + c) + 'Z';
  }

  /* ——————————————— layout ———————————————
     Everything is budgeted against the plate edge so nothing can run off the
     die. (It did once: the QR patch ended 1.3mm past the bottom and its caption
     never rendered at all.) */
  /* hasMark: a customer logo occupies the lockup corner (mode 'mark'). Only
     then does the brand line cede its left edge — with no mark the brand is
     centred across the full column. The old Serial Seal shield default is gone
     (owner call 2026-07-26): the top of the seal is the customer's text. */
  /* noBrand: f.brand === 'none' — the logo carries the seal and NO store name
     is drawn (sentinel defined in build()). It rides in as an argument, like
     hasMark, rather than being read off a global: layout() is called by the
     upload target-box and the builder's zone overlay too, and a module-level
     flag set by build() would leak this seal's answer into their next call.
     LAST in the signature on purpose — those two callers pass 8 arguments and
     get undefined here, which is the falsy "draw the name" behaviour they
     have always had, so nothing they render moves. Only the branches that put
     the name in a BAND over the customer's artwork act on it; see the reclaim
     block in the bg-zone portrait branch, and the note there for why the
     other branches deliberately do not. */
  /* fold: the seal wraps a seam — 50% on each face. FOUR styles ship:
       portrait single    — classic one-piece vertical stack, flat surfaces
       portrait folded    — horizontal crease; the fold ROTATES the far half
                            180°, so the identity panel composes upside-down
       landscape single   — classic one-piece wide layout, flat surfaces
       landscape folded   — vertical crease; a vertical-axis fold keeps "up"
                            up on both faces, so the two panels sit side by
                            side with NO rotation: design left, serial right */
  /* ═══ MOTIF LIBRARY — a No-QR seal can carry one of these in the code's
     slot (owner 2026-08-10: "will go in the blank space"). ORIGINAL drawings
     only: generic equipment and elemental marks, never a league logo, team
     crest or any TCG's trade dress. Each draws into a 10x10 box in one paint
     colour. */
  var MOTIFS = {
    baseball: { name: 'Baseball', g: 'SPORTS', d: function (c) { return (
      '<circle cx="5" cy="5" r="4.3" fill="none" stroke="' + c + '" stroke-width="0.85"/>' +
      '<path d="M2 1.9Q4.2 5 2 8.1M8 1.9Q5.8 5 8 8.1" fill="none" stroke="' + c + '" stroke-width="0.6"/>'); } },
    basketball: { name: 'Basketball', g: 'SPORTS', d: function (c) { return (
      '<circle cx="5" cy="5" r="4.3" fill="none" stroke="' + c + '" stroke-width="0.85"/>' +
      '<path d="M5 0.7V9.3M0.7 5H9.3M1.8 1.8Q5 4.4 8.2 1.8M1.8 8.2Q5 5.6 8.2 8.2" fill="none" stroke="' + c + '" stroke-width="0.55"/>'); } },
    football: { name: 'Football', g: 'SPORTS', d: function (c) { return (
      '<g transform="rotate(-38 5 5)"><ellipse cx="5" cy="5" rx="4.5" ry="2.7" fill="none" stroke="' + c + '" stroke-width="0.8"/>' +
      '<path d="M3.1 5H6.9M3.9 4.2V5.8M5 4.2V5.8M6.1 4.2V5.8" fill="none" stroke="' + c + '" stroke-width="0.5"/></g>'); } },
    soccer: { name: 'Soccer', g: 'SPORTS', d: function (c) { return (
      '<circle cx="5" cy="5" r="4.3" fill="none" stroke="' + c + '" stroke-width="0.85"/>' +
      '<path d="M5 3.4L6.5 4.5 5.9 6.3H4.1L3.5 4.5Z" fill="' + c + '"/>' +
      '<path d="M5 3.4V1M6.5 4.5 8.8 3.9M5.9 6.3 7.3 8.3M4.1 6.3 2.7 8.3M3.5 4.5 1.2 3.9" fill="none" stroke="' + c + '" stroke-width="0.5"/>'); } },
    trophy: { name: 'Trophy', g: 'SPORTS', d: function (c) { return (
      '<path d="M2.8 1.2H7.2V3.8Q7.2 6 5 6.4Q2.8 6 2.8 3.8Z" fill="' + c + '"/>' +
      '<path d="M2.8 1.9H1.2Q1.2 4.3 3 4.7M7.2 1.9H8.8Q8.8 4.3 7 4.7" fill="none" stroke="' + c + '" stroke-width="0.6"/>' +
      '<path d="M4.5 6.3H5.5V7.6H4.5Z" fill="' + c + '"/>' +
      '<path d="M3.3 8.8Q3.3 7.6 5 7.6Q6.7 7.6 6.7 8.8Z" fill="' + c + '"/>'); } },
    medal: { name: 'Medal', g: 'SPORTS', d: function (c) { return (
      '<path d="M3.2 0.8H4.6L5 2.6 5.4 0.8H6.8L5.9 3.4H4.1Z" fill="' + c + '"/>' +
      '<circle cx="5" cy="6" r="2.9" fill="none" stroke="' + c + '" stroke-width="0.8"/>' +
      '<circle cx="5" cy="6" r="1.5" fill="none" stroke="' + c + '" stroke-width="0.45"/>'); } },
    bolt: { name: 'Bolt', g: 'ELEMENTS', d: function (c) { return (
      '<path d="M5.8 0.8 2.4 5.4H4.4L3.6 9.2 7.6 4.2H5.4Z" fill="' + c + '"/>'); } },
    flame: { name: 'Flame', g: 'ELEMENTS', d: function (c) { return (
      '<path d="M5 0.8Q7.6 3 7.6 5.6Q7.6 8.6 5 9.2Q2.4 8.6 2.4 5.6Q2.4 4.4 3.4 3.1Q3.4 4.6 4.4 5Q4.2 2.6 5 0.8Z" fill="' + c + '"/>'); } },
    drop: { name: 'Drop', g: 'ELEMENTS', d: function (c) { return (
      '<path d="M5 0.9Q8.2 4.8 8.2 6.6Q8.2 9.3 5 9.3Q1.8 9.3 1.8 6.6Q1.8 4.8 5 0.9Z" fill="' + c + '"/>'); } },
    leaf: { name: 'Leaf', g: 'ELEMENTS', d: function (c) { return (
      '<path d="M8.8 1.2Q8.8 7.4 4.6 8.6Q2 9.2 1.4 8.8Q1.2 6 3 3.8Q4.8 1.6 8.8 1.2Z" fill="' + c + '"/>' +
      '<path d="M2 8.4Q4.4 5.4 7.6 2.6" fill="none" stroke="' + c + '" stroke-width="0.4" stroke-opacity="0.5"/>'); } },
    sparkle: { name: 'Sparkle', g: 'ELEMENTS', d: function (c) { return (
      '<path d="M5 0.8Q5.5 4.5 9.2 5Q5.5 5.5 5 9.2Q4.5 5.5 0.8 5Q4.5 4.5 5 0.8Z" fill="' + c + '"/>' +
      '<path d="M8 1.4Q8.15 2.45 9.2 2.6Q8.15 2.75 8 3.8Q7.85 2.75 6.8 2.6Q7.85 2.45 8 1.4Z" fill="' + c + '"/>'); } },
    gem: { name: 'Gem', g: 'ELEMENTS', d: function (c) { return (
      '<path d="M3 1.6H7L9 4 5 9 1 4Z" fill="none" stroke="' + c + '" stroke-width="0.7"/>' +
      '<path d="M1 4H9M3 1.6 4 4 5 9M7 1.6 6 4 5 9M4 4H6" fill="none" stroke="' + c + '" stroke-width="0.4"/>'); } },
    crown: { name: 'Crown', g: 'CLASSIC', d: function (c) { return (
      '<path d="M1.4 7.6 1 2.8 3.4 4.6 5 1.6 6.6 4.6 9 2.8 8.6 7.6Z" fill="' + c + '"/>' +
      '<path d="M1.6 8.4H8.4V9.2H1.6Z" fill="' + c + '"/>'); } },
    shield: { name: 'Shield', g: 'CLASSIC', d: function (c) { return (
      '<path d="M5 0.8 8.8 2Q8.8 6.8 5 9.2Q1.2 6.8 1.2 2Z" fill="none" stroke="' + c + '" stroke-width="0.8"/>' +
      '<path d="M3 4 5 5.6 7 4" fill="none" stroke="' + c + '" stroke-width="0.6"/>'); } },
    pennant: { name: 'Pennant', g: 'CLASSIC', d: function (c) { return (
      '<path d="M2.4 0.8V9.2" fill="none" stroke="' + c + '" stroke-width="0.7"/>' +
      '<path d="M2.9 1.4 8.8 2.9 2.9 4.4Z" fill="' + c + '"/>'); } },
    d20: { name: 'D20', g: 'CLASSIC', d: function (c) { return (
      '<path d="M5 0.8 8.7 2.9V7.1L5 9.2 1.3 7.1V2.9Z" fill="none" stroke="' + c + '" stroke-width="0.7"/>' +
      '<path d="M5 2.4 7.4 6.4H2.6Z" fill="none" stroke="' + c + '" stroke-width="0.5"/>' +
      '<path d="M5 0.8V2.4M8.7 2.9 7.4 6.4M1.3 2.9 2.6 6.4M5 9.2 7.4 6.4M5 9.2 2.6 6.4" fill="none" stroke="' + c + '" stroke-width="0.4"/>'); } }
  };
  try { window.SS_MOTIFS = MOTIFS; } catch (_) {}

  /* ══ LAYOUT OVERRIDES ═══════════════════════════════════════════════
     The ONE seam between hand-written geometry and the Label Farm layout
     editor. layout() below is the source of truth for every shape; anything
     the editor has saved for a shape is merged over the finished L here.

     · shapeKey is orientation + fold, e.g. "landscape:top2" — the same pair
       that selects the branch inside layout(), so a key can never name a
       shape the renderer does not build.
     · ONE level of merge, then a shallow merge of plain objects. That covers
       every zone in this file ({x,y,w,h}, {y,size}, arrays of rules) without
       letting an override invent structure the painters do not expect.
     · null in an override DELETES the slot (L.micro = null is how a layout
       says "no domain line"), which is why this cannot be a blind assign.
     · Overrides are DATA, not code: values are coerced to numbers where the
       original was a number, so a bad save cannot inject a string into
       arithmetic and produce NaN coordinates that silently vanish. */
  function mergeOverrides(L, key) {
    var all = (typeof window !== 'undefined') && window.SS_LAYOUT_OVERRIDES;
    var ov = all && (all[key] || null);
    if (!ov) return L;
    function num(orig, val) {
      if (typeof orig === 'number') {
        var n = parseFloat(val);
        return isFinite(n) ? n : orig;
      }
      return val;
    }
    for (var k in ov) {
      if (!Object.prototype.hasOwnProperty.call(ov, k)) continue;
      var v = ov[k];
      if (v === null) { L[k] = null; continue; }
      if (v && typeof v === 'object' && !(v instanceof Array) &&
          L[k] && typeof L[k] === 'object' && !(L[k] instanceof Array)) {
        for (var kk in v) {
          if (Object.prototype.hasOwnProperty.call(v, kk)) {
            L[k][kk] = num(L[k][kk], v[kk]);
          }
        }
      } else {
        L[k] = (v instanceof Array) ? v : num(L[k], v);
      }
    }
    return L;
  }

  function layout(orient, design, hasQr, hasMark, fold, bg, brandTxt, sizeId, noBrand) {
    var land = orient === 'landscape';
    var SZ = sizeOf(sizeId);
    /* Layout composes NATIVELY in the selected die's mm (owner 2026-08-30:
       "we dont need a scaler we need a manual rebuild of each design").
       Standard branches keep their tuned literals; each shape carries its
       own hand-built large_250x100 geometry block, added one shape at a
       time and tuned against the live proof. Physical constants (1.0-1.55mm
       print-safe margins, crease reserves) stay PHYSICAL - only content
       grows on the big die. */
    var W = land ? SZ.longMm : SZ.shortMm, H = land ? SZ.shortMm : SZ.longMm;
    var L = { land: land, W: W, H: H, sizeId: SZ.id, SZ: SZ };
    var full = design.window === 'full';
    /* bg: the RESOLVED background routed in by build() — a customer upload
       (f.bg) or the design's own studio bg, one channel, same zone system.
       Callers that omit the argument (ss-upload.js target boxes) keep the
       old design.bg read. */
    if (bg == null) bg = design.bg;
    /* Seal Studio background designs get their own zone system (owner spec
       2026-08-02): framed window showing the bg through, translucent black
       panels with accent outlines, cut corners, white type. The SINGLE
       layouts get the bespoke zone branches below; FOLD layouts keep their
       face geometry and pick up the same design language in the bg post-pass
       after the branch chain (owner: "the 3 others are on the old style —
       update them also"). */
    var bgz = !!(bg && !fold);
    var bgf = !!(bg && fold);
    if (fold === 'top' || fold === 'top2' || fold === 'duo') {
      if (fold === 'duo' && bg) L.lgOutline = true;   // white type over art
      bg = null; bgz = false; bgf = false;
    }
    /* a bg fold is always WINDOWED: the framed window exists to show the bg
       through, so the full-bleed fold variant (art under the text, pill
       treatment) never applies — the same rule the zoned singles enforce by
       ignoring design.window entirely */
    if (bgf) full = false;

    if (fold === 'duo') {
      /* ===== DUO - 2 PER SEAL, HAND-CUT (owner 2026-08-30: both orientations,
         both sizes - "customers cut it themselves giving basically double").
         ONE half is composed here as a complete compact seal; build()
         duplicates the finished drawing across the die and draws the dashed
         scissor guide on the split - side by side in landscape, stacked in
         portrait. The customer cuts on the line with scissors: NOT pre-cut.
         Same serial + QR on both halves - one registry record, two physical
         seals. Four hand-built geometry sets; margins stay physical. ===== */
      L.duo = true;
      if (bg) L.lgOutline = true;   /* white type over art needs its outline */
      if (land) { L.W = W = r2(W / 2); } else { L.H = H = r2(H / 2); }
      var _dBig = SZ.id === 'large_250x100';
      /* DESIGN PASS (owner 2026-08-30, "as an apple design engineer"):
         ONE alignment system per half. A logo design forms a LEFT LOCKUP
         (slot + brand rows) exactly like the landscape single; a design
         with no logo centres the whole column on the number stack's axis -
         no third centreline, ever. Vertical rhythm is equal-gap; the QR
         patch + caption centre as ONE group in their column; portrait
         halves reclaim the slot row when no logo design is active. */
      /* hasMark arrives TRUE for any duo with an uploaded logo (build() ORs
         it in - hasLogo itself is a build local, not visible here; v1
         referenced it and every duo layout THREW, freezing the preview on
         the previous shape) */
      /* ...and TRUE for an art-window design too: its art thumbnail paints
         into L.art (= the slot), so the brand must take the beside/below
         geometry or it sits on top of the art (large proof 2026-09-02,
         Graded Slab duos 2714/2715). */
      var _dLogo = hasMark || !!(design.supports && design.supports.logo) || !!design.art;
      if (land && _dBig) {
        /* large landscape half: 31.75 x 25.4 - content EARNS the big die:
           9.4mm code, 5.0mm serial, 3.2mm brand, 7.4mm logo */
        L.mark = { x: 2.0, y: 3.2, w: 7.4, h: 4.8 };
        L.brand = _dLogo
          ? { x: 10.2, y1: 5.2, y2: 8.6, ySingle: 7.2, size: 3.2 }
          : { x: 2.6, cx: 10.7, centered: true, y1: 5.0, y2: 8.4, ySingle: 7.0, size: 3.3 };
        L.text = { x: _dLogo ? 10.2 : 2.6, w: _dLogo ? 9.8 : 16.4 };
        L.eyebrow = { y: 14.2, size: 1.4 };
        L.serial = { y: 18.4, size: 5.0 };
        L.micro = null;
        L.qr = hasQr ? { x: 21.2, y: 7.0, w: 9.4, cap: 18.2 } : null;
        L.footer = { y: 22.2 };
        L.latent = hasQr ? null : [20.8, 7.2, 8.6, 6.6];
        L.numW = 17.6; L.numCx = 10.7;
        L.medal = { cx: 10.7, cy: 16.2, r: 4.6 };
        L.art = { x: 2.0, y: 3.2, w: 7.4, h: 4.8, r: 0.5 };
        L.logoSafe = { x: 2.0, y: 3.2, w: 7.4, h: 4.8 };
      } else if (land) {
        /* standard landscape half: 22.479 x 19.05 */
        L.mark = { x: 1.5, y: 2.5, w: 5.4, h: 3.6 };
        L.brand = _dLogo
          ? { x: 7.5, y1: 4.3, y2: 6.7, ySingle: 5.6, size: 2.3 }
          : { x: 2.2, cx: 7.4, centered: true, y1: 4.1, y2: 6.6, ySingle: 5.5, size: 2.4 };
        L.text = { x: _dLogo ? 7.5 : 2.2, w: _dLogo ? 6.2 : 11.0 };
        L.eyebrow = { y: 10.4, size: 1.0 };
        L.serial = { y: 13.4, size: 3.6 };
        L.micro = null;
        L.qr = hasQr ? { x: 14.1, y: 5.1, w: 7.2, cap: 13.8 } : null;
        L.footer = { y: 16.6 };
        L.latent = hasQr ? null : [13.7, 5.2, 7.4, 5.6];
        L.numW = 11.8; L.numCx = 7.4;
        L.medal = { cx: 7.4, cy: 11.6, r: 3.2 };
        L.art = { x: 1.5, y: 2.5, w: 5.4, h: 3.6, r: 0.4 };
        L.logoSafe = { x: 1.5, y: 2.5, w: 5.4, h: 3.6 };
      } else if (_dBig) {
        /* large portrait half: 25.4 x 31.75 - big centred logo, 3.6mm brand,
           5.0mm serial, 10.6mm code */
        L.mark = { x: r2((L.W - 13.2) / 2), y: 1.3, w: 13.2, h: 6.0 };
        L.brand = _dLogo
          ? { x: 2.6, cx: 12.7, centered: true, y1: 9.4, y2: 12.6, ySingle: 10.8, size: 3.6 }
          : { x: 2.6, cx: 12.7, centered: true, y1: 5.2, y2: 8.6, ySingle: 6.9, size: 3.6 };
        L.text = { x: 2.6, w: 20.2 };
        L.eyebrow = { y: 14.9, size: 1.45 };
        L.serial = { y: 18.0, size: 5.0 };
        L.micro = null;
        L.qr = hasQr ? { x: r2((L.W - 10.6) / 2), y: 19.7, w: 10.6, cap: 0 } : null;
        L.footer = { y: 30.6 };
        L.latent = hasQr ? null : [6.4, 19.9, 12.6, 7.4];
        L.numW = 20.4; L.numCx = 12.7;
        L.medal = { cx: 12.7, cy: 16.0, r: 4.8 };
        L.art = { x: r2((L.W - 13.2) / 2), y: 1.3, w: 13.2, h: 6.0, r: 0.5 };
        L.logoSafe = { x: r2((L.W - 13.2) / 2), y: 1.3, w: 13.2, h: 6.0 };
      } else {
        /* standard portrait half: 19.05 x 22.479 */
        L.mark = { x: r2((L.W - 9.0) / 2), y: 1.0, w: 9.0, h: 4.2 };
        L.brand = _dLogo
          ? { x: 1.8, cx: 9.5, centered: true, y1: 6.8, y2: 9.1, ySingle: 8.0, size: 2.4 }
          : { x: 1.8, cx: 9.5, centered: true, y1: 4.4, y2: 7.0, ySingle: 5.7, size: 2.6 };
        L.text = { x: 1.8, w: 15.45 };
        L.eyebrow = { y: 10.7, size: 1.0 };
        L.serial = { y: 12.6, size: 3.3 };
        L.micro = null;
        L.qr = hasQr ? { x: r2((L.W - 7.0) / 2), y: 14.4, w: 7.0, cap: 0 } : null;
        L.footer = { y: 21.0 };
        L.latent = hasQr ? null : [3.9, 14.6, 11.2, 5.6];
        L.numW = 15.2; L.numCx = 9.5;
        L.medal = { cx: 9.5, cy: 11.2, r: 3.1 };
        L.art = { x: r2((L.W - 9.0) / 2), y: 1.0, w: 9.0, h: 4.2, r: 0.4 };
        L.logoSafe = { x: r2((L.W - 9.0) / 2), y: 1.0, w: 9.0, h: 4.2 };
      }
      L.scrim = null;
      /* the empty art-window frame painted as a phantom box on every half -
         a window means nothing at duo scale, so it never draws here */
      L.noLogoFrame = true;
      if (L.lgOutline) {
        /* bg art behind a duo half: each half carries the bgz plate - one
           translucent dark panel behind the text stack, QR on its own white
           patch outside it. Audited per combo. */
        L.panel = land
          ? (_dBig ? { x: 1.4, y: 2.4, w: 18.6, h: 18.0, c: 0.9 }
                   : { x: 1.1, y: 1.9, w: 12.6, h: 13.4, c: 0.7 })
          : (_dBig ? { x: 1.5, y: 0.9, w: L.W - 3.0, h: 18.0, c: 0.9 }
                   : { x: 1.2, y: 0.6, w: L.W - 2.4, h: 13.6, c: 0.7 });
      }
        } else if (land && fold === 'top2') {
      /* ——— TOPLOADER BANNER (owner 2026-08-10): a toploader's opening is a
         knife edge — no rim, no spine. ONE crease at H/2. The front band
         keeps the BRAND | LOGO | SERIAL row with more room; the QR takes
         the BACK band (the back face is flat, and a QR scans at any
         rotation), the motif rotates with it via L.slotG. ——— */
      L.topfold = true;
      full = false;
      L.fold = { y: r2(H / 2), band: 1.26 };
      /* LOGO FILLS ITS COLUMN (owner 2026-08-14, order ss1022: "the qr and
         logo can be mutch larger"). The slot was a 6.2mm SQUARE floating in a
         12.5mm-wide lane, so a portrait photo contain-fit to 6.2 tall and left
         ~4mm of bare foil down each side. The lane runs x 16.2 -> 28.7 between
         the rules and the band runs from the crease strip (10.155) to the
         plate clip (18.05); this takes almost all of both, keeping 0.65mm of
         breathing room inside the rules and 0.1mm off the clip.
         y + h = 17.95 <= 18.05 — the front-band clip rule. */
      /* the tile is CENTRED in its lane now (16.2..28.7, centre 22.45) and
         stops 0.61 clear of each rule — the right edge used to sit 0.31 off
         the divider and read as heavy. Bottom 17.65 + 0.34 tile pad = 17.99,
         just inside the 18.05 clip. */
      /* the FULL band cell (owner 2026-08-18: "the logo … is so small when
         printed"): 10.55->17.65 leaves the 0.34 tile pad inside the crease
         strip (10.155) and the plate clip (18.05); 16.8->28.1 keeps the
         tile clear of both rules. Was 10.6x6.65. */
      L.mark = { x: 16.8, y: 10.55, w: 11.3, h: 7.1 };
      /* ROWS RETUNED for the 2026-08-14 design refresh (owner mockup). Both
         outer columns now carry four rows — eyebrow, hero, rule/bar, tagline —
         inside the 7.9mm the band actually has (crease strip 10.155 to plate
         clip 18.05). Baselines are spaced so the deepest descender lands at
         17.9, still 0.15 clear of the clip. */
      /* x 3.0, not 2.0: the corner brackets own the first 2.5mm of the band
         now, and text starting at 2.0 was drawn through them. */
      /* brand pulled off the tile: the fit box loses 0.5mm and the type
         drops ~7%, so "HUNTER TRADING" stops crowding the centre frame */
      L.brand = { x: 2.9, y1: 13.95, y2: 15.85, ySingle: 14.9, size: 2.35 };
      L.text = { x: 2.9, w: 12.9 };   /* bracket to rule, minus breathing */
      /* BOTH eyebrows share this baseline and size — the left one reads it
         from L.deco.lead, and they are set from the same numbers so they can
         never drift apart (owner: "match the two blue headings exactly"). */
      L.eyebrow = { y: 11.62, size: 1.02 };   /* larger (owner 2026-08-18) */
      /* up 0.35 from 14.7-ish: less air above the number, more between it and
         the blue bar, which is where the eye wanted it */
      L.serial = { y: 14.35, size: 3.3 };
      /* 11.9 wide, centred on 36.5 -> 30.55..42.45, which stops short of
         the right bracket at 42.46 */
      /* 13.2 wide: 29.15..42.35 — 0.45 clear of the right rule (28.7) and
         0.11 short of the right bracket (42.46). Was 11.9 (owner 2026-08-18:
         the number prints small). */
      L.numW = 13.4; L.numCx = 35.75;
      /* The domain rides a filled accent bar now, so the deco block draws
         BOTH — bar first, then the wordmark in white on top. L.micro stays
         as the geometry (the bar and the text both measure off it) but is
         flagged so section 6 does not paint its own black copy underneath,
         which the bar then covered completely. */
      /* the bar now matches the number column exactly (30.55..42.45) instead
         of running 0.55 wider at each end, and the wordmark's baseline is the
         bar's optical centre: 15.6 + 1.4/2 = 16.3, + 0.35 x 1.0 cap = 16.65 */
      L.micro = { x: 29.05, y: 16.65, w: 13.4, drawnByDeco: true };
      /* NO `two` ON THIS DIE — the standard seal keeps the one-line ladder.
         Declaring `two` is a layout saying it owns enough vertical room to
         grow the bar and stack two lines in it. This one does not, and the
         numbers below are measured off the 600 dpi print raster, not argued.
         THE BUDGET, standard die (44.958 x 19.05), right-hand column:
           crease strip ends            10.155
           eyebrow baseline             11.62   (1.02, cap top 10.89)
           serial baseline              14.35   (3.30, cap top 11.97, no desc)
           corner bracket, bottom right 17.95   arm 1.0 at stroke 0.22, so its
                                                ink starts at 17.84 and the
                                                square linecap runs the arm
                                                back to x 42.35 — INSIDE the
                                                bar's own 29.05..42.45
           plate clip / print-safe      18.05
         A 2.51mm bar (15.07 -> 17.58) does stack two legible lines: rendered,
         LITTLETITANLIST1111 sets at 1.02 and WHATNOT.COM at 0.92, neither
         squeezed. It also walks the bar's rounded bottom-right corner down
         into the bracket. Probed at x 42.40 in the print raster the blue ends
         at 17.441 and the bracket's ink starts at 17.822 — 0.381mm, where the
         one-line bar leaves 0.931mm. At 8x the two read as a single L-shaped
         mass and the bar looks like it grew a tail.
         Hold the bar's bottom at the proven 17.00 instead and there is 2.10mm
         between the serial baseline and the clip. Spend the same 0.30 of ink
         padding top and bottom and two cap rows at 1.02/0.95 (0.731 + 0.681)
         take 1.412 of the 1.50 that is left: 0.088mm of leading, a seventh of
         a cap. Two rows that close read as one blurred block on holo foil.
         Dropping both lines to ~0.77 to buy leading puts them under the
         0.85mm microtext rule. There is no version of this that fits.
         So on this die a long link falls back to the bare host — the owner's
         own stated fallback (2026-09-11: "if even the split lines do not fit,
         fall back to the bare host"). WIDTH is the other half of it: this box
         is 12.2mm against the large die's 17.8, and the large die is where
         the long-link case actually has the room. */
      /* centred in the back ink zone (1.0..8.895, centre 4.95): the
         VOID + sub block's own centre lands on the zone's (owner
         2026-08-18: "each text centered inside its own container") */
      L.footer = { y: 4.55 };
      /* 1.5, not 1.8. The fit pass would not bring this one down — measured
         14.78mm wide against a 12.5mm box even with a hard fit — and it is the
         only text on the band wide enough to reach the panel border, so the
         size is set outright instead of trusted to shrink. 1.8 x 12.4 / 14.78
         = 1.51, which lands the line inside the border with room. */
      L.footerSize = 2.2;   /* larger (owner 2026-08-18) */
      /* HARD fit, unlike every other shape. Soft fit stops shrinking when it
         is "close enough" and hands back up to 18% of overrun — harmless on a
         bare band, but this one has a panel border 1.5mm from the die edge and
         VOID IF DAMAGED rendered 14.78mm wide in a 12.5mm box, so it crossed
         the border (owner 2026-08-14: "the border on the top fold is touching
         the text"). Centre sits at die 8.46, so 12.5 hard keeps the left edge
         at 2.21 — 0.7 inside the border. */
      /* 10.5, and the number is empirical. The fit pass sizes this line TO
         data-fit and then tolerates ~18% of overrun before it stops shrinking
         (data-fit-hard is not honoured on this node — verified by measuring:
         12.5 asked, 14.72 rendered, 1.178x). 10.5 x 1.178 = 12.4, which lands
         the line at die 2.26..14.66 — inside the 1.5 panel border it used to
         cross. Re-measure in the browser if this text ever changes length. */
      /* 12.4 x the measured 1.178 overrun = ~14.6 rendered, centred on
         9.21 (post-rotation of numCx 35.75) -> 1.91..16.51: inside the 1.5
         panel border, clear of the QR bracket at 17.78 (owner 2026-08-18:
         "void if damaged needs to be larger") */
      L.voidFit = 12.9;   /* 1.61..16.81 — 0.11 off the border */
      L.voidHard = true;
      /* recentred on its half of the band and grown (owner 2026-08-18):
         cx 10.06 lands post-rotation at 34.9, fit 15 spans 27.4..42.4 —
         clear of the QR bracket (27.18) and the panel border (43.46) */
      L.serial2 = { cx: 10.06, y: 5.0, size: 3.5, fit: 16.2 };   /* block centred on 4.95 */
      L.backG = 'rotate(180 ' + r2(W / 2) + ' ' + r2(H / 4) + ')';
      L.slotG = L.backG;
      /* the rules grow with the slot they bracket — leaving them at 11.0-17.6
         under a 10.65-17.95 logo made the column separators look clipped */
      L.rules = [[16.2, 10.9, 16.2, 17.95], [28.7, 10.9, 28.7, 17.95]];
      /* NO-LOGO COLLAPSE (2026-09-08, ss1100): when the placeholder is suppressed
         (print pass, logo off-design) the slot's left rule goes with it and the
         name may run into the freed window; the divider before the domain bar
         stays so the right column keeps its edge. Preview with no logo still
         shows the dashed slot (owner 2026-08-11), so nothing changes there. */
      L.textNoMark = { x: 2.9, w: 25.2 };
      /* ——— DESIGN REFRESH (owner mockup 2026-08-14) ————————————————————
         Everything here is NEW furniture and every piece is optional: the
         paint block is guarded by `if (L.deco)`, so the other four seal
         shapes render exactly as before.

         ink: the mockup commits to one royal blue for every accent on the
         seal. It is set here rather than taken from f.color because this
         template ships its own ink — the accent picker was retired and a
         Clean Foil order carries the old purple default, which is not this
         design. inkSafe still clamps it for the foil. */
      L.deco = {
        ink: '#1f3df5',
        rulesNoMark: [[28.7, 10.9, 28.7, 17.95]],  /* see L.textNoMark above */
        tile: { pad: 0.08, r: 0.6, sw: 0.17 },   /* keyline hugs the image */
        corner: 1.0,                                  /* front corner brackets */
        panel: { x: 1.5, y: 1.35, w: r2(W - 3.0), h: 6.9, r: 0.9 },
        /* brackets REMOVED (owner 2026-08-18: "remove the blue border
           thing") — the patch edge against the art draws its own frame */
        qrFrame: 0, qrPad: 0,
        /* label + pill removed (owner 2026-08-18) — the dashed crease and
           its ticks remain as the application guide */
        foldLabel: '',
        /* y/size deliberately identical to L.eyebrow above */
        lead: { y: 11.62, size: 1.02, track: 0.1, text: 'PREMIUM PROTECTION' },
        /* baseline is the optical centre of rule..bracket-bottom:
           (16.55 + 17.95) / 2 = 17.25, + 0.35 x 0.72 */
        tag:  { rule: 16.55, y: 17.5, size: 0.85, track: 0.08,
                text: 'PROTECT WHAT YOU COLLECT' },
        bar:  { y: 15.6, h: 1.4, r: 0.22 },           /* the domain's blue bar */
        /* NOT "SCAN TO VERIFY AUTHENTICITY" — see the note in the paint
           block. The code opens the CUSTOMER's own link. */
        /* clear of the bar's 17.0 bottom, centred in what is left */
        sub: null,   /* caption removed (owner 2026-08-18) */
        voidSub:   { text: 'SHOWS IF PEELED', size: 1.15, dy: 1.62 },
        serialSub: { text: 'SERIAL NUMBER',   size: 1.15, dy: 1.62 }
      };
      /* ADAPTIVE LEFT COLUMN (owner 2026-08-18, ss1025: "the text is so
         small you can't read it … cleaned, optimized"). Split the brand the
         way the drawing code will (camelCase/digits count as spaces) and
         balance two lines; past ~11 chars a line the fit pass lands under
         ~1mm printed, so the column drops the stock eyebrow + tagline and
         hands the whole band to the name, three lines if needed. A short
         brand renders the owner's 2026-08-14 mockup byte-identically. */
      var _bt = String(brandTxt || '').replace(/([a-z0-9])([A-Z])/g, '$1 $2')
                  .replace(/([A-Za-z])(\d)/g, '$1 $2').trim();
      var _bw = _bt.split(/\s+/).filter(Boolean);
      var _b2 = _bt.length;
      for (var _bi = 1; _bi < _bw.length; _bi++) {
        var _bl = Math.max(_bw.slice(0, _bi).join(' ').length,
                           _bw.slice(_bi).join(' ').length);
        if (_bl < _b2) _b2 = _bl;
      }
      if (_b2 > 11) {
        L.brand = { x: 2.9, y1: 12.55, y2: 14.55, y3: 16.55, ySingle: 14.9,
                    size: 2.35, threeLine: true };
        L.deco.lead = null;
        L.deco.tag = null;
      }
      /* QR takes the whole back band. It is SQUARE, so height is the only
         constraint and the band is the die's top half: usable is the plate
         clip (1.0) down to the crease strip (fold 9.525 - band 1.26/2 =
         8.895). 7.85 fills 1.0 -> 8.85 with 0.045 to spare. It cannot get
         meaningfully bigger than this without either crossing the crease —
         where the fold cracks the ink and a cracked module stops the scan —
         or pushing into the 1mm the die cut needs. */
      /* THE WHOLE BACK BAND, as actually painted: the plate clip crops
         everything above 1.0, so the VISIBLE box is 1.0 -> the fold (9.525)
         and the code is sized/centred against THAT box (the 2026-08-18 v7
         cut centred it against an imaginary y=0 top and the code sat high,
         0.32 over 0.95 margins). Now: box 1.0..9.525, code 7.47 at
         1.42..8.89 — 0.42 margins all round except 0.635 at the fold,
         where the crease reserve keeps ink out of the crack zone. */
      /* COORDS ARE PRE-ROTATION (L.slotG turns this band 180°, y' =
         9.525 - y) — which is why every clip-frame fix "moved" the gap
         instead of closing it. py 0 / ph 9.525 maps onto itself: the patch
         spans the die top edge to the fold line exactly. The code (ink)
         sits at post-rotation 1.03..8.50 — dead centre of the patch AND
         inside the 1.0/8.895 safe zone; equal 1.03 margins above and
         below, 0.42 at the sides. */
      L.qr = hasQr ? { x: r2((W - 8.31) / 2), y: 0.61, w: 8.31, cap: 0,
                       py: 0, ph: 9.525 } : null;
      L.latent = null; L.medal = false; L.scrim = null;
      L.art = { x: 16.8, y: 10.55, w: 11.3, h: 7.1, r: 0.6 };
      L.logoSafe = { x: 16.8, y: 10.55, w: 11.3, h: 7.1 };
      if (SZ.id === 'large_250x100') {
        /* ===== LARGE DIE (2.5 x 1.0 in) - HAND-REBUILT (owner 2026-08-30).
           Crease at 12.7; front band content 13.33..24.4 (crease strip to
           plate clip), back band visible 1.0..12.07. Same column grammar,
           every number re-cut for the die. */
        L.fold = { y: 12.7, band: 1.26 };
        L.mark = { x: 23.6, y: 14.05, w: 15.7, h: 9.85 };
        if (L.brand.threeLine) {
          L.brand = { x: 4.0, y1: 16.2, y2: 19.0, y3: 21.8, ySingle: 19.2,
                      size: 3.3, threeLine: true };
        } else {
          L.brand = { x: 4.0, y1: 17.7, y2: 20.4, ySingle: 19.2, size: 3.3 };
        }
        L.text = { x: 4.0, w: 18.0 };
        L.eyebrow = { y: 15.5, size: 1.45 };
        L.serial = { y: 19.2, size: 4.6 };
        L.numW = 19.0; L.numCx = 51.1;
        L.micro = { x: 41.6, y: 22.35, w: 19.0, drawnByDeco: true };
        /* THE BUDGET, large die (63.5 x 25.4), right-hand column:
             crease strip ends        13.33
             eyebrow baseline         15.5   (1.45)
             serial baseline          19.2   (4.60, cap top 15.89, no desc)
             plate clip               24.4
           19.2 -> 24.4 = 5.20mm. One line spends 1.95; two spend 3.65 and
           leave 1.00 under the number and 0.55 to the clip.
             bar   20.30 -> 23.75   h 3.45, 0.48 padding top and bottom
             line1 baseline 21.82   1.45mm, cap 20.776..21.82
             line2 baseline 23.27   1.05mm, cap 22.514..23.27, 0.69 between
           SIZE 1.45 — this die's own eyebrow size, not the 1.02 the one-line
           bar inherited from the standard die. The text box is 17.8mm here
           (0.60 of ink each side, the same margin the one line keeps), and
           that width is the whole difference between the dies: it takes a
           26-character store name at ~0.96mm, where the standard die's
           12.2mm box would put the same string at 0.68mm. This is the die
           the long-link case actually ships on.
           LINE 2 IS SMALLER ON PURPOSE, AND IT TRACKS LINE 1. The store name
           is what the owner asked to print and the host is the supporting
           line, but the name is also the line the fit pass squeezes. A fixed
           size2 of 1.05 was right for a short name (1.45 over 1.05) and
           INVERTED for a long one — PawPaw rendered a 0.97mm store name under
           a 1.05mm EBAY.COM, the subject reading as the subordinate, for the
           very order that prompted the feature. So the paint site derives it:
           0.9x whatever line 1 lands on, clamped to [floor2, size2].
             short name   1.45 over 1.05   (unchanged)
             PawPaw       0.97 over 0.95
           floor2 IS THE HOST'S OWN FLOOR, and it is higher than line 1's. The
           host is the string a finder actually types; 0.95 is the bottom of
           what that may print at. line 1's floor stays 0.88 — a name that
           small is still worth more than dropping it for the bare host.
           `one` IS THE SPLIT TRIGGER: split when the ideal ONE line would set
           below 0.93 by fitSize's reckoning (~1.00mm on the press — fitSize
           runs 6-8% low, see its note). That catches the whole crushed band
           the character count missed (0.92 / 0.94 / 0.73 measured, all one
           line before this) and cannot reach the protected cases, which set
           at the authored 1.02 with room to spare. */
        L.micro.two = { bar: { y: 20.30, h: 3.45, r: 0.3 },
                        y1: 21.82, y2: 23.27,
                        size: 1.45, track: 0.1,
                        size2: 1.05, track2: 0.08, pad: 1.2,
                        one: 0.93, floor: 0.88, floor2: 0.95 };
        L.footer = { y: 6.05 };
        L.footerSize = 3.0;
        L.voidFit = 17.5;
        L.voidHard = true;
        L.serial2 = { cx: 14.2, y: 6.7, size: 4.6, fit: 21.5 };
        L.backG = 'rotate(180 ' + r2(W / 2) + ' ' + r2(H / 4) + ')';
        L.slotG = L.backG;
        L.rules = [[22.9, 13.8, 22.9, 24.3], [40.6, 13.8, 40.6, 24.3]];
        L.qr = hasQr ? { x: r2((W - 11.1) / 2), y: 0.81, w: 11.1, cap: 0,
                         py: 0, ph: 12.7 } : null;
        L.art = { x: 23.6, y: 14.05, w: 15.7, h: 9.85, r: 0.8 };
        L.logoSafe = { x: 23.6, y: 14.05, w: 15.7, h: 9.85 };
        var _dlead = L.deco.lead ? { y: 15.5, size: 1.45, track: 0.1,
                                     text: 'PREMIUM PROTECTION' } : null;
        var _dtag = L.deco.tag ? { rule: 22.5, y: 23.55, size: 1.15, track: 0.08,
                                   text: 'PROTECT WHAT YOU COLLECT' } : null;
        L.deco = {
          ink: '#1f3df5',
          tile: { pad: 0.1, r: 0.8, sw: 0.22 },
          corner: 1.3,
          panel: { x: 1.5, y: 1.35, w: r2(W - 3.0), h: 9.6, r: 1.2 },
          qrFrame: 0, qrPad: 0,
          foldLabel: '',
          lead: _dlead,
          tag: _dtag,
          bar: { y: 20.9, h: 1.95, r: 0.3 },
          sub: null,
          voidSub:   { text: 'SHOWS IF PEELED', size: 1.55, dy: 2.2 },
          serialSub: { text: 'SERIAL NUMBER',   size: 1.55, dy: 2.2 }
        };
      }
    } else if (land && fold === 'top') {
      /* ——— SLAB BANNER v2.1 (owner 2026-08-10, audited): the SPINE is the
         index face (slabs in cases show only the top). PERFECT 2dp symmetry:
         bands 5.72 each around a 7.61 spine (folds 5.72 / 13.33). Every
         band's content is optically centred; the serial re-centres on the
         whole spine when there is no QR/motif; the logo slot fits INSIDE
         the 1.0mm print-safe margin. ——— */
      L.topfold = true;
      full = false;
      L.fold = { y: 13.33, band: 0.9 };
      L.fold2 = { y: 5.72, band: 0.9 };
      /* spine: the index face (block centred on y 9.525) */
      L.eyebrow = { y: 8.05, size: 0.95 };
      L.serial = { y: 11.65, size: 3.6 };
      L.numW = hasQr ? 28.0 : 36.0;
      L.numCx = hasQr ? 17.0 : r2(W / 2);
      /* 33.6, not 34.4 — the 2026-08-14 refresh brackets the spine's four
         corners, and the right-hand pair reaches in to 42.66. At 34.4 the QR's
         foil patch ended at 41.4 and left a 0.26mm slot between it and the
         bracket, which reads as a collision at seal scale. Sliding the code
         0.8mm left costs nothing (the number column ends at 31.0, so the
         gutter only narrows from 3.4 to 2.6) and it is PURE X — the size,
         the module pitch and the crease clearances are all untouched. */
      L.qr = hasQr ? { x: 33.6, y: 6.03, w: 7.0, cap: 0 } : null;
      /* front: display face, everything centred on y 15.97 (safe-zone centre) */
      /* FRONT BAND = LOGO + NAME, nothing else (owner 2026-08-11, ss1017:
         "move the serial seal under the serial number, and then use the whole
         bottom row for the logo and the name"). SERIALSEAL.COM lives on the
         spine under the serial now, so the band splits between a bigger logo
         slot and a brand line with ~50% more width to grow into.
         SAFE HEIGHT means inside the plate clip: the plate insets 1.0mm from
         the die edge, so the slot bottom is pinned to 18.05 — an earlier cut
         ran to 18.53 and the clip sliced the customer's logo mid-curve
         ("logo is cut off"; same overrun the v2.1 audit fixed at 18.35).
         Top clears the fold dashes (~12.88–13.78). */
      /* CENTERED as a group (owner: "logo and name centered, it's left
         aligned"): the logo+name composition insets 4.03 from BOTH die
         edges — logo 4.03→13.03, name zone 13.93→40.93 — so a full-width
         name balances the band exactly about W/2, and the name is anchored
         MIDDLE in its zone so short names hold the centre instead of
         hugging the logo. */
      /* taller slot, same 18.05 print-safe bottom (owner 2026-08-18) */
      L.mark = { x: 4.03, y: 13.75, w: 9.0, h: 4.3 };
      /* ONE big line, never the two-line split (owner: "the name is still
         squished, it needs to use the whole new space") — size 3.4 fills the
         band's height and data-fit shrinks long names into the 27mm run
         instead of stacking them small. oneLine is the draw-side switch. */
      L.brand = { x: 13.93, cx: 27.43, centered: true,
                  y1: 15.63, y2: 17.73, ySingle: 17.1, size: 3.4, oneLine: true };
      L.text = { x: 13.93, w: 27.0 };
      /* no spine/front micro — the domain rides the BACK band with the
         rotated serial (owner: "serial seal needs to go under the other
         serial, not the top"). Set for real further down, once L.backG
         exists, because the 2026-08-14 refresh gives it a filled accent bar
         and the bar has to be drawn INSIDE that rotated group. The generic
         micro pass at section 6 is skipped either way (null here, and
         drawnByDeco on the real one). */
      L.micro = null;
      /* where the name goes when nothing occupies the slot — build() swaps to
         these so a logo-less banner spends the whole band on the brand,
         centred on the die like the logo+name group is */
      L.textNoMark = { x: 2.6, w: 40.8, cx: r2(W / 2) };
      /* back: honesty band, both texts optically centred on the band's
         centre y 2.86 and SYMMETRIC about W/2 (12.48 / 32.48) — the old
         16.0 / 36.5 sat 3.7mm right of centre, so VOID crowded one margin
         while dead space floated mid-band (owner 2026-08-11, order ss1017:
         "the serial and VOID IF DAMAGED are in wrong areas"). The spine got
         exactly this recentre in the v2.1 audit; the back band never did.
         SIZES (owner follow-up, same order: "still small"): the 5.72mm band
         was carrying 1.35/2.0mm type in 12mm boxes. Serial 3.0 and VOID 2.0
         in 17.5mm fit boxes — 1.55mm side margins kept, 2.5mm gap between
         the boxes, cap tops ≥1.0mm print-safe, baselines clear of the fold
         dashes at ~5.27. Baseline = centre + 0.35×size. */
      /* 3.86, not 3.56 (2026-08-14 refresh, +0.30 in READING order). The band
         is authored pre-rotation, so reading-order y grows AWAY from the
         crease: the lane runs 0.45 (crease reserve) to 4.72 (plate clip). The
         refresh hangs a quiet sub-line under this baseline and wraps the
         domain in a filled bar above it, and the old 3.56 left only 1.16mm
         between the baseline and the clip — not enough for a 0.8mm sub-line
         plus its gap. Moving the row 0.30 down buys the sub-line its space at
         the bottom and, at the same time, opens the 0.16mm the bar needed
         above the serial's cap. Cap tops stay ≥1.0mm off the die edge:
         die 1.86 for VOID, die 1.87 for the serial. */
      L.footer = { y: 3.86 };
      L.footerSize = 2.0;
      L.voidFit = 17.5;
      L.voidCx = 32.48;
      /* ROTATION MATH, learned the hard way (ss1017 round 6: the domain
         printed at the DIE'S TOP EDGE, half cut off): inside backG
         (rotate 180 about y=2.86) an anchor at pre-rotation y renders at
         die-y (5.72 − y), and the flipped glyphs then extend DOWNWARD in
         die space from that point by 0.72×size. So serial2 (y 3.55)
         occupies die 2.17→4.19, and the domain must START below 4.19:
         pre-rotation y 1.37 → die 4.35→5.07, clear of the fold dashes at
         5.27. A "bigger y = lower on the band" instinct is EXACTLY WRONG
         inside this group. */
      L.serial2 = { cx: 12.48, y: 3.85, size: 2.8, fit: 17.5 };   /* +0.30 with VOID — see L.footer */
      /* the domain no longer draws itself: the refresh reverses it out of a
         filled accent bar, and both halves come from L.micro below. */
      L.serial2Micro = null;
      L.backG = 'rotate(180 ' + r2(W / 2) + ' 2.86)';
      /* SERIALSEAL.COM, reversed white out of a filled accent bar — the one
         place on this seal where ink is the ground and the wordmark is the
         hole in it. Geometry is PRE-ROTATION (it is drawn inside backG via
         .g), so reading-order y grows away from the crease:
           bar   0.57 → 1.67   = die 4.05 → 5.15, 0.12 clear of the 5.27
                                 crease reserve and 0.16 under the serial's
                                 cap top (reading 1.834)
           text  baseline 1.48 = cap reading 0.76→1.48 = die 4.24→4.96,
                                 optically centred in the bar
         drawnByDeco keeps section 6's generic microtext pass off it, exactly
         as on the toploader — otherwise a black copy prints underneath and
         the bar covers it. */
      L.micro = { x: r2(12.48 - 6.0), y: 1.48, w: 12.0,
                  drawnByDeco: true, g: L.backG };
      /* ONE column rule per band that has two things to separate — the slab's
         answer to the toploader's pair. Each carries a diamond at its own
         midpoint (5th element), because the two bands have different centres.
           spine  x 32.3: between the number column's right edge (numCx 17.0 +
                  numW/2 14.0 = 31.0) and the QR at 33.6. Runs between the
                  bracket rails so the frame reads as one object.
           front  x 13.48: the 0.90mm gutter the layout already leaves between
                  the logo slot (ends 13.03) and the brand zone (starts 13.93).
                  The diamond at dr 0.28 spans 13.20→13.76, which is 0.17 off
                  the logo and 0.14 off the widest brand the fit pass produces
                  (measured 13.901 at 27 characters). */
      /* the spine rule EXISTS ONLY WITH A CODE IN THE SLOT. Without one the
         serial re-centres on the whole spine and its column grows to
         numW 36 (4.48 → 40.48) — the rule would be drawn straight through
         a long number. */
      var _srule = [32.3, 6.35, 32.3, 12.7, 9.525];
      var _frule = [13.48, 13.95, 13.48, 18.05, 16.0];
      L.rules = hasQr ? [_srule, _frule] : [_frule];
      L.latent = null; L.medal = false; L.scrim = null;
      L.art = { x: 4.03, y: 13.85, w: 9.0, h: 4.2, r: 0.5 };
      L.logoSafe = { x: 4.03, y: 13.75, w: 9.0, h: 4.3 };
      /* the banner logo BLEEDS to the die edge like a background (owner:
         "top to bottom, it's still so small") — drawn OUTSIDE the plate
         clip, so its box runs from just under the fold dashes (13.78) to
         the cut at 19.05: 5.1mm tall vs the 4.2 the clip allows. The
         cutter's ±0.5mm tolerance only ever shaves the logo art's own
         padding, exactly as it does for full-bleed backgrounds. */
      /* 13.73 keeps 0.4 off the crease (13.33) where the fold cracks ink;
         the bottom still bleeds to the die edge. Was 13.95/5.1. */
      L.logoBleed = { x: 4.03, y: 13.73, w: 9.0, h: 5.32 };
      /* ——— DESIGN REFRESH, SLAB (owner 2026-08-14: "apply same style to slab
         shape"). Same vocabulary as the toploader — one royal blue, corner
         brackets, column rules with diamonds, a reversed wordmark on a filled
         bar, a quiet sub-line, fold labels in white pills — but NONE of its
         numbers. The toploader splits 15.79mm of safe height into two 7.90mm
         bands; the slab splits the same total into THREE, and its two outer
         bands are 4.27mm — 54% of a toploader band. Everything below was
         re-derived against that.

         WHAT THE TOPLOADER HAS AND THIS SHAPE DOES NOT, and why:
         · panel border — the back band's ink runs die 1.12 → 5.15 inside a
           1.00 → 5.27 lane. A border needs its rails plus ~0.25 of inner
           clearance at each end; there are 0.12 and 0.12. Nowhere else takes
           one either: the front band's logo bleeds past the plate clip to the
           die edge on purpose, so a rail across it would cut the customer's
           mark. Panel OR bar on this band, and the bar is the piece that
           carries the brand.
         · blue brackets round the QR — the QR is 7.00mm tall in a 6.71mm
           safe band. It is legal today only because it is BARE FOIL, and
           foil may cross a crease reserve; ink may not. A bracket is ink, and
           at any padding it lands 0.3–0.5mm inside both reserves. Making room
           means dropping the code to ~6.1mm — 24% of its area — on the one
           element that has a job. Not traded.
         · left-column eyebrow, tagline rule and tagline — there is no left
           column: the brand is ONE centred line and the owner pinned this
           band to logo + name only. Above it, the fs 3.4 case (a short name
           renders at full size, cap top 14.652) leaves 0.87mm under the
           crease reserve; below it, the baseline sits 0.95mm off the plate
           clip. A rule, a diamond and a 0.72 cap need 0.88 of that 0.95 —
           "fits" by 0.07mm on a die whose cutter tolerance is +/-0.5.
         · a keyline tile round the logo — the slot the toploader draws it on
           (L.mark) is not where this shape's logo lands (L.logoBleed, which
           runs past the plate clip to the cut), so the keyline would frame a
           box the mark deliberately overflows. Its top edge also lands 0.27
           inside the 13.33 crease reserve.
         See the paint block near the end of build() for the claim rules. */
      L.deco = {
        ink: '#1f3df5',
        /* THE HERO IS THE SPINE, not the front band. A slab on a shelf shows
           its top edge and nothing else, so the frame goes where the index
           reads. Rails sit 0.18 inside the crease reserves (6.17 / 12.88) and
           the 0.22 stroke's square cap adds 0.11, landing ink at 6.24 and
           12.81. Arm 0.80, not the toploader's 1.00: 2 x 1.00 in a 6.35mm
           frame closes the corners into a near-continuous vertical rule. */
        corner: { arm: 0.8, inset: 1.5, top: 6.35, bot: 12.7 },
        /* 0.28, not 0.34 — the front band's diamond has to live inside a
           0.90mm gutter. One value for both rules keeps them a family. */
        diamond: 0.28,
        /* the domain's bar. x/w and the wordmark's baseline come from
           L.micro; these two numbers are the bar's own box, PRE-ROTATION. */
        bar: { y: 0.57, h: 1.1, r: 0.2 },
        /* the spine's third row, under the hero number: eyebrow / number /
           sub, the same stack the toploader builds in its number column. It
           only became possible once the fold labels moved out to the left
           margin — the centre lane under the serial was theirs. Baseline
           12.78 is shared with the fold labels, so the whole footer row sits
           on one line; cap top 12.26 leaves 0.61 of air under the 3.6mm
           number and 0.10 to the crease reserve.
           needsQr: this line names the CODE, and without one in the slot the
           serial re-centres and there is nothing to scan. The toploader's
           copy prints regardless — that is its behaviour, left alone. */
        sub: null,   /* caption removed (owner 2026-08-18) */
        /* +0.74 in reading order = die 1.12 → 1.64, with 0.12 to the plate
           clip and 0.22 back up to the VOID baseline. The toploader's 1.25
           would push this line 0.65mm OUTSIDE the die: on a 4.27mm band the
           gap between a line and its sub-line is most of the budget, so it
           is set to the millimetre rather than inherited. 0.72, not 0.80 —
           the same size the toploader gives its other quiet lines, and the
           0.08 it gives back is the difference between 0.14 and 0.22 of
           breathing room under the baseline. */
        voidSub: { text: 'SHOWS IF PEELED', size: 0.72, dy: 0.74 },
        /* TWO creases, so two labels, and the paint block takes an explicit
           list instead of deriving one from L.fold (which names only the
           first crease, and assumes the room is on the far side of the line).

           BOTH SIT ON THE SPINE — below the first crease and above the
           second. Under the 13.33 crease is the front band, where a short
           brand renders at the full 3.4mm and puts its cap top at 14.652:
           that lane is 0.87mm for a 0.62 pill plus clearance. A label that
           fits "GREYBEARD SPORTSCARDS" and collides with "TOPPS" is a bug
           with a delay on it. Above the 13.33 crease there is 1.23mm of dead
           space between the serial's baseline (11.65) and the reserve, and
           using it makes the pair symmetric about the spine's centre
           (9.525 +/- 3.045) — they read as matched crease markers rather
           than two loose stickers.

           AND OFF THE CENTRE LINE, at cx 5.9 in the 8.6mm of dead margin
           left of the number column. Centred on the die (the toploader's
           convention, and the first two rounds of this) the upper label
           stacked 0.59mm above "INDIVIDUALLY NUMBERED" in the same blue, the
           same weight and the same centre — it read as a second heading on
           the hero band. In the margin they clear the eyebrow by 1.30
           horizontally and leave the spine's centre lane to the eyebrow /
           number / sub stack. 0.58 rather than 0.66 for the same reason:
           0.61x the eyebrow's size is a hierarchy, 0.69x is a competition.

           The PILL is white = no ink = bare foil, so it may cross a reserve;
           only the type inside it is held clear (ink at 6.27 and 12.78).
           "FOLD HERE", not "FOLD OVER TOP EDGE": a slab banner has two
           creases and neither one IS the top edge — the spine between them
           is. The long string would also need an 11mm pill. */
        foldLabel: 'FOLD HERE',
        foldAt: [{ cx: 5.9, y: 6.17,  h: 0.62, ty: 6.69,  w: 5.9, size: 0.58, fit: 5.3 },
                 { cx: 5.9, y: 12.26, h: 0.62, ty: 12.78, w: 5.9, size: 0.58, fit: 5.3 }],
        /* with no logo the brand claims the whole front band (L.textNoMark),
           so the gutter the front rule divides stops existing. Drop that rule
           and keep whatever the spine earned — which is nothing when there is
           no code either, hence the empty list rather than null. */
        rulesNoMark: hasQr ? [_srule] : []
      };
      if (SZ.id === 'large_250x100') {
        /* ===== LARGE DIE (2.5 x 1.0 in) - HAND-REBUILT (owner 2026-08-30).
           Bands: back 0..7.6, spine 7.6..17.8 (10.2), front 17.8..25.4.
           Same band grammar as standard, every number re-cut for the die. */
        L.fold = { y: 17.8, band: 0.9 };
        L.fold2 = { y: 7.6, band: 0.9 };
        /* spine: index face, block centred on y 12.7 */
        L.eyebrow = { y: 10.7, size: 1.3 };
        L.serial = { y: 15.4, size: 4.8 };
        L.numW = hasQr ? 38.0 : 50.0;
        L.numCx = hasQr ? 24.0 : r2(W / 2);
        L.qr = hasQr ? { x: 47.5, y: 8.3, w: 9.0, cap: 0 } : null;
        /* front band: logo bleed + one big brand line, print-safe 24.4 */
        L.mark = { x: 5.4, y: 18.6, w: 12.0, h: 5.8 };
        L.brand = { x: 18.9, cx: 36.9, centered: true,
                    y1: 20.9, y2: 23.6, ySingle: 22.6, size: 4.4, oneLine: true };
        L.text = { x: 18.9, w: 36.0 };
        L.textNoMark = { x: 3.5, w: 56.5, cx: r2(W / 2) };
        L.micro = null;
        /* back band: rotated about its centre 3.8; lane 0.6..6.6 pre-rotation */
        L.footer = { y: 5.15 };
        L.footerSize = 2.7;
        L.voidFit = 23.5;
        L.voidCx = 45.9;
        L.serial2 = { cx: 17.6, y: 5.15, size: 3.8, fit: 23.5 };
        L.serial2Micro = null;
        L.backG = 'rotate(180 ' + r2(W / 2) + ' 3.8)';
        L.micro = { x: r2(17.6 - 8.0), y: 1.95, w: 16.0,
                    drawnByDeco: true, g: L.backG };
        var _sruleL = [45.6, 8.4, 45.6, 17.0, 12.7];
        var _fruleL = [18.15, 18.7, 18.15, 24.35, 21.5];
        L.rules = hasQr ? [_sruleL, _fruleL] : [_fruleL];
        L.art = { x: 5.4, y: 18.7, w: 12.0, h: 5.6, r: 0.7 };
        L.logoSafe = { x: 5.4, y: 18.6, w: 12.0, h: 5.8 };
        L.logoBleed = { x: 5.4, y: 18.33, w: 12.0, h: 7.07 };
        L.deco = {
          ink: '#1f3df5',
          corner: { arm: 1.1, inset: 2.0, top: 8.5, bot: 17.0 },
          diamond: 0.38,
          bar: { y: 0.72, h: 1.5, r: 0.27 },
          sub: null,
          voidSub: { text: 'SHOWS IF PEELED', size: 1.0, dy: 1.0 },
          foldLabel: 'FOLD HERE',
          foldAt: [{ cx: 7.9, y: 8.3,  h: 0.85, ty: 9.0,  w: 7.9, size: 0.78, fit: 7.1 },
                   { cx: 7.9, y: 16.3, h: 0.85, ty: 17.0, w: 7.9, size: 0.78, fit: 7.1 }],
          rulesNoMark: hasQr ? [_sruleL] : []
        };
      }
    } else if (bgz && land) {
      /* ——— BG ZONES, LANDSCAPE: window left, identity centre, QR right.
         Must read as a sibling of the portrait zone stack. ——— */
      L.bgz = true;
      /* the whole left region (owner 2026-08-29, twice): print-safe 1.0mm
         margins, and the identity panel slid right (16.2 -> 18.3) to buy the
         window the width that makes a square logo fill the die height */
      L.art = { x: 1.0, y: 1.0, w: 14.7, h: H - 2.0, r: 0 };
      /* centered between window end (18.0) and the QR patch (33.66):
         18.0 + (15.66 - 12.6)/2. No-QR keeps the wide-plate rule. */
      var bzX = hasQr ? 16.18 : 16.2;
      /* logo mark sits over the window's top-left corner */
      L.mark = { x: 2.0, y: 2.0, w: 4.4, h: 4.4 };
      /* one translucent black panel carries name + eyebrow + serial.
         No QR -> the panel claims the verify zone's width too: a seal
         without a code should read as a wider identity plate, not a plate
         parked beside an empty lot (owner 2026-08-10). */
      var bzlW = hasQr ? 12.6 : r2(W - 1.55 - bzX);
      var bzlCx = r2(bzX + bzlW / 2);
      L.panel = { x: bzX, y: 2.3, w: bzlW, h: hasQr ? 14.4 : 12.6, c: 0.9 };
      L.brand = { x: r2(bzX + 0.8), cx: bzlCx, centered: true,
                  y1: 4.8, y2: 7.25, ySingle: 6.0, size: hasQr ? 2.5 : 2.8 };
      L.text = { x: r2(bzX + 0.8), w: r2(bzlW - 1.6) };
      L.eyebrow = hasQr ? { y: 10.4, size: 1.25 } : { y: 9.4, size: 1.25 };
      L.serial = hasQr ? { y: 14.1, size: 3.5 } : { y: 13.0, size: 3.9 };
      L.qr = hasQr ? { x: 31.67, y: 2.43, w: 9.4, cap: 13.83 } : null;
      L.rule = hasQr ? { cx: 36.37, y: 14.68, w: 7.0 } : null;
      L.micro = hasQr ? { x: 31.37, y: 16.08, w: 10.0 }
                      : { x: r2(bzlCx - 5.0), y: 16.35, w: 10.0 };
      L.footer = { y: hasQr ? 16.73 : 17.8 };  // no-QR fallback, clear of the panel border
      /* quiet dark wash under the verify footer so white type reads */
      L.wash = { x: r2(bzX + bzlW + 0.8), y: 11.13 };
      L.numW = hasQr ? 11.2 : 19.0; L.numCx = bzlCx;
      L.latent = null; L.medal = false; L.scrim = null;
      if (SZ.id === 'large_250x100') {
        /* ===== LARGE DIE (2.5 x 1.0 in) - HAND-REBUILT (owner 2026-08-30).
           window | identity panel | QR, re-cut for 63.5 x 25.4: the window
           goes near-square (21.4 x 23.4), the panel takes 17.6mm and the QR
           column centres a 13.0mm code between panel and die edge. ===== */
        L.art = { x: 1.0, y: 1.0, w: 21.4, h: H - 2.0, r: 0 };
        bzX = 24.0;
        bzlW = hasQr ? 17.6 : r2(W - 1.75 - bzX);
        bzlCx = r2(bzX + bzlW / 2);
        L.mark = { x: 2.6, y: 2.6, w: 6.0, h: 6.0 };
        L.panel = { x: bzX, y: 2.6, w: bzlW, h: hasQr ? 19.6 : 17.2, c: 1.1 };
        L.brand = { x: r2(bzX + 1.0), cx: bzlCx, centered: true,
                    y1: 6.2, y2: 9.6, ySingle: 7.9, size: hasQr ? 3.3 : 3.7 };
        L.text = { x: r2(bzX + 1.0), w: r2(bzlW - 2.0) };
        L.eyebrow = hasQr ? { y: 13.9, size: 1.65 } : { y: 12.6, size: 1.65 };
        L.serial = hasQr ? { y: 18.9, size: 4.6 } : { y: 17.4, size: 5.1 };
        L.qr = hasQr ? { x: 45.5, y: 3.1, w: 13.0, cap: 18.6 } : null;
        L.rule = hasQr ? { cx: 52.0, y: 19.9, w: 9.6 } : null;
        L.micro = hasQr ? { x: 45.2, y: 21.6, w: 13.6 }
                        : { x: r2(bzlCx - 6.7), y: 21.9, w: 13.4 };
        L.footer = { y: hasQr ? 22.6 : 23.3 };
        L.wash = { x: r2(bzX + bzlW + 1.0), y: 15.0 };
        L.numW = hasQr ? 15.6 : 26.0; L.numCx = bzlCx;
      }
    } else if (bgz) {
      /* ——— BG ZONES, PORTRAIT: logo+name / framed window / serial panel /
         QR / verify footer, top to bottom. ——— */
      L.bgz = true;
      L.topH = 9.5;                 // dark overlay behind the top text zone
      /* 9.5 x 2.55 at y 1.45, not 11 x 3.3 at 1.8: the old box ended at
         y=5.1 and the two-line brand's glyphs start at 4.66. Now it ends
         at 4.0, clearing the brand by 0.66mm. Only reachable since the
         customer could ask for 'mark' over a background, so nothing that
         has ever printed moves. */
      L.mark = { x: r2((W - 9.5) / 2), y: 1.45, w: 9.5, h: 2.55 };
      L.brand = hasMark
        ? { x: 2.0, cx: W / 2, centered: true, y1: 6.5, y2: 8.6, ySingle: 7.2, size: 2.35 }
        : { x: 2.0, cx: W / 2, centered: true, y1: 4.2, y2: 6.8, ySingle: 5.4, size: 2.9 };
      L.text = { x: 2.0, w: W - 4.0 };
      /* no QR -> the artwork window grows into the QR zone and the serial
         panel rides down with it — the same absorption the plain portrait
         single has always done */
      L.art = { x: 1.35, y: 9.5, w: W - 2.7, h: hasQr ? 15.5 : 24.0, r: 0 };
      L.panel = { x: 1.55, y: hasQr ? 25.8 : 34.3, w: W - 3.1, h: 5.6, c: 0.7 };
      L.eyebrow = { y: hasQr ? 27.85 : 36.35, size: 1.3 };
      L.serial = { y: hasQr ? 30.75 : 39.25, size: 3.6 };
      L.qr = hasQr ? { x: r2((W - 8.6) / 2), y: 32.2, w: 8.6, cap: 42.3 } : null;
      L.rule = hasQr ? { cx: W / 2, y: 42.75, w: 6.5 } : null;
      L.micro = { x: r2(W / 2 - 5), y: 43.55, w: 10.0 };
      L.footer = { y: 42.2 };      // no-QR fallback line, clear of the domain
      L.washY = hasQr ? 31.0 : 33.0;   // darker wash behind the QR / serial zone
      L.numW = 14.0; L.numCx = W / 2;
      L.latent = null; L.medal = false; L.scrim = null;
      if (SZ.id === 'large_250x100') {
        /* ===== LARGE DIE (1.0 x 2.5 in) - HAND-REBUILT (owner 2026-08-30).
           Same zone order (brand / window / panel / QR / verify footer),
           composed for 25.4 x 63.5. The window and the QR are the winners:
           the die buys a 22.5mm-tall window and an 11.8mm code. ===== */
        L.topH = 13.0;
        L.mark = { x: r2((W - 12.6) / 2), y: 2.0, w: 12.6, h: 3.4 };
        L.brand = hasMark
          ? { x: 2.6, cx: W / 2, centered: true, y1: 9.1, y2: 12.0, ySingle: 10.0, size: 3.2 }
          : { x: 2.6, cx: W / 2, centered: true, y1: 5.9, y2: 9.4, ySingle: 7.5, size: 4.0 };
        L.text = { x: 2.6, w: W - 5.2 };
        L.art = { x: 1.35, y: 13.0, w: W - 2.7, h: hasQr ? 22.5 : 34.6, r: 0 };
        L.panel = { x: 1.55, y: hasQr ? 36.9 : 49.0, w: W - 3.1, h: 7.7, c: 0.9 };
        L.eyebrow = { y: hasQr ? 39.7 : 51.8, size: 1.75 };
        L.serial = { y: hasQr ? 43.7 : 55.8, size: 4.9 };
        L.qr = hasQr ? { x: r2((W - 11.8) / 2), y: 45.9, w: 11.8, cap: 59.9 } : null;
        L.rule = hasQr ? { cx: W / 2, y: 60.5, w: 8.6 } : null;
        L.micro = { x: r2(W / 2 - 6.7), y: 61.5, w: 13.4 };
        L.footer = { y: 59.7 };
        L.washY = hasQr ? 44.1 : 46.8;
        L.numW = 19.0; L.numCx = W / 2;
      }
      /* ——— THE RECLAIM (owner 2026-09-16, ss1115: "our text destroys") ———
         L.topH is a dark overlay painted from y=0 across the FULL die width,
         and the only thing it backs is the store name. Take the name away and
         it is a grey slab lying across the customer's photograph — worse than
         the name was, because at least the name said something. So with no
         name there is no band, and the window takes the space back.

         topH = 0 is what stops the paint: the overlay draws r2(L.topH) tall,
         and a zero-height rect renders nothing. No new condition at the paint
         site, and no second key that could disagree with this one. It is also
         the honest number — the band's height IS zero. (Its companion zone
         probe, _backZone, guards its own divide and a zero-height zone can
         only match the single line y=0, where nothing prints.)

         The window rises to 1.35: the SAME gutter it already holds on its left
         and right edges, so the artwork sits inset by one even margin on three
         sides instead of hanging off a bar.

         Arithmetic off the values just assigned, never fresh literals, so ONE
         block serves BOTH dies — standard 9.5 -> 1.35 (window +8.15mm) and
         large_250x100 13.0 -> 1.35 (window +11.65mm) — and a die added later
         inherits it without being remembered.

         ONLY when the top zone is genuinely empty. hasMark means the customer
         asked for their logo up there: the band is then the MARK's backing,
         not the name's, and pulling it would drop the logo onto bare photo and
         run the window up underneath it. A mark seal keeps its band and simply
         paints no name. (L.mark is left where it is rather than nulled — with
         no mark nothing reads it, and the sites that might are guarded; a null
         here would be a new way for a caller to throw.) */
      if (noBrand && !hasMark) {
        L.art.h = r2(L.art.h + L.art.y - 1.35);
        L.art.y = 1.35;
        L.topH = 0;
        /* The guard clip keeps the watermark out of the WINDOW, but the
           window insets 1.35 where the plate insets 1.0 — so a 0.35mm
           gutter of chopped SERIALSEAL.COM glyphs rings the artwork on
           every bg seal. It has always been there; the dark band used to
           cover the top of it. Reclaim the band and that strip lands in
           open sky (owner 2026-09-16: "theres still some watermark around
           where the text was"). Record the window's bottom edge so the
           watermark can be held BELOW the artwork entirely on these seals.
           Only set when the band is reclaimed, so no seal that has ever
           printed changes. */
        L.wmBelow = r2(L.art.y + L.art.h);
      }
    } else if (land && fold) {
      /* ——— LANDSCAPE FOLDED: vertical crease at W/2, no rotation ——— */
      var vc = W / 2;                            // 22.479
      L.fold = { x: vc, band: 1.26 };
      var idR = vc - 0.63;                       // identity panel right edge
      var vx0 = vc + 0.63;                       // verify panel left edge
      L.art = full
        ? { x: 0.9, y: 6.4, w: idR - 1.8, h: H - 6.4 - 1.0, r: 1.0 }
        : { x: 1.55, y: 6.7, w: idR - 3.1, h: H - 6.7 - 1.55, r: 0.7 };
      L.mark = { x: 1.9, y: 2.0, w: 3.0, h: 3.5 };
      L.brand = hasMark
        ? { x: 5.4, y1: 3.3, y2: 5.75, ySingle: 4.7, size: 2.5 }
        : { x: 2.4, cx: idR / 2 + 0.5, centered: true,
            y1: 3.3, y2: 5.75, ySingle: 4.7, size: 2.5 };
      L.text = { x: 2.4, w: idR - 2.4 - 0.6 };
      var vCx = (vx0 + (W - 1)) / 2;             // 33.53
      L.eyebrow = hasQr ? { y: 3.4, size: 1.35 } : { y: 5.6, size: 1.35 };
      L.serial = hasQr ? { y: 6.35, size: 3.2 } : { y: 9.35, size: 3.8 };
      L.micro = { x: vx0 + 0.9, y: hasQr ? 7.85 : 11.05, w: W - vx0 - 2.0 };
      /* QR fills the lower verify panel; the OFFICIAL LINK caption has no
         room in a 17mm panel — cap:0 skips it */
      L.qr = hasQr ? { x: vCx - 4.5, y: 8.4, w: 9.0, cap: 0 } : null;
      L.footer = { y: 16.4 };
      L.scrim = null;                            // art never crosses the crease
      L.latent = hasQr ? null : [vx0 + 2.5, 11.9, 8.2, 3.9];
      L.numW = W - vx0 - 3.0;   // same optical gutter as the other layouts
      L.numCx = vCx;
      L.medal = { cx: vCx, cy: L.serial.y - 1.0, r: 4.2 };
      if (SZ.id === 'large_250x100') {
        /* ===== LARGE DIE (2.5 x 1.0 in) - HAND-REBUILT (owner 2026-08-30).
           Vertical crease at W/2 (native), identity left / verify right.
           vc, idR, vx0, vCx all derive from the native W, so only this
           shape's own literals are restated: deeper window, 12.4mm code,
           bigger brand and serial. Crease band stays the physical 1.26. ===== */
        L.art = full
          ? { x: 0.9, y: 8.4, w: idR - 1.8, h: H - 8.4 - 1.0, r: 1.2 }
          : { x: 1.55, y: 8.8, w: idR - 3.1, h: H - 8.8 - 1.55, r: 0.9 };
        L.mark = { x: 2.2, y: 2.4, w: 4.2, h: 4.8 };
        L.brand = hasMark
          ? { x: 7.4, y1: 4.3, y2: 7.6, ySingle: 6.2, size: 3.3 }
          : { x: 2.9, cx: r2(idR / 2 + 0.6), centered: true,
              y1: 4.3, y2: 7.6, ySingle: 6.2, size: 3.3 };
        L.text = { x: 2.9, w: idR - 3.8 };
        L.eyebrow = hasQr ? { y: 4.4, size: 1.75 } : { y: 7.2, size: 1.75 };
        L.serial = hasQr ? { y: 8.3, size: 4.2 } : { y: 12.3, size: 5.0 };
        L.micro = { x: r2(vx0 + 1.2), y: hasQr ? 10.3 : 14.6, w: r2(W - vx0 - 2.6) };
        L.qr = hasQr ? { x: r2(vCx - 6.2), y: 11.0, w: 12.4, cap: 0 } : null;
        L.footer = { y: 21.9 };
        L.latent = hasQr ? null : [r2(vx0 + 3.2), 15.6, 11.0, 5.4];
        L.numW = r2(W - vx0 - 4.0);
        L.numCx = vCx;
        L.medal = { cx: vCx, cy: L.serial.y - 1.3, r: 5.6 };
      }
    } else if (land) {
      /* Landscape: art on the left, identity in the middle, QR on the right. */
      var qw = 10.0, qx = W - 1.9 - qw;
      L.qr = hasQr ? { x: qx, y: 3.9, w: qw, cap: 3.9 + qw + 1.2 } : null;
      var colR = hasQr ? qx - 1.4 : W - 2.4;
      L.art = full
        ? { x: 1, y: 1, w: W - 2, h: H - 2, r: 1.35, full: true }
        : { x: 1.55, y: 1.55, w: 13.0, h: H - 3.1, r: 0.7 };
      /* where a FEATURED LOGO may sit when the window is full-bleed: the
         art column only. A slot centred on the whole die drops the logo on
         the identity column and the serial. */
      L.logoSafe = { x: 1.55, y: 1.55, w: 13.0, h: H - 3.1 };
      /* Full-bleed LOGO designs confine the mark to logoSafe on the left —
         the identity column starts PAST it, never across it (owner
         2026-08-10: landscape single drew the serial over the logo window) */
      var tx = full
        ? ((hasMark || design.supports.logo) ? L.logoSafe.x + L.logoSafe.w + 1.5 : 2.6)
        : L.art.x + L.art.w + 1.4;
      L.mark = { x: tx, y: 3.2, w: 3.0, h: 3.5 };
      L.text = { x: tx, w: Math.max(6, colR - tx) };
      L.brand = hasMark
        ? { x: tx + 3.9, y1: 4.1, y2: 6.7, ySingle: 5.6, size: 2.5 }
        : { x: tx, cx: tx + L.text.w / 2, centered: true,
            y1: 4.1, y2: 6.7, ySingle: 5.6, size: 2.5 };
      L.eyebrow = { y: 10.3, size: 1.35 };
      /* serial is the hero */
      L.serial = { y: 14.1, size: 3.8 };
      L.micro = { x: tx, y: 15.5, w: Math.max(6, colR - tx) };
      L.footer = { y: 16.6 };
      /* full-bleed scrim must reach the lockup at tx=2.6 and the serial column,
         not just the QR side — w:22 left light type on raw artwork */
      L.scrim = full ? { side: true, w: 30 } : null;
      /* latent phase panel dodges the QR white backing (x qx..qx+10) — at
         W-15 the QR patch overpainted 5 of its 8mm and gutted the copy trap */
      L.latent = hasQr ? [qx - 9.4, 3, 8, 6] : [W - 15, 3, 8, 6];
      if (SZ.id === 'large_250x100') {
        /* ===== LARGE DIE (2.5 x 1.0 in) - HAND-REBUILT (owner 2026-08-30).
           Same three-column read: art left, identity centre, QR right.
           The window earns real width (18.6), the QR grows to 13.4 and the
           serial to 5.0 - margins stay physical. ===== */
        var qwL = 13.4, qxL = r2(W - 2.2 - qwL);
        L.qr = hasQr ? { x: qxL, y: 5.2, w: qwL, cap: 5.2 + qwL + 1.5 } : null;
        var colRL = hasQr ? qxL - 1.8 : W - 2.9;
        L.art = full
          ? { x: 1, y: 1, w: W - 2, h: H - 2, r: 1.6, full: true }
          : { x: 1.55, y: 1.55, w: 18.6, h: H - 3.1, r: 0.9 };
        L.logoSafe = { x: 1.55, y: 1.55, w: 18.6, h: H - 3.1 };
        var txL = full
          ? ((hasMark || design.supports.logo) ? r2(L.logoSafe.x + L.logoSafe.w + 2.0) : 3.4)
          : r2(L.art.x + L.art.w + 1.8);
        L.mark = { x: txL, y: 4.2, w: 4.2, h: 4.8 };
        L.text = { x: txL, w: Math.max(8, colRL - txL) };
        L.brand = hasMark
          ? { x: txL + 5.2, y1: 5.4, y2: 8.9, ySingle: 7.4, size: 3.4 }
          : { x: txL, cx: r2(txL + L.text.w / 2), centered: true,
              y1: 5.4, y2: 8.9, ySingle: 7.4, size: 3.4 };
        L.eyebrow = { y: 13.7, size: 1.8 };
        L.serial = { y: 18.8, size: 5.0 };
        L.micro = { x: txL, y: 20.7, w: Math.max(8, colRL - txL) };
        L.footer = { y: 22.1 };
        L.scrim = full ? { side: true, w: 42 } : null;
        L.latent = hasQr ? [qxL - 12.6, 4, 10.6, 8] : [W - 20, 4, 10.6, 8];
        L.numW = Math.max(8, colRL - txL) - 1.2;
        L.numCx = r2(txL + L.text.w / 2);
        L.medal = { cx: L.numCx, cy: L.serial.y - 1.4, r: 7.0 };
      }
    } else if (!fold) {
      /* ——— PORTRAIT SINGLE: the classic one-piece vertical stack, for
         seals applied to a flat surface (no seam, no crease). ——— */
      /* With a corner mark the old side-by-side row starved the NAME — the
         mark left ~8mm of column and fit() crushed "TJ BREAKS" to 1.4mm while
         jamming it against the emblem. Stacked instead: mark centred on top,
         brand centred FULL-WIDTH beneath — the art window gives up ~2.5mm. */
      L.art = full
        ? { x: 1, y: 1, w: W - 2, h: H - 2, r: 1.35, full: true }
        : (hasMark
            ? { x: 1.55, y: 10.2, w: W - 3.1, h: hasQr ? 15.26 : 23.08, r: 0.7 }
            : { x: 1.55, y: 7.6, w: W - 3.1, h: hasQr ? 17.86 : 25.68, r: 0.7 });
      /* FEATURED LOGO safe area on a full-bleed window: the region the
         windowed variant would occupy — between the brand lockup and the
         serial block. Centring the slot on the whole die contain-fits a
         square logo across the die's middle, which planted it on top of
         "INDIVIDUALLY NUMBERED" (owner screenshot 2026-07-28). Background
         ART may still bleed edge to edge; the LOGO may not. */
      L.logoSafe = hasMark
        ? { x: 1.55, y: 10.2, w: W - 3.1, h: hasQr ? 15.26 : 23.08 }
        : { x: 1.55, y: 7.6, w: W - 3.1, h: hasQr ? 17.86 : 25.68 };
      L.mark = hasMark
        ? { x: r2((W - 4.6) / 2), y: 1.7, w: 4.6, h: 4.6 }
        : { x: 2.6, y: 2.95, w: 4.8, h: 4.8 };
      L.brand = hasMark
        ? { x: 2.4, cx: W / 2, centered: true, y1: 7.6, y2: 9.7, ySingle: 8.4, size: 2.9 }
        : { x: 2.9, cx: W / 2, centered: true,
            y1: 4.55, y2: 7.15, ySingle: 6.1, size: 2.9 };
      L.text = { x: L.brand.x, w: W - L.brand.x - 2.9 };
      var psy0 = hasQr ? 27.6 : 36.0;
      L.eyebrow = { y: psy0, size: 1.4 };
      L.serial = { y: psy0 + 3.9, size: 4.2 };
      L.micro = { x: 2.2, y: psy0 + 5.3, w: W - 4.4 };
      /* cap gap 1.25 -> 1.0: at 1.25 the caption baseline landed at 44.05 on a
         44.958 die, i.e. 0.09mm of glyph past the 1mm print boundary. The
         block below the QR is genuinely tight (microtext above pins the QR),
         so the gap is what gives. */
      L.qr = hasQr ? { x: r2((W - 9.2) / 2), y: 33.6, w: 9.2, cap: 33.6 + 9.2 + 1.0 } : null;
      L.footer = { y: 43.2 };
      L.scrim = full ? { side: false, h: 8.4, hb: H - (psy0 - 2.2) - 1 } : null;
      L.latent = [4.2, 26.5, 10.6, 6.0];
      L.medal = { cx: W / 2, cy: L.serial.y - 1.1, r: 6.0 };
      if (SZ.id === 'large_250x100') {
        /* ===== LARGE DIE (1.0 x 2.5 in) - HAND-REBUILT (owner 2026-08-30).
           Same stack, composed for 25.4 x 63.5: margins stay 1.55mm, the
           hero serial and the QR take the space the big die actually has.
           Every value below is this shape's own - no derivation. ===== */
        L.mark = hasMark
          ? { x: r2((W - 6.2) / 2), y: 2.2, w: 6.2, h: 6.2 }
          : { x: 2.9, y: 3.6, w: 6.4, h: 6.4 };
        L.brand = hasMark
          ? { x: 2.9, cx: W / 2, centered: true, y1: 10.6, y2: 13.5, ySingle: 11.7, size: 4.0 }
          : { x: 3.2, cx: W / 2, centered: true, y1: 6.3, y2: 9.9, ySingle: 8.4, size: 4.0 };
        L.text = { x: L.brand.x, w: W - L.brand.x - L.brand.x };
        var _py0 = hasQr ? 39.2 : 51.2;
        L.art = full
          ? { x: 1, y: 1, w: W - 2, h: H - 2, r: 1.6, full: true }
          : (hasMark
              ? { x: 1.55, y: 14.6, w: W - 3.1, h: _py0 - 14.6 - 3.4, r: 0.9 }
              : { x: 1.55, y: 11.2, w: W - 3.1, h: _py0 - 11.2 - 3.4, r: 0.9 });
        L.logoSafe = { x: L.art.x, y: L.art.y, w: L.art.w, h: L.art.h };
        L.eyebrow = { y: _py0, size: 1.85 };
        L.serial = { y: _py0 + 5.3, size: 5.7 };
        L.micro = { x: 2.9, y: _py0 + 7.5, w: W - 5.8 };
        L.qr = hasQr
          ? { x: r2((W - 12.0) / 2), y: 48.0, w: 12.0, cap: 48.0 + 12.0 + 1.35 }
          : null;
        L.footer = { y: 61.1 };
        L.scrim = full ? { side: false, h: 11.8, hb: H - (_py0 - 3.1) - 1 } : null;
        L.latent = [5.6, 37.6, 14.2, 8.2];
        L.medal = { cx: W / 2, cy: L.serial.y - 1.5, r: 8.2 };
        L.numW = W - 5.4; L.numCx = W / 2;
      }
    } else {
      /* ——— Portrait = FOLD LAYOUT (owner, from the first physical test:
         the seal wraps a seam — 50% lands on each face, and the fold
         rotates the far half 180°, so a single vertical composition looked
         broken when applied). The die is now TWO self-contained panels
         whose visual tops meet at the crease: fold it over an edge and
         BOTH faces read upright.
           IDENTITY panel = die top half, composed upside-down in die space
           (brand + art). Text/art/logo content rotates via L.identG /
           L.artFlip; rects are 180°-symmetric so window, frame and plate
           cutout stay in die coordinates.
           VERIFY panel = die bottom half, upright: serial block, QR,
           latent panel — always on bare foil (art never crosses the
           crease), so portrait needs no scrims and type is always ink. */
      var crease = H / 2;                       // 22.479
      var identTop = crease - 0.63;             // local y0 sits here
      var identH = identTop - 1;                // 20.85 usable panel height
      L.fold = { y: crease, band: 1.26 };
      L.identG = 'translate(' + r2(W) + ' ' + r2(identTop) + ') rotate(180)';
      /* window in DIE coords (die y = identTop − localY − h); full-bleed is
         contained to the identity panel — folding a full-die image split it
         across the crease, which was the original complaint */
      /* full window starts tighter under the brand and runs deeper (owner
         2026-08-11, portrait folded: "the image could be much larger") */
      var awLocal = full
        ? { x: 0.9, y: 6.7, w: W - 1.8, h: identH - 7.2, r: 1.0 }
        : (hasMark
            ? { x: 1.55, y: 9.6, w: W - 3.1, h: identH - 10.7, r: 0.7 }
            : { x: 1.55, y: 7.3, w: W - 3.1, h: identH - 8.4, r: 0.7 });
      /* a logo IN the window carries no frame on this layout (owner: it "has
         a border around the logo") — artwork windows keep theirs */
      L.noLogoFrame = true;
      L.art = { x: awLocal.x, y: identTop - awLocal.y - awLocal.h,
                w: awLocal.w, h: awLocal.h, r: awLocal.r };
      L.artFlip = 'rotate(180 ' + r2(W / 2) + ' ' + r2(L.art.y + L.art.h / 2) + ')';
      /* identity text block — LOCAL coords inside L.identG */
      L.mark = hasMark
        ? { x: r2((W - 4.6) / 2), y: 1.0, w: 4.6, h: 4.6 }
        : { x: 2.6, y: 1.85, w: 4.8, h: 4.8 };
      L.brand = hasMark
        ? { x: 2.4, cx: W / 2, centered: true, y1: 6.9, y2: 9.0, ySingle: 7.6, size: 2.9 }
        : { x: 2.9, cx: W / 2, centered: true,
            y1: 3.6, y2: 6.25, ySingle: 5.15, size: 2.9 };
      L.text = { x: L.brand.x, w: W - L.brand.x - 2.9 };
      /* verify panel — DIE coords, upright below the crease */
      var vy0 = crease + 0.63;                  // 23.11
      L.eyebrow = { y: vy0 + 2.4, size: 1.4 };
      L.serial = { y: vy0 + 6.1, size: 3.9 };
      L.micro = { x: 2.2, y: vy0 + 7.8, w: W - 4.4 };
      /* the QR is the panel's purpose — let it eat the dead space below it */
      L.qr = hasQr
        ? { x: r2((W - 10.6) / 2), y: vy0 + 8.6, w: 10.6, cap: vy0 + 8.6 + 10.6 + 1.45 }
        : null;
      L.footer = { y: H - 2.3 };
      L.scrim = null;
      /* no QR -> the latent phase panel is the verify zone's centrepiece:
         wider, and deep enough to reach the footer instead of floating in
         a half-empty panel */
      L.latent = hasQr ? null : [r2((W - 12.6) / 2), vy0 + 9.0, 12.6, 9.4];
      if (SZ.id === 'large_250x100') {
        /* ===== LARGE DIE (1.0 x 2.5 in) - HAND-REBUILT (owner 2026-08-30).
           Same two-panel fold: identity upside-down above the crease, verify
           upright below. crease/identTop/vy0 derive from the native H, so
           only this shape's own literals are restated. ===== */
        awLocal = full
          ? { x: 0.9, y: 8.6, w: W - 1.8, h: identH - 9.2, r: 1.2 }
          : (hasMark
              ? { x: 1.55, y: 12.6, w: W - 3.1, h: identH - 13.9, r: 0.9 }
              : { x: 1.55, y: 9.6, w: W - 3.1, h: identH - 10.9, r: 0.9 });
        L.art = { x: awLocal.x, y: identTop - awLocal.y - awLocal.h,
                  w: awLocal.w, h: awLocal.h, r: awLocal.r };
        L.artFlip = 'rotate(180 ' + r2(W / 2) + ' ' + r2(L.art.y + L.art.h / 2) + ')';
        L.mark = hasMark
          ? { x: r2((W - 6.0) / 2), y: 1.2, w: 6.0, h: 6.0 }
          : { x: 2.9, y: 2.4, w: 6.2, h: 6.2 };
        L.brand = hasMark
          ? { x: 2.9, cx: W / 2, centered: true, y1: 8.9, y2: 11.7, ySingle: 9.9, size: 3.6 }
          : { x: 3.2, cx: W / 2, centered: true, y1: 4.7, y2: 8.2, ySingle: 6.7, size: 3.9 };
        L.text = { x: L.brand.x, w: W - L.brand.x * 2 };
        L.eyebrow = { y: vy0 + 3.2, size: 1.85 };
        L.serial = { y: vy0 + 8.3, size: 5.2 };
        L.micro = { x: 2.9, y: vy0 + 10.6, w: W - 5.8 };
        L.qr = hasQr
          ? { x: r2((W - 13.6) / 2), y: vy0 + 11.8, w: 13.6,
              cap: vy0 + 11.8 + 13.6 + 1.7 }
          : null;
        L.footer = { y: H - 2.6 };
        L.latent = hasQr ? null : [r2((W - 16.4) / 2), vy0 + 12.4, 16.4, 12.4];
      }
    }
    /* ——— BG ZONES ON FOLD LAYOUTS: the zoned singles' design language —
       framed empty window, one translucent cut-corner serial panel, dark
       washes under white type — DERIVED from the fold slots above. The face
       geometry itself never moves; only zone furniture is added. Washes
       live in L.fwash (folds have no topH/washY/wash): 'top' backs an
       identity strip that ENDS at its window (landscape: brand above the
       window), 'fid' backs the portrait identity strip, which STARTS at its
       window and reads toward the crease, so the gradient runs the other
       way; 'bot' backs the verify zone. The 'fid' rect starts 1.6mm inside
       the window and rides the _bgguard clip, so its fade lives over the
       window edge and the brand glyphs all sit on the flat dark body —
       without ever tinting the window itself. The serial panel takes the
       domain microtext too: on folds it sits 1.5–1.7mm under the serial
       (the singles park it far away, by the QR), and a panel edge through
       its glyphs is worse than a slightly taller panel. */
    if (bgf) {
      L.bgz = true;
      L.medal = false;    // lathework under a translucent panel over art is mud
      var pT = r2(L.eyebrow.y - L.eyebrow.size - 0.55);
      var pB = r2(L.micro.y + (land ? 0.5 : 0.65));
      if (land) {
        var fx0 = r2(L.fold.x + L.fold.band / 2);
        L.panel = { x: r2(fx0 + 0.7), y: pT, w: r2(W - 1.55 - fx0 - 0.7),
                    h: r2(pB - pT), c: 0.8 };
        L.fwash = [
          { x: 0, y: 0, w: r2(L.fold.x - L.fold.band / 2), h: r2(L.art.y), g: 'top' },
          { x: fx0, y: pB, w: r2(W - fx0), h: r2(H - pB), g: 'bot' }];
      } else {
        var aB = r2(L.art.y + L.art.h);
        /* LOCAL identity coords (inside L.identG). The shared fold slot is
           4.6 x 4.6 at y 1.0 and ends at 5.6, while the brand's glyphs start
           at 4.64 — a ~1mm overlap that only shows now that the mark has an
           opaque backing behind it. Re-cut here, in the bg branch, so the
           legacy non-bg fold (which draws no backing) is untouched. */
        if (hasMark) L.mark = (SZ.id === 'large_250x100')
          ? { x: r2((W - 12.6) / 2), y: 1.0, w: 12.6, h: 4.0 }
          : { x: r2((W - 9.5) / 2), y: 0.75, w: 9.5, h: 3.1 };
        L.panel = { x: 1.55, y: pT, w: r2(W - 3.1), h: r2(pB - pT), c: 0.7 };
        L.fwash = [
          { x: 0, y: r2(aB - 1.6), w: W, h: r2(L.fold.y - L.fold.band / 2 - aB + 1.6),
            g: 'fid', clip: true },
          { x: 0, y: pB, w: W, h: r2(H - pB), g: 'bot' }];
      }
    }
    /* Number column defaults — branches that positioned their own verify
       panel (landscape-fold) already set these.

       The margin here is optical, not just legal. At W-3.2 the eyebrow and the
       serial both filled their budget and landed 0.57mm from the clip — inside
       it, so nothing was actually chopped, but on an 88px card preview 0.57mm
       is under two pixels and "INDIVIDUALLY NUMBERED" read as running off both
       edges (owner 2026-07-28). W-4.4 roughly doubles the gutter for a ~5%
       type reduction, which is the cheaper trade. */
    if (L.numW == null) L.numW = land ? L.text.w - 1.0 : W - 4.4;
    if (L.numCx == null) L.numCx = land ? (L.text.x + L.text.w / 2) : (W / 2);
    /* rosette medallion centred behind the serial block; fold radii are
       panel-scaled so the lathework stays inside the verify panel.
       (== null, not falsy: bg-zone layouts set false to opt out — lathework
       under a translucent panel over busy art is mud, not security) */
    if (L.medal == null) L.medal = { cx: L.numCx, cy: L.serial.y - 1.1, r: land ? 5.2 : 4.6 };
    /* the shape's identity, and the editor's handle on it */
    /* fold arrives as true | 'top' | 'top2' | falsy, so a plain fold keyed
       itself "landscape:true" — a name nobody can read in the editor's
       shape list. Same five shapes, spelled the way the UI spells them. */
    L.shapeKey = (SZ.isDefault ? '' : SZ.id + ':') +
                 orient + ':' + (fold === true ? 'fold' : (fold || 'single'));
    return mergeOverrides(L, L.shapeKey);
  }

  /* (kept only for the shield-motif art in ss-art.js lookalikes — the seal
     lockup no longer draws a default shield) */
  function shield(cx, cy, w, col) {
    var h = w * 1.18, x = cx - w / 2, y = cy - h / 2;
    return '<path d="M' + r2(x) + ' ' + r2(y + h * 0.06) +
      'L' + r2(cx) + ' ' + r2(y) + 'L' + r2(x + w) + ' ' + r2(y + h * 0.06) +
      'L' + r2(x + w) + ' ' + r2(y + h * 0.55) +
      'Q' + r2(x + w) + ' ' + r2(y + h * 0.88) + ' ' + r2(cx) + ' ' + r2(y + h) +
      'Q' + r2(x) + ' ' + r2(y + h * 0.88) + ' ' + r2(x) + ' ' + r2(y + h * 0.55) + 'Z" ' +
      'fill="none" stroke="' + col + '" stroke-width="' + r2(w * 0.1) + '"/>' +
      '<text x="' + r2(cx) + '" y="' + r2(cy + h * 0.21) + '" font-family="Helvetica" font-size="' +
      r2(w * 0.68) + '" font-weight="bold" text-anchor="middle" fill="' + col + '">S</text>';
  }

  function artSvg(design, accent, id, ctx) {
    var raw = typeof design.art === 'function'
      ? design.art({ color: accent, ink: INK, light: LIGHT, id: id,
                     W: ctx.W, H: ctx.H, sig: ctx.sig, q: ctx.q, G: G })
      : String(design.art || '');
    raw = raw.split('{{color_hex}}').join(accent).split('{{id}}').join(id);
    /* WHITE IS FOIL: there is no white ink, so white/near-white art prints as
       bare rainbow stock — the preview must show that, not flat white. Only
       fill/stroke attrs are rewritten (gradient stop-colors stay literal).
       Print side ignores this mapping: whites go out as authored (= no ink). */
    var holoRef = 'url(#' + id + '_holoink)';
    /* PURE WHITE = NO PRINT = THE HOLO BACKGROUND ITSELF. Design areas
       authored #ffffff are bare-foil declarations, so they render as a bare
       12% frost and the REAL animated holo ground under the SVG shows through
       — not a painted stand-in. Near-white (#e9eaf2) detail lines sitting ON
       ink keep the foil-gradient glint (nothing behind them but ink). */
    raw = raw.replace(/(fill|stroke)="(#ffffff|#fff)"/gi,
                      '$1="rgba(255,255,255,0.12)"');
    raw = raw.replace(/(fill|stroke)="#e9eaf2"/gi, '$1="' + holoRef + '"');
    /* ART COLOR: designs with a `palette` map the 5-tone structural ink
       family onto their own vibrant colors (owner: "colorize them, vibrant,
       Pokémon-like") — flat fills AND gradient stops. Ink still prints solid
       and full-density; only the HUES change, so black-on-black art becomes
       energy-card color over live foil. Designs without a palette keep the
       rich-black deepen ladder. */
    var pal = design.palette;
    if (pal) {
      raw = raw
        .replace(/(fill|stroke|stop-color)="#0c0d13"/gi, '$1="' + pal.s1 + '"')
        .replace(/(fill|stroke|stop-color)="#14161f"/gi, '$1="' + pal.s2 + '"')
        .replace(/(fill|stroke|stop-color)="(?:#1c1f2b|#141a26)"/gi, '$1="' + pal.s3 + '"')
        .replace(/(fill|stroke|stop-color)="#a9b0c6"/gi, '$1="' + pal.s4 + '"')
        .replace(/(fill|stroke|stop-color)="(?:#000000|#000|#111116|#0a0c14)"/gi, '$1="' + pal.s5 + '"');
    } else {
      raw = raw
        .replace(/(fill|stroke|stop-color)="#0c0d13"/gi, '$1="#04050a"')
        .replace(/(fill|stroke|stop-color)="#14161f"/gi, '$1="#090b12"')
        .replace(/(fill|stroke|stop-color)="#1c1f2b"/gi, '$1="#10131c"')
        .replace(/(fill|stroke|stop-color)="#111116"/gi, '$1="#05050a"');
    }
    return raw;
  }

  /* Cover-fit a bitmap into a window: fill and crop, never letterbox. */
  function imageEl(img, rect, fit) {
    fit = fit || {};
    var z = fit.zoom || 1;
    var dw = rect.w * z, dh = rect.h * z;
    var dx = rect.x - (dw - rect.w) / 2 + (fit.dx || 0) * rect.w;
    var dy = rect.y - (dh - rect.h) / 2 + (fit.dy || 0) * rect.h;
    return '<image href="' + esc(img) + '" x="' + r2(dx) + '" y="' + r2(dy) +
      '" width="' + r2(dw) + '" height="' + r2(dh) +
      '" preserveAspectRatio="xMidYMid slice"/>';
  }

  function build(design, f, q) {
    f = f || {}; q = q == null ? 1 : q;
    /* inkSafe clamps pale accents to a printable depth — light ink is
       invisible on the holographic foil, so no path may bypass the clamp */
    var accent = (window.SS && window.SS.inkSafe)
      ? window.SS.inkSafe(f.color || design.defaults.color) : (f.color || '#5b21b6');
    /* LOGO CARRIES THE SEAL: the literal 'none' on the brand channel is the
       no-brand sentinel — the third of the family, beside f.serial === 'none'
       below and f.qr === 'none' under that. One key, one decision: there is no
       second flag naming the same choice (see the logo_mode note further down
       for what happens when two flags describe one thing and then disagree).
       Nothing name-shaped may draw — and 'none' must never be the DEFAULT for
       a missing brand, only for an explicit one, which is why this reads
       f.brand alone and not the design fallback chain on the next line.
       A seal that drops its name must still be identified by SOMETHING, so
       the builder only offers this when a logo is uploaded (gateOk()); the
       renderer does not re-litigate that here — it draws what it is handed.
       Unlike the serial sentinel, this one DOES move the maths: the name sat
       in a dark overlay band across the top of the customer's artwork, and an
       empty band is worse than a full one (owner 2026-09-16, ss1115: "our
       text destroys"). The bg-zone portrait branch in layout() hands that
       strip back to the window — see the reclaim block there. */
    var noBrand = String(f.brand || '') === 'none';
    var brand = noBrand ? ''
      : String(f.brand || design.defaults.brand || 'YOUR BRAND').toUpperCase();
    /* PLAIN STICKERS: the literal 'none' on the serial channel is the
       no-serial sentinel — the twin of f.qr === 'none' below. Nothing
       serial-shaped may draw: no number, no INDIVIDUALLY NUMBERED eyebrow,
       no serial plate, no back-face repeat, no SERIAL NUMBER caption. The
       layout maths stay untouched everywhere — freed space simply stays
       background/foil, which IS the product being bought. */
    var noSerial = String(f.serial || '') === 'none';
    var serial = noSerial ? '' : String(f.serial || '001');
    var runTotal = f.run_total ? String(f.run_total) : '';
    /* Every seal carries a QR. If the customer gave a link, it goes to their
       link. If they didn't, it goes to THIS SEAL'S verification record — the
       serial is already registered at order time, so the code resolves to
       proof-of-issue rather than scanning to nothing. That turns dead foil
       into the product's whole pitch: anyone holding the card can check it.
       Suppress with f.qr === 'none' (or a design that has no QR slot). */
    var qrRaw = String(f.qr || '');
    var qrLc = qrRaw.toLowerCase();
    /* 'business:<CODE>' — a SerialSeal Business dynamic QR. Tested FIRST and
       given its own branch because it is the one mode whose printed string
       this file composes itself (see businessUrl above); every other mode
       arrives as a finished destination. */
    var isBiz = qrLc.indexOf('business:') === 0;
    /* 'sku' -- the printed code IS this seal's own serial (see skuCode
       above). 'sku:<CODE>' is accepted too, for a line that ever carries an
       explicit per-seal code: that form rides the road Business already uses
       (the render sidecar's qr_for map -> RENDER_ONE_JS's `qr` argument) and
       needs nothing new built. Neither literal can arrive as a customer link:
       normUrl() puts 'https://' in front of anything without a scheme and
       qrProblem() refuses a host with no dot, which is the same property that
       makes 'none', 'auto' and 'business:' safe sentinels today. */
    var isSku = qrLc === 'sku' || qrLc.indexOf('sku:') === 0;
    /* 'sku' is neither '' nor 'auto', so usingVerify is already false for it.
       Deliberately NOT given an !isSku term -- it would read as load-bearing. */
    var usingVerify = !isBiz && (!qrRaw || qrLc === 'auto');
    /* THE PRINTED WORDMARK FOLLOWS THE QR (owner 2026-08-19: "it's
       automatic — if url put their website, if ebay put ebay link, if
       whatnot put whatnot, if social put @ with username, if nothing
       serialseal.com"). The FOREGROUND lines only — the diagonal watermark
       repeat stays ours as provenance. f.site_text survives as an explicit
       override for records that carried one. */
    var siteWord = '';
    /* TWO LINES, when one will not do (owner 2026-09-11: "if the url is too
       long just do username \n url"). siteWord stays the ONE-line string and
       is untouched for every link that fits; siteLines is the split form —
       [STORE NAME, HOST] — offered alongside it and taken only by a paint
       site that has the room AND can set both halves at a legible size. */
    var siteLines = null;
    /* the IDEAL one line: host + store name, before any fallback. siteWord
       may already have degraded to the bare host by the time a paint site
       reads it, and "is the one line too small?" has to be asked of the line
       the customer actually wanted, not of the short thing we fell back to. */
    var siteFull = '';
    (function () {
      var u = String(f.site_text || '').trim() || qrRaw;
      /* isSku: a POS payload is a SERIAL, not a URL, and it walks this whole
         ladder unchallenged if let through -- 'FRP-K7D4Q' has no scheme and
         no slash, so the plain-URL branch treats the entire string as the
         host, the charset test passes, and the seal prints its own serial a
         SECOND time in the microtext slot, dressed as the customer's domain.
         Silent, and the worst failure mode in this mode. Bailing here drops
         siteWord to the 'SERIALSEAL.COM' default below, which is exactly what
         Business and the verify default already do and exactly the owner's
         2026-08-19 rule: "if nothing serialseal.com". In POS mode the
         customer named no link, so there is nothing else honest to print. */
      if (!u || qrLc === 'none' || isBiz || isSku || usingVerify || /^mailto:/i.test(u)) return;
      u = u.replace(/^https?:\/\//i, '').replace(/^www\./i, '');
      var host = u.split(/[\/?#]/)[0].toLowerCase();
      if (!host) return;
      var path = u.split(/[?#]/)[0].split('/').slice(1).filter(Boolean);
      var pick, i, seg;
      if (/(^|\.)((instagram|facebook|fb|tiktok|x|twitter|youtube|threads|linktr)\.(com|ee)|twitch\.tv)$/.test(host)) {
        /* social: @username — the first meaningful path segment */
        seg = '';
        for (i = 0; i < path.length; i++) {
          var pv = path[i].replace(/^@/, '');
          if (pv && ['user', 'c', 'channel', 'profile', 'pages', 'people']
                .indexOf(pv.toLowerCase()) < 0) { seg = pv; break; }
        }
        pick = seg ? '@' + seg : host;
      } else if (/(^|\.)ebay\./.test(host) || host === 'whatnot.com') {
        /* marketplace: host + the store name, plumbing segments dropped */
        seg = '';
        for (i = path.length - 1; i >= 0; i--) {
          var pw = path[i];
          if (pw && ['usr', 'str', 'sch', 'itm', 'b', 'user', 'live', 's']
                .indexOf(pw.toLowerCase()) < 0 && !/^\d+$/.test(pw)) { seg = pw; break; }
        }
        pick = host + (seg ? '/' + seg : '');
      } else {
        pick = host;                       /* a plain URL: their website */
      }
      /* LEGIBILITY LADDER (2026-09-11, ss1105): the combined host/segment
         form carries a ~30 character ceiling, and a long store name blows it
         — PawPaw's EBAY.COM/PAWPAWCARDSANDCOLLECTIBLES is 35. The old single
         test then dropped the customer's link entirely and printed OUR domain
         on THEIR seal. Fall back to the bare host first: EBAY.COM still sends
         the finder to the right marketplace and is the honest short form.
         THE HOST IS NOT LENGTH-CAPPED (2026-09-11 fix). It is the last honest
         thing we can print, and an ordinary shop domain runs long —
         thecardbreakroomcollectibles.com is 32 characters and has no segment
         to split, so a ceiling here put SERIALSEAL.COM on that customer's own
         seal, which is exactly what the owner ruled out ("serialseal.com only
         when the customer named no link"). Charset is still enforced; the
         size is the fit pass's problem, not the ladder's.
         serialseal.com is now reachable ONLY when `pick` is empty. */
      var _OK = /^[@A-Z0-9][A-Z0-9.\/@_-]{2,29}$/;
      var _OKCH = /^[@A-Z0-9][A-Z0-9.\/@_-]{2,}$/;      /* charset, no ceiling */
      var _hostOnly = String(host).toUpperCase();
      siteFull = String(pick).toUpperCase();
      siteWord = _OK.test(siteFull) ? siteFull
               : (_OKCH.test(_hostOnly) ? _hostOnly : '');
      /* THE SPLIT is OFFERED here and DECIDED at the paint site. The store
         name goes on top and the host under it, which is the order the owner
         asked for and also the useful one: the name identifies the seller,
         the host is what the finder already knows. No segment (a plain long
         hostname) — nothing to split, so one line stands.
         WHY NOT DECIDE IT HERE: this function knows the string, not the box.
         Gating the split on the 30-character ceiling — as the first cut did —
         fires on the wrong seals in both directions, because it is WIDTH that
         binds. Measured on the large die, all still one line: EBAY.COM/
         TITANICLITTLELISTINGS (30 ch) set at 0.92mm, EBAY.COM/W x19 (28 ch) at
         0.73mm, EBAY.COM/VINTAGECARDVAULT99 (27 ch) at 0.94mm — every one of
         them crushed and none of them split, while PawPaw's 35 characters did.
         So the offer is made whenever there is a segment at all and the paint
         site, which knows its own box, asks fitSize() whether the one line is
         actually being squeezed. Charset only here: an over-long half is
         refused by that site's own floor, in mm, where it belongs. */
      if (seg) {
        var _seg1 = (siteFull.charAt(0) === '@' ? '@' : '') +
                    String(seg).toUpperCase();
        if (_OKCH.test(_seg1) && _OKCH.test(_hostOnly)) siteLines = [_seg1, _hostOnly];
      }
    })();
    siteWord = siteWord || 'SERIALSEAL.COM';
    /* no serial -> nothing for the verify URL to name. Forced to the no-QR
       path here, not left to verifyUrl('') happening to return '' — a code
       encoding 'verify?s=' must be unreachable by construction. */
    var qrLink = qrLc === 'none' ? ''
      : isBiz ? businessUrl(qrRaw.slice(9))
      /* POS: the bare serial, verbatim. noSerial is not decoration -- a plain
         sticker (f.serial === 'none') has no number to BE a code, and an empty
         payload must be unreachable by construction rather than merely
         unlikely, the same reason the verify branch below carries the guard.
         hasQr then falls false on its own and the layout reflows to its
         no-QR geometry with nothing further to change. */
      : isSku ? (noSerial ? '' : skuCode(qrLc === 'sku' ? serial : qrRaw.slice(4)))
      : (usingVerify ? (noSerial ? '' : verifyUrl(serial)) : qrRaw);
    var hasQr = !!(design.supports.qr && qrLink);
    /* a chosen motif fills the code's slot when there is no code — the
       layout keeps its filled-slot geometry. A real QR always wins. */
    var motifKey = String(f.motif || '').trim().toLowerCase();
    var hasMotif = !hasQr && !!(design.supports.qr && MOTIFS[motifKey]);
    var id = design.key;
    var orient = f.orientation || design.orientation || 'portrait';
    var holoPlate = design.plate === 'holo';
    var img = f.image || design.image || '';
    var fit = f.fit || design.fit;
    /* per-customer BACKGROUND (builder upload, a data URL): one resolved
       channel that behaves exactly like a studio design.bg — every bg
       consumer below reads bgImg, never design.bg directly */
    /* background resolution: the customer's own upload (f.bg) always wins;
       studio designs then pick the crop BAKED FOR THIS SHAPE (the studio
       positions each of the four individually), with the portrait-single
       image (design.bg) as the fallback for designs published before
       per-shape crops existed. */
    var _fold = f.style === 'fold';
    var bgImg = f.bg || '';
    if (!bgImg) {
      bgImg = (orient === 'landscape'
        ? (_fold ? design.bg_lf : design.bg_ls)
        : (_fold ? design.bg_pf : ''))
        || design.bg || '';
    }
    /* Logo classification runs BEFORE layout: the brand line only shares the
       top row with a corner mark when a logo actually sits there — otherwise
       the brand takes the full width (the old Serial Seal shield is gone). */
    var caps = design.caps || {};
    var logoCat = caps.category === 'logo';
    /* ——— WHERE THE LOGO GOES: ONE KEY, THREE ANSWERS ———
       A background and a logo are LAYERS the customer arranges, not two
       things competing for one slot (owner 2026-08-08: "upload bg and also
       a logo for the ultimate customization"). A bg therefore no longer
       forces the logo into the window; f.logo_mode is the whole decision:

         'off'     their own picture ALREADY contains their logo, so
                   printing the uploaded file on top would print it twice.
         'mark'    the lockup slot at the top of the seal — over a
                   background this is what leaves the picture unobstructed.
         'window'  featured, centred in the framed window (the default, and
         or ''     what every bg + logo seal did before this).

       'off' replaces a SECOND builder flag (st.bgOwnLogo) that used to sit
       beside logo_mode and describe the same choice. Two flags for one
       decision can disagree, and when they do the preview and the printed
       seal disagree with them — so there is one key and it lives here. */
    var logoOff = String(f.logo_mode || '') === 'off';
    var hasLogo = !!f.logo && !logoOff;
    var wantMark = f.logo_mode === 'mark';
    var logoAsArt = hasLogo && !wantMark &&
      (!!bgImg || logoCat || f.logo_mode === 'window' || (!img && !design.art));
    var dropArt = logoAsArt && !logoCat;   // logo beds keep their frame art
    var hasMark = hasLogo && !logoAsArt;
    /* ⚠ ON A BG SEAL THE "WINDOW FRAME" IS THE LOGO'S FRAME, NOTHING ELSE.
       Owner 2026-08-08: "customers are adding the bg and no logo and that
       container around the logo needs to drop if they don't upload the logo
       and just have a bg."

       The background BLEEDS to the die (see the bgImg <image> at x=0 y=0 W×H
       further down) — it is not painted inside L.art. So on a bg seal the
       framed window is not a window into anything: it is a rounded rectangle
       drawn ON TOP of the customer's picture. With a logo in it, it reads as
       the logo's container and belongs. With nothing in it — no logo, logo
       'off' because the picture already contains one, or logo 'mark' up in the
       lockup — it is an empty box sitting across their artwork.

       This ONE flag drives both the frame and the mark anchor, so the frame can
       never outlive the thing it frames. Do not re-derive either site's
       condition locally; that is how they drift apart. */
    var logoInWindow = hasLogo && logoAsArt;
    /* seal style: 'fold' wraps a seam, 'single' lies flat. The default
       everywhere is the plain single layout (owner call) — folding is an
       explicit choice in the builder's style picker. */
    var foldStyle = f.style === 'topfold' ? 'top'
      : f.style === 'topfold2' ? 'top2'
      : f.style === 'duo' ? 'duo' : f.style === 'fold';
    /* A bg design reserves the mark slot only when the customer ASKED for
       it (logo_mode 'mark'); the default is still the window, so the brand
       keeps the full top row unless the customer moved the logo up there.
       The slot geometry already exists in every bg branch of layout()
       (L.mark, and L.brand yields to it) — it was only ever unreachable. */
    var markSlot = hasMark;
    var L = layout(orient, design, hasQr || hasMotif,
                   /* duo needs to know about ANY logo, not just a corner
                      mark - its halves re-space around the logo slot */
                   markSlot || (hasLogo && foldStyle === 'duo'),
                   foldStyle, bgImg,
                   /* brandTxt is the RAW name, so it cannot take the `brand`
                      shortcut: f.brand is the truthy string 'none' on a
                      no-brand seal and `f.brand || brand` would hand the
                      banner's line-balancer the literal word to measure and
                      lay out. The sentinel is a mode, never copy. */
                   noBrand ? '' : String(f.brand || brand), f.label_size_id,
                   noBrand);
    /* the editor reads this to discover what zones exist for the shape on
       screen, and what they currently measure. Read-only by convention —
       edits go through SS_LAYOUT_OVERRIDES so they survive a re-render. */
    try { window.SS_LAST_LAYOUT = L; } catch (_) {}
    /* A layout may ship its OWN ink, and it outranks f.color. The accent
       picker was retired, so a Clean Foil order still carries the old
       purple default (#5b21b6) in its properties — the 2026-08-14 banner
       design is royal blue, and every accent on it has to agree or the
       seal renders half one colour and half the other (which is exactly
       what the first render did). */
    /* ——— NO-NUMBERING SPACE RECLAIM (owner 2026-08-30, ss1071: "the layout
       needs to update and fill the spaces") ———
       With numbering off, the eyebrow/serial stack does not draw — and on
       the FOLD shapes their panel kept painting around the void: a big empty
       box parked over the customer's art with the QR crowding the die edge
       below it (the bgz portrait single already drops its panel; the folds
       never did). With a code present the verify half becomes a QR HERO:
       the code grows and centres in its half, the handle/domain line sits
       just above it, the caption under it, and the bg-fold panel re-wraps
       that block instead of the void. Without a code the empty panel simply
       goes — the latent centrepiece already owns that case. Sizes both work:
       everything derives from the native W/H and the crease. */
    if (noSerial && L.fold && !L.topfold && !L.duo) {
      if (L.qr) {
        if (L.land) {
          var _vx3 = L.fold.x + L.fold.band / 2;
          var _vw3 = L.W - 1.0 - _vx3;
          /* the half is short: handle line (1.9) + code + caption (1.65) +
             panel padding all stack inside H-2 - the code yields, never the
             text (a clipped handle was v1's bug) */
          var _q3 = Math.min(L.H * 0.515, _vw3 - 4.0);
          L.qr.w = _q3;
          L.qr.x = r2(_vx3 + (_vw3 - _q3) / 2);
          L.qr.y = r2((L.H - _q3) / 2 + 0.65);
          L.qr.cap = r2(L.qr.y + _q3 + 1.6);
          if (L.micro) L.micro.y = r2(L.qr.y - 1.5);
        } else {
          var _vy3 = L.fold.y + L.fold.band / 2;
          var _vh3 = L.H - 1.0 - _vy3;
          var _q4 = Math.min(13.2, L.W - 5.8, _vh3 - 7.0);
          L.qr.w = _q4;
          L.qr.x = r2((L.W - _q4) / 2);
          L.qr.y = r2(_vy3 + (_vh3 - _q4) / 2 - 0.5);
          L.qr.cap = r2(L.qr.y + _q4 + 1.65);
          if (L.micro) L.micro.y = r2(L.qr.y - 1.9);
        }
        if (L.panel) {
          var _pT3 = r2(L.micro ? L.micro.y - 2.0 : L.qr.y - 1.5);
          var _pB3 = r2(L.qr.cap + 1.15);
          L.panel.y = _pT3;
          L.panel.h = r2(_pB3 - _pT3);
          if (L.fwash) {
            for (var _wi = 0; _wi < L.fwash.length; _wi++) {
              if (L.fwash[_wi].g === 'bot') {
                L.fwash[_wi].y = _pB3;
                L.fwash[_wi].h = r2(L.H - _pB3);
              }
            }
          }
        }
      } else if (L.panel) {
        L.panel = null;
      }
    }
    if (L.deco && L.deco.ink) {
      accent = (window.SS && window.SS.inkSafe)
        ? window.SS.inkSafe(L.deco.ink) : L.deco.ink;
    }
    /* SLAB BANNER, NO LOGO (owner 2026-08-11: "box around logo not needed …
       lots of dead space"). The window is a foil hole punched through the ink
       band; with a mark in it that hole IS the logo's field, but empty it is a
       blank foil box eating a seventh of the front face. Drop it and give the
       whole band to the brand name — the same call made for bg seals on
       2026-08-08, applied to the banner's own slot. Must sit after layout()
       (L does not exist before it) and before any zone is read. */
    if (L.topfold && !logoInWindow) {
      /* PREVIEW keeps the dashed YOUR LOGO slot on both banners (owner
         2026-08-11: "slab banner needs the your logo area also" /
         "toploader needs your logo area") — a logo design without a logo
         yet shows where it lands instead of pretending the design has no
         slot. Only when no placeholder will draw (logo already off-design,
         print pass, or a non-logo design) does the window collapse and the
         brand take the whole band. */
      /* f._print is set ONLY by the press sidecar (tools/render_seals.py
         RENDER_ONE_JS). 2026-09-08: order ss1100 staged 600 cells with the dashed
         YOUR LOGO slot because the print pass mounts at q=1 and nothing told this
         file it was printing — the comment above promised a collapse the code
         never made. A placeholder is preview furniture; it must never print. */
      var _tfPh = !f.logo && design.supports.logo && q >= 0.8 && logoCat && !f._print;
      if (!_tfPh) {
        L.art = { x: 0, y: 0, w: 0, h: 0, r: 0 };
        L.logoSafe = null;
        if (L.textNoMark) {
          L.brand.x = L.textNoMark.x;
          if (L.textNoMark.cx) { L.brand.cx = L.textNoMark.cx; L.brand.centered = true; }
          L.text = L.textNoMark;
        }
        /* furniture that divided the logo from the name has nothing left to
           divide — the slab's front-band rule sits in the 0.90mm gutter the
           collapsed slot used to leave, which the brand now runs straight
           through. Shapes that name no replacement keep whatever they had. */
        if (L.deco && L.deco.rulesNoMark) L.rules = L.deco.rulesNoMark;
      }
    }
    var W = L.W, H = L.H;
    /* HOLO INK: there is no white ink on this stock — anything light prints
       as NOTHING, which means live rainbow foil. So light elements are not
       "white" in the preview either: they fill with the foil gradient
       (id_holoink), which is what the press actually produces. The print
       side maps every url(#*_holoink)/url(#*_foilpatch) reference and every
       data-holo element to plain white (= no ink = foil). */
    var HOLO = 'url(#' + id + '_holoink)';
    /* Type color follows the GROUND it sits on, not the plate flag:
       — dark flood plate, or full-bleed image under its ink scrim → foil type
         (light = unprinted = rainbow through the dark)
       — bare foil (the holo-heavy default: no flood, ink only where drawn)
         → ink type. Foil type on foil ground would vanish. */
    /* Fold layouts keep ALL type on bare foil (art is contained to the
       identity panel), so ink type always. SINGLE layouts with full-bleed
       images still scrim + flip to foil type. */
    /* Full-bleed IMAGE seals used to flip to foil type over the scrims; the
       text pills (4d) replaced that strategy — the pill is foil, so the type
       on it must be INK or it vanishes into its own backing (owner: unreadable
       text on busy backgrounds). Dark ink-flood plates still take foil type. */
    var darkGround = !holoPlate;
    var TXT = darkGround ? HOLO : INK;
    /* bg-zone type is WHITE (owner spec): on this stock #ffffff is a bare-foil
       knockout — the panels/overlays behind it are what guarantee contrast */
    if (L.bgz) TXT = '#ffffff';

    /* ——— GROUND LUMINANCE: measured, and asked ONCE, HERE ———
       Two places downstream need the answer and they are far apart: the defs
       block a few lines below (the bg-zone washes and panel deepen on light
       ground) and the text pass at the very end of build(). So it is settled
       before either of them runs.
       THE TXT FLIP IS NOT MOVED WITH IT - it still happens at the banner block
       further down. Furniture drawn between here and there (the back-band
       panel outline) reads TXT, and flipping earlier would change it. */
    var bannerOutline = !!(L.topfold && bgImg) || !!L.lgOutline;
    /* zone art draws all type white over the artwork; on light art (Cosmic,
       order ss1059) that washes out - and white here prints as bare foil. */
    var zoneOutline = !!L.bgz && !bannerOutline;
    var gLevel = (bannerOutline || zoneOutline)
      ? groundLevel(design, bgImg, orient, f.style) : 0;
    var lightGround = gLevel === 1;       /* MEASURED light: the full fix */
    var backGround = gLevel !== 0;        /* light OR unmeasured: back the type */
    /* inkSafe clamps any ink to a depth the press can actually hold; INK is
       already the press black, but it goes through the same gate as every
       other colour on the seal rather than round it. */
    var INKT = backGround
      ? ((window.SS && window.SS.inkSafe) ? window.SS.inkSafe(INK) : INK) : INK;
    /* THE BG-ZONE BACKING, DEEPENED ON LIGHT GROUND.
       A zoned layout already draws the thing that makes its knockout work - a
       translucent ink panel and two gradient washes - and on pale artwork it is
       simply too thin: at 0.45 the panel leaves the brightest crops in the
       catalog (Prismatic, Pagoda Garden) at ~3.6:1 under white type. Deepening
       IT, rather than buying contrast a glyph at a time, is the right lever. It
       covers exactly the area it already covered, it cannot merge with the row
       above, and it stays the designed shape instead of a scalloped sticker.
       Nowhere near the 0.93 solids that once turned uploaded artwork into black
       bars - the panel keeps its cut corners and accent hairline and the art
       still reads through it.
       DARK GROUND EMITS THE ORIGINAL LITERALS, CHARACTER FOR CHARACTER. */
    /* WHAT THE LAYOUT ITSELF PUTS UNDER THE TYPE, collected as it is drawn.
       Each entry is a die-space band with the gradient that fills it, as
       [offset, alpha] stops down its height - the same numbers the linear
       gradients in the defs are built from, so this cannot drift from what is
       painted. _backAlphaAt() reads it; lightGroundPass() uses the answer to
       decide whether a run is backed or on bare artwork. */
    var _backZ = [];
    function _backZone(x, y, w, h, stops) { _backZ.push([x, y, w, h, stops]); }
    function _backAlphaAt(x, y) {
      var best = 0, i, j;
      for (i = 0; i < _backZ.length; i++) {
        var z = _backZ[i];
        if (x < z[0] || x > z[0] + z[2] || y < z[1] || y > z[1] + z[3]) continue;
        var t = z[3] > 0 ? (y - z[1]) / z[3] : 0, st = z[4], a = st[st.length - 1][1];
        for (j = 1; j < st.length; j++) {
          if (t <= st[j][0]) {
            var t0 = st[j - 1][0], t1 = st[j][0];
            var k = t1 > t0 ? (t - t0) / (t1 - t0) : 0;
            a = st[j - 1][1] + (st[j][1] - st[j - 1][1]) * k;
            break;
          }
        }
        if (a > best) best = a;
      }
      return best;
    }
    /* 2 = backed to the floor | 1 = partly backed | 0 = bare artwork.

       THE TWO QUESTIONS READ DIFFERENT PROBES, and that is the whole trick.
       "Is this run finished?" has to hold across the BAND THE EYE READS - a
       third of a cap height above the caps and below the baseline - because
       that band is what the contrast is measured over. The brand line on the
       landscape panel sits 0.5mm under the panel's top edge: dead centre it
       has 0.66 of ink under it, and its reading band is half on bare art. It
       measured 4.49:1 while the centre probe called it finished. So the SKIP
       test takes the WORST of three probes.
       "Is this run on bare artwork?" is about where the run IS, not where its
       halo lands, so the FLIP test reads the centre alone. Taking the worst
       there would flip the brand name to ink on top of a deep ink panel, which
       is the mirror bug.

       +0.06 is margin: needA lands a run exactly ON 4.5:1, and "exactly on the
       floor, add nothing" is not a decision worth making - a run that close
       takes the bloom and comes back at 6-9:1. x0.55 is the other end: below
       it there is too little ink under the run for any bloom that fits between
       two rows to finish the job. */
    var _needA = needAlpha(groundHi(design, bgImg, orient, f.style));
    function _backClassAt(x, yTop, yMid, yBot) {
      var aC = _backAlphaAt(x, yMid);
      var aMin = Math.min(aC, _backAlphaAt(x, yTop), _backAlphaAt(x, yBot));
      if (aMin >= _needA + 0.06) return 2;
      return aC >= _needA * 0.55 ? 1 : 0;
    }
    var _zo = (lightGround && zoneOutline)
      ? { t0: '0.78', t1: '0.7', b1: '0.52', b2: '0.74',
          d0: '0.7', d1: '0.78', pn: '0.66' }
      : { t0: '0.5', t1: '0.44', b1: '0.3', b2: '0.48',
          d0: '0.44', d1: '0.5', pn: '0.45' };
    var _ZSTOPS = { top: [[0, +_zo.t0], [0.85, +_zo.t1], [1, 0]],
                    bot: [[0, 0], [0.5, +_zo.b1], [1, +_zo.b2]],
                    fid: [[0, 0], [0.15, +_zo.d0], [1, +_zo.d1]] };
    var sig = G.signature(brand + '|' + (runTotal || 'open') + '|' + design.key);

    /* ——— LOGO PLACEMENT ——— (classified above, before layout)
       A logo is FEATURED, not stamped in a corner. Three ways it lands:
       — logo-category designs: always centred large in the window (the bed
         art frames it); the tiny lockup-corner slot was the bug the owner
         hit ("adding the logo didn't remove the original design").
       — designer/artwork designs with f.logo_mode 'window': the logo
         REPLACES the artwork and takes the window.
       — f.logo_mode 'mark': keep the art, logo takes the lockup corner. */

    var o = [];
    /* the viewBox IS the die: layout() hands back native mm for the selected
       size, so no scale group exists any more (owner 2026-08-30: manual
       rebuild, not a scaler) */
    o.push('<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 ' + r2(W) + ' ' + r2(H) + '"' +
           (L.fold ? ' data-fold="' + (L.land ? 'v' : 'h') + '"' : '') + '>');

    o.push('<defs>');
    /* the foil itself, as an ink: light prismatic pastels so type stays
       legible on the dark plate while clearly reading as live rainbow foil */
    /* GLOSS WHITE STOCK (2026-10-18): no foil under the ink, so every
       "unprinted = rainbow" knockout is unprinted = white. Preview only -
       the print side maps these refs to plain white on every stock. */
    o.push(f.white
      ? '<linearGradient id="' + id + '_holoink" x1="0" y1="0" x2="1" y2="1">' +
        '<stop offset="0" stop-color="#ffffff"/>' +
        '<stop offset="0.5" stop-color="#f6f6f8"/>' +
        '<stop offset="1" stop-color="#ffffff"/></linearGradient>'
      : '<linearGradient id="' + id + '_holoink" x1="0" y1="0" x2="1" y2="1">' +
      '<stop offset="0" stop-color="#93f2e2"/>' +
      '<stop offset="0.26" stop-color="#a6c4ff"/>' +
      '<stop offset="0.5" stop-color="#dcaeff"/>' +
      '<stop offset="0.73" stop-color="#ffb0e2"/>' +
      '<stop offset="1" stop-color="#ffe2a4"/></linearGradient>');
    /* the QR backing is unprinted stock too — bright silver foil, not paper
       white. Faint prismatic cast keeps the preview honest without hurting
       the module contrast. */
    o.push(f.white
      ? '<linearGradient id="' + id + '_foilpatch" x1="0" y1="0" x2="1" y2="1">' +
        '<stop offset="0" stop-color="#ffffff"/>' +
        '<stop offset="1" stop-color="#ffffff"/></linearGradient>'
      : '<linearGradient id="' + id + '_foilpatch" x1="0" y1="0" x2="1" y2="1">' +
      '<stop offset="0" stop-color="#f4fbfa"/>' +
      '<stop offset="0.5" stop-color="#eef0fd"/>' +
      '<stop offset="1" stop-color="#fdf3f6"/></linearGradient>');
    o.push('<clipPath id="' + id + '_artclip">' + (design.window === 'circle'
      ? '<circle cx="' + r2(L.art.x + L.art.w / 2) + '" cy="' + r2(L.art.y + L.art.h / 2) +
        '" r="' + r2(Math.min(L.art.w, L.art.h) / 2) + '"/>'
      : '<rect x="' + r2(L.art.x) + '" y="' + r2(L.art.y) + '" width="' + r2(L.art.w) +
        '" height="' + r2(L.art.h) + '" rx="' + L.art.r + '"/>') + '</clipPath>');
    o.push('<clipPath id="' + id + '_plate"><rect x="1" y="1" width="' + r2(W - 2) +
           '" height="' + r2(H - 2) + '" rx="1.35"/></clipPath>');
    /* Full-bleed art would swallow the type, so it gets a scrim — down the
       right in landscape (where the identity column sits), top and bottom in
       portrait. */
    /* Scrim gradients: enough ink for foil type to read, but a FADE — the
       old 0.93-solid slabs turned uploaded artwork into black bars (owner
       bug report). Peaks land where the type sits, transitions ghost the
       art through. */
    var sd = !!(L.scrim && L.scrim.side);
    o.push('<linearGradient id="' + id + '_scrimT" x1="0" y1="0" x2="' + (sd ? 1 : 0) + '" y2="' + (sd ? 0 : 1) + '">' +
           '<stop offset="0" stop-color="' + INK + '" stop-opacity="' + (sd ? 0 : 0.85) + '"/>' +
           '<stop offset="0.35" stop-color="' + INK + '" stop-opacity="' + (sd ? 0.55 : 0.55) + '"/>' +
           '<stop offset="0.75" stop-color="' + INK + '" stop-opacity="' + (sd ? 0.85 : 0.18) + '"/>' +
           '<stop offset="1" stop-color="' + INK + '" stop-opacity="' + (sd ? 0.9 : 0) + '"/></linearGradient>');
    o.push('<linearGradient id="' + id + '_scrimB" x1="0" y1="0" x2="0" y2="1">' +
           '<stop offset="0" stop-color="' + INK + '" stop-opacity="0"/>' +
           '<stop offset="0.22" stop-color="' + INK + '" stop-opacity="0.62"/>' +
           '<stop offset="0.5" stop-color="' + INK + '" stop-opacity="0.88"/>' +
           '<stop offset="1" stop-color="' + INK + '" stop-opacity="0.92"/></linearGradient>');
    if (L.bgz) {
      /* bg-zone furniture: top overlay behind the identity zone (portrait),
         bottom wash behind the QR/verify zone, and an evenodd guard clip
         that keeps security texture OUT of the unobstructed art window */
      o.push('<linearGradient id="' + id + '_bgtop" x1="0" y1="0" x2="0" y2="1">' +
             '<stop offset="0" stop-color="' + INK + '" stop-opacity="' + _zo.t0 + '"/>' +
             '<stop offset="0.85" stop-color="' + INK + '" stop-opacity="' + _zo.t1 + '"/>' +
             '<stop offset="1" stop-color="' + INK + '" stop-opacity="0"/></linearGradient>');
      o.push('<linearGradient id="' + id + '_bgbot" x1="0" y1="0" x2="0" y2="1">' +
             '<stop offset="0" stop-color="' + INK + '" stop-opacity="0"/>' +
             '<stop offset="0.5" stop-color="' + INK + '" stop-opacity="' + _zo.b1 + '"/>' +
             '<stop offset="1" stop-color="' + INK + '" stop-opacity="' + _zo.b2 + '"/></linearGradient>');
      o.push('<clipPath id="' + id + '_bgguard"><path clip-rule="evenodd" d="' +
             roundRect(1, 1, W - 2, H - 2, 1.35) + ' ' +
             cutRect(L.art.x, L.art.y, L.art.w, L.art.h, 1.0) + '"/></clipPath>');
      /* NESTED with _bgguard, never instead of it: the intersection is
         (plate minus window) AND (below the window), i.e. the verify zone
         the watermark has always occupied — with no gutter ring left
         beside or above the artwork. */
      if (L.wmBelow) {
        o.push('<clipPath id="' + id + '_wmlower"><rect x="0" y="' +
               r2(L.wmBelow) + '" width="' + r2(W) + '" height="' +
               r2(Math.max(0, H - L.wmBelow)) + '"/></clipPath>');
      }
      if (L.fold) {
        /* portrait-fold identity strip: it sits BELOW its window, so the
           wash is _bgtop reversed — fade at the window edge, flat dark
           through the brand zone toward the crease */
        o.push('<linearGradient id="' + id + '_bgfid" x1="0" y1="0" x2="0" y2="1">' +
               '<stop offset="0" stop-color="' + INK + '" stop-opacity="0"/>' +
               '<stop offset="0.15" stop-color="' + INK + '" stop-opacity="' + _zo.d0 + '"/>' +
               '<stop offset="1" stop-color="' + INK + '" stop-opacity="' + _zo.d1 + '"/></linearGradient>');
      }
    }
    o.push('</defs>');

    /* ——— 1. PLATE ——— */
    if (L.art.full && img) {
      /* artwork runs edge to edge; nothing to flood underneath */
    } else if (holoPlate) {
      /* bare foil: ink only where drawn */
    } else if (L.art.full) {
      /* full-bleed frame with no artwork yet — cutting the window here would
         punch out the whole plate and strand light type on bright foil */
      o.push('<path fill="' + INK + '" d="' + roundRect(1, 1, W - 2, H - 2, 1.35) + '"/>');
    } else {
      o.push('<path fill-rule="evenodd" fill="' + INK + '" d="' +
             roundRect(1, 1, W - 2, H - 2, 1.35) + ' ' +
             roundRect(L.art.x, L.art.y, L.art.w, L.art.h, L.art.r) + '"/>');
    }

    /* ——— 1b. STUDIO BACKGROUND ———
       design.bg is a Seal Studio background: it replaces the bare paper UNDER
       the whole template — the template's art, furniture and type render over
       it unchanged (owner 2026-08-02: "just put a background on the existing
       template, not fill in the hero image"). Distinct from design.image /
       f.image, which fill the ART slot. slice = cover; the die crops it.
       Drawn BEFORE the plate clip on purpose: the plate is inset 1mm from
       the cut edge, and clipping the background to it left a visible empty
       border all round (owner: "padded border of nothing") — background
       art BLEEDS to the die like any printed label. */
    if (bgImg) {
      o.push('<image href="' + esc(bgImg) + '" x="0" y="0" width="' + r2(W) +
        '" height="' + r2(H) + '" preserveAspectRatio="xMidYMid slice"/>');
    }
    o.push('<g clip-path="url(#' + id + '_plate)">');

    /* — back-band panel (2026-08-14 design refresh). Background furniture,
         so it belongs here with the plate: everything that follows — the QR
         above all — paints over it instead of under it. */
    if (L.deco && L.deco.panel) {
      var _dp = L.deco.panel;
      o.push('<rect x="' + r2(_dp.x) + '" y="' + r2(_dp.y) + '" width="' + r2(_dp.w) +
             '" height="' + r2(_dp.h) + '" rx="' + r2(_dp.r) +
             '" fill="none" stroke="' + TXT + '" stroke-width="0.14" opacity="0.4"/>');
    }

    /* ——— 2. ARTWORK ——— */
    o.push('<g clip-path="url(#' + id + '_artclip)">');
    /* fold layout: art content rotates 180° about its own window centre so
       it reads upright on the applied face; the window rect itself is
       rotation-symmetric and stays in die space */
    if (L.artFlip) o.push('<g transform="' + L.artFlip + '">');
    if (L.bgz) {
      /* the window belongs to the background — it shows through, unobstructed */
    } else if (dropArt) {
      /* the logo took the window — bare foil behind it, nothing else */
    } else if (img) o.push(imageEl(img, L.art, fit));
    else if (design.art) {
      if (design.fullbleed) o.push(artSvg(design, accent, id, { W: W, H: H, sig: sig, q: q }));
      else {
        /* Vector art is authored in a 100 × 117 box. COVER-fit it uniformly so
           it is never squashed when the window changes shape between
           orientations — the same rule the bitmaps get. */
        var s = Math.max(L.art.w / 100, L.art.h / 117);
        var ox = L.art.x + (L.art.w - 100 * s) / 2;
        var oy = L.art.y + (L.art.h - 117 * s) / 2;
        o.push('<g transform="translate(' + r2(ox) + ' ' + r2(oy) + ') scale(' + r2(s * 1000) / 1000 + ')">');
        o.push(artSvg(design, accent, id, { W: 100, H: 117, sig: sig, q: q }));
        o.push('</g>');
      }
    }
    /* No artwork and no vector art: the window stays bare foil. The old dashed
       placeholder rect here read as a stray hairline bug at card scale — the
       exposed holo IS the empty state, it needs no furniture. */
    if (L.artFlip) o.push('</g>');
    o.push('</g>');

    /* Scrim only exists to make type readable OVER an image — with no image
       the window is bare foil and painting the scrim anyway produced black
       bands with invisible ink-on-ink type. */
    if (L.scrim && img) {
      if (L.scrim.side) {
        o.push('<rect x="' + r2(W - 1 - L.scrim.w) + '" y="1" width="' + r2(L.scrim.w) +
               '" height="' + r2(H - 2) + '" fill="url(#' + id + '_scrimT)"/>');
      } else {
        o.push('<rect x="1" y="1" width="' + r2(W - 2) + '" height="' + L.scrim.h +
               '" fill="url(#' + id + '_scrimT)"/>');
        o.push('<rect x="1" y="' + r2(H - L.scrim.hb - 1) + '" width="' + r2(W - 2) +
               '" height="' + L.scrim.hb + '" fill="url(#' + id + '_scrimB)"/>');
      }
    }

    /* ——— 2b. BG-ZONE OVERLAYS ———
       Portrait: dark translucent overlay behind the top text zone, quieter
       wash behind the QR zone. Landscape: wash under the verify footer only —
       the identity panel is its own backing. Translucent ink prints as tinted
       ink, so preview == print. */
    if (L.bgz) {
      if (L.fwash) {
        /* fold washes, precomputed by the layout post-pass */
        for (var fw = 0; fw < L.fwash.length; fw++) {
          var fz = L.fwash[fw];
          var fr = '<rect x="' + r2(fz.x) + '" y="' + r2(fz.y) + '" width="' + r2(fz.w) +
                   '" height="' + r2(fz.h) + '" fill="url(#' + id + '_bg' + fz.g + ')"/>';
          o.push(fz.clip ? '<g clip-path="url(#' + id + '_bgguard)">' + fr + '</g>' : fr);
          _backZone(fz.x, fz.y, fz.w, fz.h, _ZSTOPS[fz.g]);
        }
      } else if (L.land) {
        o.push('<rect x="' + r2(L.wash.x) + '" y="' + r2(L.wash.y) + '" width="' +
               r2(W - L.wash.x) + '" height="' + r2(H - L.wash.y) +
               '" fill="url(#' + id + '_bgbot)"/>');
        _backZone(L.wash.x, L.wash.y, W - L.wash.x, H - L.wash.y, _ZSTOPS.bot);
      } else {
        o.push('<rect x="0" y="0" width="' + r2(W) + '" height="' + r2(L.topH) +
               '" fill="url(#' + id + '_bgtop)"/>');
        o.push('<rect x="0" y="' + r2(L.washY) + '" width="' + r2(W) + '" height="' +
               r2(H - L.washY) + '" fill="url(#' + id + '_bgbot)"/>');
        _backZone(0, 0, W, L.topH, _ZSTOPS.top);
        _backZone(0, L.washY, W, H - L.washY, _ZSTOPS.bot);
      }
    }

    /* ——— 3. SECURITY GROUND: fine-line raster with a latent phase panel.
       Copy trap — a scan or photocopy aliases this into visible banding.
       On dark plates the lines are HOLO (white in print = foil engraved
       through the ink); on bare foil they are ink. Either way a photocopy
       kills the foil and exposes itself. ——— */
    var bareFoil = holoPlate && (!img || !!L.fold);
    if (L.bgz) {
      /* the art window stays unobstructed — raster only the zones around it.
         Ink on printed background art, subtle by design. */
      var rr = { q: q, color: INK, stroke: 0.055, spacing: 0.62,
                 amp: 0.13, opacity: 0.16, latent: null };
      if (L.fold) {
        /* folds: one full-die pass behind the guard clip — the window is
           the hole in the clip, everything around it gets the texture */
        o.push('<g clip-path="url(#' + id + '_bgguard)">' +
               G.raster(1, 1, W - 2, H - 2, sig, rr) + '</g>');
      } else if (L.land) {
        o.push(G.raster(r2(L.art.x + L.art.w), 1,
                        r2(W - 1 - L.art.x - L.art.w), H - 2, sig, rr));
      } else {
        o.push(G.raster(1, 1, W - 2, r2(L.art.y - 1), sig, rr));
        o.push(G.raster(1, r2(L.art.y + L.art.h), W - 2,
                        r2(H - 1 - L.art.y - L.art.h), sig, rr));
      }
    } else {
      o.push(G.raster(1, 1, W - 2, H - 2, sig, {
        q: q, color: bareFoil ? INK : HOLO, stroke: 0.055, spacing: 0.62,
        /* 25% lighter on the refreshed banner: at 0.16 the fine-line
           raster was reading THROUGH the 0.72mm captions and greying
           them (owner: "competing with the small text"). */
        amp: 0.13,
        opacity: (bareFoil ? 0.16 : 0.28) * (L.deco ? 0.75 : 1),
        latent: L.latent
      }));
    }

    /* ——— 3b. WATERMARK — the brand ghosted across the whole die ———
       Classic security-document furniture: the customer's own name tiled
       diagonally at a ~9% ink screen. Reads as a whisper on the foil, sits
       over the art the way the raster does. A counterfeiter has to rebuild
       it per brand, and a photocopy drops a 9% screen entirely. */
    /* vendor imprint, not customer text: every seal ships watermarked with the
       site — the owner's call ("serialseal.com on every seal as branding") */
    var wmUnit = 'SERIALSEAL.COM · ';
    var wmStr = '';
    while (wmStr.length < 64) wmStr += wmUnit;
    var wmSpan = Math.max(W, H) * 1.6;
    var wmRows = ['<g transform="rotate(-28 ' + r2(W / 2) + ' ' + r2(H / 2) +
                  ')" opacity="0.09" data-wm="1">'];
    for (var wr = 0, wy = -wmSpan / 2; wy <= wmSpan / 2; wy += 4.6, wr++) {
      wmRows.push('<text x="' + r2(W / 2 - wmSpan / 2 + (wr % 2 ? 2.4 : 0)) +
        '" y="' + r2(H / 2 + wy) + '" font-family="Helvetica" font-size="2.1" ' +
        'font-weight="bold" letter-spacing="0.12" fill="' + INK + '">' +
        esc(wmStr) + '</text>');
    }
    wmRows.push('</g>');
    /* bg zones: the watermark stays out of the unobstructed art window */
    /* duo halves with a bg plate: the plate covers nearly the whole half, so
       the rows only survive as slivers down the die edges - and stacked row
       fragments in a narrow slot read as SIDEWAYS text, which reads as the
       whole proof being rotated (owner 2026-08-31: "the size proof
       inverts"). The plate, raster and baked art carry the look there. */
    if (L.duo && L.panel) { /* no watermark under the duo plate */ }
    else if (L.bgz) {
      var _wm = '<g clip-path="url(#' + id + '_bgguard)">' + wmRows.join('') + '</g>';
      o.push(L.wmBelow
        ? '<g clip-path="url(#' + id + '_wmlower)">' + _wm + '</g>'
        : _wm);
    } else o.push(wmRows.join(''));

    /* ——— 3c. ROSETTE MEDALLION — engine-turned lathe work behind the serial,
       the banknote move. Foil line-work on dark plates, ink on bare foil.
       Parameters come from the issuer signature, so no two brands share a
       curve. Skipped on thumbnails where 0.06mm strokes alias to fuzz. ——— */
    /* same duo-plate rule: through the translucent plate the lathe-work is
       just a dark smudge behind the serial */
    if (q >= 0.6 && L.medal && !(L.duo && L.panel)) {
      o.push('<g opacity="' + (bareFoil ? 0.34 : 0.5) + '" data-holo="1">' +
        G.rosette(L.medal.cx, L.medal.cy, L.medal.r, sig,
          { q: q, color: bareFoil ? INK : HOLO, stroke: 0.06, fade: true }) +
        '</g>');
    }

    /* ——— 4. (no border furniture) ———
       The guilloche edge bands and the inner keyline that used to stack here
       are gone — at any screen size they read as skinny lines trapped inside
       the seal (owner call, 2026-07-26; bands were still visible down both
       sides of the q=1 builder preview after the thumbnail gate). Removed from
       preview AND print together so preview == print holds. The security story
       is carried by the raster copy-trap, the latent phase panel, the serial
       microtext and the deterministic signature; the border system is the
       plate edge against live foil plus one confident window frame. */
    /* bgz + logo-in-window used to draw a cut-corner accent frame here.
       Removed (owner 2026-08-29, ss1061: "drop the purple border") - the same
       furniture judgement the banner made in 2026-08-11: a customer's logo
       needs no box. bgz ARTWORK windows never had this frame, so nothing else
       changes. Gate kept preview/press identical; so does its absence. */
    if (false) {
    } else if (!L.bgz && !L.art.full && !L.topfold && !L.duo &&
               !(logoInWindow && L.noLogoFrame)) {
      /* !L.topfold: the banner's logo sits directly on the foil end-cap with
         no container (owner 2026-08-11, ss1017: "box around logo not
         needed … has a border on it") — the accent frame is for windowed
         layouts where the slot needs an edge against the plate.
         logoInWindow && noLogoFrame: portrait fold drops the frame the same
         way, but ONLY around a logo — its artwork windows keep theirs. */
      o.push(design.window === 'circle'
        ? '<circle cx="' + r2(L.art.x + L.art.w / 2) + '" cy="' + r2(L.art.y + L.art.h / 2) +
          '" r="' + r2(Math.min(L.art.w, L.art.h) / 2) + '" fill="none" stroke="' + accent + '" stroke-width="0.22"/>'
        : '<rect x="' + r2(L.art.x) + '" y="' + r2(L.art.y) + '" width="' + r2(L.art.w) +
          '" height="' + r2(L.art.h) + '" rx="' + L.art.r + '" fill="none" stroke="' + accent + '" stroke-width="0.22"/>');
    }

    /* ——— 4b. GRADE BADGE ———
       A collector's own condition call, printed where a grader's box sits: the
       top outer corner of the art window. Ink the box, knock the number out to
       bare foil (#ffffff is a bare-foil declaration in this file, never white
       ink) so the grade FLASHES — it is the one number on the seal a buyer
       looks for first. Deliberately generic: a box and a numeral, carrying no
       company name, because imitating a grading firm's label is the one thing
       this must never do. */
    var grade = String(f.grade == null ? '' : f.grade).trim().slice(0, 4);
    if (grade && !L.art.full) {
      var gw = Math.max(3.4, Math.min(L.art.w * 0.34, 5.6));
      var gh = gw * 0.72;
      var gx = L.art.x + L.art.w - gw - 0.55, gy = L.art.y + 0.55;
      o.push('<rect x="' + r2(gx) + '" y="' + r2(gy) + '" width="' + r2(gw) +
        '" height="' + r2(gh) + '" rx="' + r2(gh * 0.22) + '" fill="' + INK + '"/>');
      o.push('<rect x="' + r2(gx + 0.28) + '" y="' + r2(gy + 0.28) + '" width="' + r2(gw - 0.56) +
        '" height="' + r2(gh - 0.56) + '" rx="' + r2(gh * 0.16) +
        '" fill="none" stroke="' + accent + '" stroke-width="0.16"/>');
      o.push('<text x="' + r2(gx + gw / 2) + '" y="' + r2(gy + gh * 0.42) +
        '" text-anchor="middle" font-family="Helvetica" font-size="' + r2(gh * 0.26) +
        '" font-weight="bold" letter-spacing="0.07" fill="#ffffff" data-onink="1">GRADE</text>');
      o.push('<text x="' + r2(gx + gw / 2) + '" y="' + r2(gy + gh * 0.86) +
        '" text-anchor="middle" font-family="Helvetica" font-size="' + r2(gh * 0.52) +
        '" font-weight="bold" fill="#ffffff" data-onink="1" data-fit="' + r2(gw - 1.0) +
        '" data-fit-hard="1">' + esc(grade) + '</text>');
    }

    /* ——— 4c. TECH CHROME (design.chrome === 'tech') ———
       Cyber Frame furniture: octagonal keyline around the logo window, corner
       slashes, a gradient brand rule, an outlined serial plate and QR corner
       brackets. Deliberately restrained — the furniture must frame the
       customer's logo, never compete with it. All ink + one gradient, so it
       prints exactly as previewed. */
    if (design.chrome === 'tech' && !L.bgz) {
      var cyb = 'url(#' + id + '_cybg)';
      o.push('<defs><linearGradient id="' + id + '_cybg" x1="0" y1="0" x2="1" y2="1">' +
        '<stop offset="0" stop-color="#0e7490"/><stop offset="1" stop-color="#6d28d9"/>' +
        '</linearGradient></defs>');
      function octPath(x, y, w2, h2, c) {
        return 'M' + r2(x + c) + ' ' + r2(y) + 'H' + r2(x + w2 - c) +
          'L' + r2(x + w2) + ' ' + r2(y + c) + 'V' + r2(y + h2 - c) +
          'L' + r2(x + w2 - c) + ' ' + r2(y + h2) + 'H' + r2(x + c) +
          'L' + r2(x) + ' ' + r2(y + h2 - c) + 'V' + r2(y + c) + 'Z';
      }
      /* the logo window, framed: ink line with a gradient echo a hair outside */
      if (!L.art.full) {
        var co = Math.min(2.4, L.art.w * 0.15);
        o.push('<path d="' + octPath(L.art.x - 0.5, L.art.y - 0.5, L.art.w + 1, L.art.h + 1, co) +
          '" fill="none" stroke="' + INK + '" stroke-width="0.42"/>');
        o.push('<path d="' + octPath(L.art.x - 1.15, L.art.y - 1.15, L.art.w + 2.3, L.art.h + 2.3, co + 0.35) +
          '" fill="none" stroke="' + cyb + '" stroke-width="0.22" opacity="0.9"/>');
        /* corner slashes, top-right of the frame */
        for (var ci = 0; ci < 3; ci++) {
          var sx = L.art.x + L.art.w - 2.6 + ci * 1.05, sy = L.art.y - 2.1;
          o.push('<path d="M' + r2(sx) + ' ' + r2(sy + 1.3) + 'L' + r2(sx + 0.85) + ' ' + r2(sy) +
            '" stroke="' + cyb + '" stroke-width="0.32" opacity="0.95"/>');
        }
      }
      /* serial plate: an outlined octagon spanning eyebrow + number — with
         no serial there is nothing to plate, and an empty outlined octagon
         reads as a misprint */
      if (!noSerial && !L.duo && L.eyebrow && L.serial) {
        var pw = Math.min(L.numW + 2.6, W - 2.6), px = L.numCx - pw / 2;
        var pt = L.eyebrow.y - L.eyebrow.size - 1.0;
        /* stop the plate above the microtext line — at portrait scale the two
           are 1.5mm apart and the first render clipped SERIALSEAL.COM */
        /* ...but ONLY when the two share a coordinate space. L.micro.g names a
           group the domain is drawn inside (the slab banner puts it on the
           rotated BACK band, pre-rotation y 1.48), and that number means
           nothing here: read as a die y it clamped this plate to its 3mm floor
           and ran the bottom rail straight through the 3.6mm serial. The plate
           and the domain are on opposite faces of that shape — there is
           nothing to stop short of. */
        var pb = L.serial.y + 1.15;
        if (L.micro && L.micro.y && !L.micro.g) pb = Math.min(pb, L.micro.y - 1.5);
        if (pb - pt < 3) pb = pt + 3;
        o.push('<path d="' + octPath(px, pt, pw, pb - pt, 1.5) +
          '" fill="none" stroke="' + INK + '" stroke-width="0.4"/>');
        o.push('<path d="' + octPath(px + 0.55, pt + 0.55, pw - 1.1, pb - pt - 1.1, 1.2) +
          '" fill="none" stroke="' + cyb + '" stroke-width="0.2" opacity="0.85"/>');
      }
      /* brand rule: one gradient hairline under the lockup */
      /* `&& brand`, the same shape as the `!noSerial` guard on the serial
         plate above: this rule UNDERLINES the name, and with no name it is a
         coloured hairline ruling off blank foil. Cyber Frame is a non-bg
         design (`!L.bgz`), so it is not the ss1115 seal — but a logo-only
         order on this design would have carried the orphan. Strictly a no-op
         for anything already printed: before the sentinel existed `brand`
         fell through to design.defaults.brand and then to 'YOUR BRAND', so it
         was never empty on any seal that has ever rendered. */
      if (brand && L.brand && L.brand.ySingle) {
        var bw2 = Math.min(L.numW || (W - 6), W - 6);
        var bx2 = L.brand.centered ? L.brand.cx - bw2 / 2 : L.brand.x;
        o.push('<path d="M' + r2(bx2) + ' ' + r2(L.brand.ySingle + 1.35) + 'h' + r2(bw2) +
          '" stroke="' + cyb + '" stroke-width="0.3" opacity="0.95"/>');
      }
      /* QR corner brackets */
      if (hasQr && L.qr) {
        var qbx = L.qr.x - 0.9, qby = L.qr.y - 0.9, qbs = L.qr.w + 1.8, bl = 2.1;
        [[qbx, qby, 1, 1], [qbx + qbs, qby, -1, 1],
         [qbx, qby + qbs, 1, -1], [qbx + qbs, qby + qbs, -1, -1]].forEach(function (k) {
          o.push('<path d="M' + r2(k[0] + k[2] * bl) + ' ' + r2(k[1]) + 'H' + r2(k[0]) +
            'V' + r2(k[1] + k[3] * bl) + '" fill="none" stroke="' + cyb +
            '" stroke-width="0.4"/>');
        });
      }
    }

    /* ——— 4d. TEXT PILLS ON FULL-BLEED ART ———
       Busy background art swallowed every text row (owner: "I can't even read
       any of the text"). On full-bleed layouts each text zone gets a rounded
       foil pill: the fill is the foilpatch declaration, so it blocks the art's
       ink and PRINTS as bare silver — the banner treatment from the owner's
       reference mocks, not a screen-only cheat. The thin ink keyline is what
       separates pill from art on a light background. Window layouts skip all
       of this; their type already sits on clean foil. */
    /* bg-zone layouts (singles AND folds) REPLACE the pills with the panel
       system below; pills remain for full-bleed customer artwork only
       (f.image, no design.bg) */
    var artBehindText = !L.bgz && !L.topfold && !L.duo && !!((L.art.full && img) || bgImg);
    /* BANNERS OVER ART (owner 2026-08-10): no pills, no panels — white type
       with a contrasting ink OUTLINE straight over the art (applied to every
       <text> in the outline pass at the end of build). The art stays fully
       visible; the QR keeps its scan patch because physics demands it. */
    /* bannerOutline / zoneOutline / gLevel / lightGround / INKT are all
       settled in the GROUND LUMINANCE block above - the defs needed them. */
    if (bannerOutline) TXT = '#ffffff';
    /* the white FURNITURE - corner brackets, column rules, diamonds, the motif,
       the sub-lines - is drawn with TXT from here down, so flipping it here is
       what keeps the seal one design instead of two halves. ONLY on the banner
       branch: a bg zone keeps its knockout, and its furniture with it. */
    if (lightGround && bannerOutline) TXT = INKT;
    function pillRect(cx2, yTop, w2, h2) {
      o.push('<rect x="' + r2(cx2 - w2 / 2) + '" y="' + r2(yTop) + '" width="' + r2(w2) +
        '" height="' + r2(h2) + '" rx="' + r2(h2 / 2) +
        '" fill="url(#' + id + '_foilpatch)" data-holo="1" stroke="' + INK +
        '" stroke-width="0.14" stroke-opacity="0.55"/>');
    }
    if (artBehindText && L.eyebrow && L.serial) {
      if (!noSerial) {
        /* one banner spans eyebrow + serial, capped clear of the microtext */
        var pTop = L.eyebrow.y - L.eyebrow.size - 0.7;
        var pBot = L.serial.y + 1.0;
        if (L.micro && L.micro.y) pBot = Math.min(pBot, L.micro.y - 1.2);
        pillRect(L.numCx, pTop, Math.min(L.numW + 2.2, W - 2.6), pBot - pTop);
      }
      /* the domain microtext still prints on a plain sticker, so its slim
         pill stays either way */
      if (L.micro && L.micro.y && q >= 0.8) {
        pillRect(L.micro.x + L.micro.w / 2, L.micro.y - 1.45,
                 Math.min(L.micro.w * 0.72, W - 4), 2.0);
      }
    }

    /* ——— 4e. BG-ZONE PANEL ———
       One translucent black panel with a thin accent outline and cut corners:
       portrait carries eyebrow + serial, landscape carries name + eyebrow +
       serial. Translucent ink prints as tinted ink; the white type inside
       knocks out to bare foil. */
    /* The PORTRAIT panel exists only to carry eyebrow + serial — with no
       serial it would be an empty box parked across the customer's art (the
       logo-frame lesson above), so it goes with them. The LANDSCAPE panel
       carries the brand name too and stays; its freed serial zone simply
       stays panel, per the no-layout-changes rule. */
    /* "Portrait bg-zone stack" used to be spelled `L.topH`, borrowing the
       band's height as a proxy for which branch built this layout. That held
       only while the band was always drawn. The no-brand reclaim legitimately
       sets topH to 0, and the proxy would then read "not the portrait stack"
       and hand a no-serial + no-brand seal the exact empty box this guard
       exists to prevent. Ask the real question instead — the same three
       values the branch chain itself selects on. Identical today for all
       five shapes: portrait bg single (had topH) is the only one it admits,
       landscape bg single is L.land, every bg fold sets L.fold, and duo
       never sets L.bgz at all. */
    if ((L.bgz || L.duo) && L.panel &&
        !(noSerial && L.bgz && !L.land && !L.fold)) {
      o.push('<path d="' + cutRect(L.panel.x, L.panel.y, L.panel.w, L.panel.h, L.panel.c) +
             '" fill="' + INK + '" fill-opacity="' + _zo.pn + '" stroke="' + accent +
             '" stroke-width="0.18"/>');
      _backZone(L.panel.x, L.panel.y, L.panel.w, L.panel.h,
                [[0, +_zo.pn], [1, +_zo.pn]]);
    }

    /* ——— 5. BRAND LOCKUP ——— */
    /* The mark slot appears when the design asks for a logo OR the customer
       supplied one — that is what lets an artwork seal swap its house shield
       for the customer's own mark. */
    /* the featured-logo slot: centred in the art window and FULL WIDTH by
       default (owner rule 2026-07-28: "scale to full max width within the
       container — they all want the logo in the middle"). The size slider
       only shrinks from there; injectLogo contain-fits the image inside the
       slot, so a wide wordmark spans the window and a tall mark caps at the
       window height without ever overflowing. */
    /* On a full-bleed window the slot is confined to L.logoSafe — the space
       between the brand and the serial block — because background art may
       cover the die but the logo must never cover the identity or number. */
    /* bg zones: the logo box IS the framed window, every shape. Inset was 10%
       per side - a wide wordmark got only 80% of the window and read small
       (owner 2026-08-28, ss1059: "make his logo wider"). 4% still keeps
       ~0.5mm of air off the accent frame at this window size. */
    /* full window (owner 2026-08-29, ss1061: "logo full width") - the frame
       this inset used to clear is gone */
    var lbox = L.bgz
      ? { x: L.art.x, y: L.art.y, w: L.art.w, h: L.art.h }
      : ((L.art.full && L.logoSafe) ? L.logoSafe : L.art);
    var lscale = Math.max(0.5, Math.min(1.6, parseFloat(f.logo_scale) || 1));
    /* 0.99, not 0.96: the 4% inset was invisible padding inside a frame
       that already has its own, and on the banner it cost real size
       (owner: "enlarge the actual artwork inside the blue frame"). */
    var _si = L.deco ? 0.99 : 0.96;
    var slotW = lbox.w * _si * lscale, slotH = lbox.h * _si * lscale;
    slotW = Math.min(slotW, lbox.w * _si); slotH = Math.min(slotH, lbox.h * _si);
    var slotX = lbox.x + (lbox.w - slotW) / 2, slotY = lbox.y + (lbox.h - slotH) / 2;
    /* ——— LOGO BACKING: legibility over a PHOTOGRAPH, on foil, with no
       white ink ———
       A mark dropped straight onto a customer's picture has two ways to
       vanish, and the STOCK decides which one:
         · a LIGHT mark prints as NOTHING — bare foil — so over a bright
           part of the photo there is nothing to see it against;
         · a DARK mark is heavy ink, so over a dark part of the photo it is
           ink on ink.
       So over a background the mark always gets a container, and the
       container is the OPPOSITE POLARITY to the mark. f.logo_plate is the
       builder's existing "this mark is mostly light" call (the same 45%
       dark-pixel threshold the print sidecar re-runs for link-attached
       logos), so this needs no new field:
         light mark -> translucent ink panel, cut corners + accent hairline:
                       the same furniture as the serial panel, and the mark
                       knocks out of it to live rainbow foil.
         dark  mark -> FOIL PATCH — unprinted stock, the brightest thing this
                       product can produce — with a hairline keyline to cut it
                       from the photo. Dark ink on bare silver is the highest
                       contrast available on this material.
       With NO background nothing changes: only a light mark gets the old
       opaque plate, a dark one still prints straight on the foil.
       Returns the fraction to inset the logo anchor by, so the mark never
       kisses the edge of whatever was drawn behind it. */
    function logoBacking(bx, by, bw, bh, rx) {
      if (!bgImg) {
        if (!f.logo_plate) return 0;
        o.push('<rect x="' + r2(bx) + '" y="' + r2(by) + '" width="' + r2(bw) +
               '" height="' + r2(bh) + '" rx="' + rx + '" fill="#10131c"/>');
        return 0.07;
      }
      var cc = Math.min(bw, bh) * 0.14;
      if (f.logo_plate) {
        o.push('<path d="' + cutRect(bx, by, bw, bh, cc) + '" fill="' + INK +
               '" fill-opacity="0.62" stroke="' + accent + '" stroke-width="0.18"/>');
      } else {
        o.push('<path d="' + cutRect(bx, by, bw, bh, cc) + '" fill="url(#' + id +
               '_foilpatch)" data-holo="1" stroke="' + INK +
               '" stroke-width="0.16" stroke-opacity="0.6"/>');
      }
      /* the banner bands are barely 7mm tall — 9% a side of that is real
         logo size (owner 2026-08-18: "so small when printed") */
      return L.topfold ? 0.04 : 0.09;
    }
    function markAnchor(bx, by, bw, bh, rx, cover) {
      /* cover=true: WINDOW slots on every shape (owner 2026-08-18: the
         banner fix was right — "the other templates … have a bunch of
         white space around them"). The image fills the whole box (slice),
         so the backing patch and its inset ring are skipped — the image
         covers them anyway. Corner marks (logo_mode 'mark') keep the
         contain fit + backing: a tiny cropped mark is worse than a tiny
         framed one. */
      var pin = cover ? 0 : logoBacking(bx, by, bw, bh, rx);
      o.push('<rect class="ss-mark" x="' + r2(bx + bw * pin) + '" y="' +
             r2(by + bh * pin) + '" width="' + r2(bw * (1 - pin * 2)) +
             '" height="' + r2(bh * (1 - pin * 2)) +
             '" rx="0.5" fill="none" stroke="none"' +
             (cover ? ' data-cover="1"' : '') + '/>');
    }
    if (logoInWindow && !(L.topfold && L.logoBleed)) {
      /* the SAME flag the window frame is gated on, so the frame and the thing
         it frames can never disagree */
      /* invisible anchor — injectLogo() reads its bbox and drops the image
         in as a SIBLING, so wrapping the anchor in the fold-flip group also
         rotates the injected logo: upright on the applied face */
      /* banner logos are excluded here: their anchor is emitted AFTER the
         plate clip closes so the logo can bleed to the die edge */
      if (L.artFlip) o.push('<g transform="' + L.artFlip + '">');
      markAnchor(slotX, slotY, slotW, slotH, 1.1, true);
      if (L.artFlip) o.push('</g>');
    } else if (!f.logo && design.supports.logo && q >= 0.8 && logoCat && !f._print) {
      /* builder preview only, LOGO-BED designs: show where the logo lands in
         the window. Corner placeholders are gone with the shield — a design
         with no logo has nothing in the top corner, the brand owns the row. */
      /* the placeholder previews in the window slot (lbox above), so it
         rides the window's own fold flip — same frame as the real logo */
      var phT = L.artFlip;
      if (phT) o.push('<g transform="' + phT + '">');
      o.push('<rect class="ss-mark" x="' + r2(slotX) + '" y="' + r2(slotY) + '" width="' +
             r2(slotW) + '" height="' + r2(slotH) + '" rx="0.5" fill="none" stroke="' + accent +
             '" stroke-width="0.16" stroke-dasharray="0.7 0.5" opacity="0.8"/>');
      o.push('<text x="' + r2(slotX + slotW / 2) + '" y="' + r2(slotY + slotH / 2 + 0.35) +
        '" font-family="Helvetica" font-size="1.3" font-weight="bold" text-anchor="middle" fill="' +
        accent + '" opacity="0.85" data-fit="' + r2(Math.max(2, slotW - 0.5)) +
        '" data-fit-hard="1">YOUR LOGO</text>');
      if (phT) o.push('</g>');
    }
    /* ——— identity text block: corner mark + brand. On the fold layout this
       whole block lives in L.identG (local coords, rotated 180°) so it reads
       upright on its applied face. Landscape: no group, die coords. ——— */
    if (L.identG) o.push('<g transform="' + L.identG + '">');
    /* hasMark, not `f.logo && !logoAsArt`: a logo the customer told us NOT
       to print ('off') must not leave an .ss-mark anchor behind either —
       injectLogo() finds anchors by class and would print it after all. */
    if (hasMark) {
      var ms = Math.max(0.7, Math.min(1.6, lscale));
      var mw = L.mark.w * ms, mh = L.mark.h * ms;
      var mx = L.mark.x + (L.mark.w - mw) / 2, my = L.mark.y + (L.mark.h - mh) / 2;
      markAnchor(mx, my, mw, mh, 0.5);
    }
    /* No shield: the lockup row is the customer's text (owner call). A logo
       in 'mark' mode still takes the corner via the ss-mark anchor above. */
    if (caps.supportsTitle !== false && brand) {
      var bx = L.brand.centered ? L.brand.cx : L.brand.x;
      if (artBehindText && L.brand) {
        var twoLine = brand.split(/\s+/).length > 1 && brand.length > 9;
        var bTop = (twoLine ? L.brand.y1 : L.brand.ySingle) - L.brand.size * 1.02;
        var bBot = (twoLine ? L.brand.y2 : L.brand.ySingle) + L.brand.size * 0.34;
        var bCx = L.brand.centered ? L.brand.cx : (L.brand.x + (L.numW || W - 6) / 2);
        pillRect(bCx, bTop, Math.min((L.numW || W - 6) + 1.6, W - 2.6), bBot - bTop);
      }
      var banchor = L.brand.centered ? ' text-anchor="middle"' : '';
      var bw = Math.max(5, (L.land ? (L.text.x + L.text.w) : W - 2.9) - L.brand.x);
      var words = brand.split(/\s+/);
      /* one long WORD can never balance across lines — split it where a
         human would: camelCase and letter/digit seams
         (BubezCardsandCollections -> Bubez Cards and Collections, ss1025);
         a token with no seams at all midpoint-breaks. */
      if (words.length === 1 && brand.length >= 13) {
        /* seam-split the RAW text — `brand` is already uppercased, and
           ALL-CAPS has no camelCase seams left to find */
        var rawB = String(f.brand || brand).trim();
        var wsp = rawB.replace(/([a-z0-9])([A-Z])/g, '$1 $2')
                      .replace(/([A-Za-z])(\d)/g, '$1 $2').trim().split(/\s+/)
                      .map(function (w) { return w.toUpperCase(); });
        words = wsp.length > 1 ? wsp
          : [brand.slice(0, Math.ceil(brand.length / 2)),
             brand.slice(Math.ceil(brand.length / 2))];
      }
      if (L.brand.threeLine && words.length >= 3) {
        /* three lines, balanced by longest-line length */
        var b3 = [1, 2], best3 = Infinity;
        for (var i3 = 1; i3 < words.length - 1; i3++) {
          for (var j3 = i3 + 1; j3 < words.length; j3++) {
            var mx3 = Math.max(words.slice(0, i3).join(' ').length,
                               words.slice(i3, j3).join(' ').length,
                               words.slice(j3).join(' ').length);
            if (mx3 < best3) { best3 = mx3; b3 = [i3, j3]; }
          }
        }
        [[0, b3[0], L.brand.y1], [b3[0], b3[1], L.brand.y2],
         [b3[1], words.length, L.brand.y3]].forEach(function (ln) {
          o.push('<text x="' + r2(bx) + '" y="' + ln[2] + '"' + banchor +
            ' font-family="Helvetica" font-size="' + r2(L.brand.size * 0.87) +
            '" font-weight="bold" letter-spacing="0.06" fill="' + TXT +
            '" data-fit="' + r2(bw) + '">' + esc(words.slice(ln[0], ln[1]).join(' ')) + '</text>');
        });
      } else if (words.length > 1 && brand.length > 9 && !L.brand.oneLine) {
        /* a three-line layout carrying only two lines centres them */
        if (L.brand.threeLine) {
          L.brand = Object.assign({}, L.brand, {
            y1: r2((L.brand.y1 + L.brand.y2) / 2),
            y2: r2((L.brand.y2 + L.brand.y3) / 2) });
        }
        /* Balance the two lines by LENGTH, not word count — splitting
           "MYSTERIOUS MIDNIGHT COLLECTIBLES AND BREAKS" at the middle word
           left a 32-char first line that blew 5mm past the die edge. */
        var mid = 1, bestLen = Infinity;
        for (var wi = 1; wi < words.length; wi++) {
          var longer = Math.max(words.slice(0, wi).join(' ').length,
                                words.slice(wi).join(' ').length);
          if (longer < bestLen) { bestLen = longer; mid = wi; }
        }
        o.push('<text x="' + r2(bx) + '" y="' + L.brand.y1 + '"' + banchor + ' font-family="Helvetica" font-size="' +
          r2(L.brand.size * 0.87) + '" font-weight="bold" letter-spacing="0.06" fill="' + TXT +
          '" data-fit="' + r2(bw) + '">' + esc(words.slice(0, mid).join(' ')) + '</text>');
        o.push('<text x="' + r2(bx) + '" y="' + L.brand.y2 + '"' + banchor + ' font-family="Helvetica" font-size="' +
          r2(L.brand.size * 0.87) + '" font-weight="bold" letter-spacing="0.06" fill="' + TXT +
          '" data-fit="' + r2(bw) + '">' + esc(words.slice(mid).join(' ')) + '</text>');
      } else {
        o.push('<text x="' + r2(bx) + '" y="' + L.brand.ySingle + '"' + banchor + ' font-family="Helvetica" font-size="' +
          L.brand.size + '" font-weight="bold" letter-spacing="0.05" fill="' + TXT +
          '" data-fit="' + r2(bw) + '">' + esc(brand) + '</text>');
      }
    }
    if (L.identG) o.push('</g>');

    /* ——— 6. SERIAL — the hero number ——— */
    var cx = r2(L.numCx);
    /* Serials with a separator (GS-0017, K7D-4QM) stand alone; only a bare
       number gets the "No." honorific. Run fraction appears only when a run
       total is passed — random pack serials carry no /12, a fraction over a
       random code reads as nonsense. */
    var serialLabel = (serial.indexOf('-') >= 0 ? '' : 'No. ') + serial + (runTotal ? '/' + runTotal : '');
    /* Both lines are centred and sized to fill the number column, so the fit
       must be HARD: these start at the 1.4mm type floor, which is exactly the
       case where fit()'s "close enough" tolerance stops shrinking and hands
       back up to 18% of overrun. Centred, that overrun splits across both
       edges — which is how the landscape-folded eyebrow ended up sitting
       precisely on the print boundary. */
    if (!noSerial) {
      /* duo halves have no room for the eyebrow (owner 2026-08-30: "drop the
         url and individually numbered ... use the space for logo and name") */
      if (!L.duo)
      o.push('<text x="' + cx + '" y="' + r2(L.eyebrow.y) + '" font-family="Helvetica" font-size="' +
        L.eyebrow.size + '" font-weight="bold" letter-spacing="0.12" text-anchor="middle" fill="' +
        (L.bgz ? '#ffffff' : accent) +
        '" data-fit="' + r2(L.numW) + '" data-fit-hard="1">INDIVIDUALLY NUMBERED</text>');
      o.push('<text x="' + cx + '" y="' + r2(L.serial.y) + '" font-family="Helvetica" font-size="' +
        L.serial.size + '" font-weight="bold" letter-spacing="0.05" text-anchor="middle" fill="' + TXT +
        '" data-fit="' + r2(L.numW) + '" data-fit-hard="1">' + esc(serialLabel) + '</text>');
    }
    /* Microtext line — 0.85mm solid type. Test prints showed 0.5mm at 75%
       opacity turning to mush on the vinyl; this size survives the press and
       the SERIALSEAL.COM branding stays readable with a loupe or a good squint.
       Thumbs skip it (aliases into a dotted line at gallery scale). */
    if (q >= 0.8 && L.micro && !L.micro.drawnByDeco && !L.duo) {
      /* centred + bold (owner 2026-07-28): with text-anchor=middle the
         whole-unit trim re-centres automatically, so the brand line sits
         dead-centre on every layout instead of hugging the left edge.
         L.micro is null on the banner — its domain rides the back band. */
      o.push(G.microtext(r2(L.micro.x + L.micro.w / 2), r2(L.micro.y), L.micro.w,
        siteWord,
        { size: L.bgz ? 0.95 : 1.15, spacing: 0.05, color: TXT, opacity: 1,
          bold: true, anchor: 'middle' }));
    }

    /* ——— 7. QR — the one white patch, for scan contrast ——— */
    var _qrLate = '';
    if (L.slotG && L.qr) o.push('<g transform="' + L.slotG + '">');
    if (hasQr && L.qr) {
      var qx = L.qr.x, qy = L.qr.y, qw = L.qr.w;
      /* unprinted stock, not paper white — the patch IS bare silver foil,
         and black modules on silver scan fine */
      /* py/ph: a patch that outgrows the square code (toploader back band).
         That patch must ESCAPE the plate clip — the clip excludes the crease
         strip, so a patch drawn in here visibly stops ~0.6mm short of the
         fold line (owner 2026-08-18: "qr still not correct"). Buffered and
         emitted after the clip closes, same trick as the banner logo bleed. */
      var _qrStr =
        '<rect x="' + r2(qx) + '" y="' + r2(L.qr.py != null ? L.qr.py : qy) +
        '" width="' + r2(qw) + '" height="' + r2(L.qr.ph || qw) +
        '" rx="0.35" fill="url(#' + id + '_foilpatch)" data-holo="1"/>' +
        '<rect x="' + r2(qx + 0.42) + '" y="' + r2(qy + 0.42) + '" width="' + r2(qw - 0.84) +
        '" height="' + r2(qw - 0.84) + '" data-qr="' + esc(qrLink) +
        '" fill="url(#' + id + '_foilpatch)" data-holo="1"/>';
      if (L.topfold && L.qr.ph) _qrLate = _qrStr;
      else o.push(_qrStr);
      /* Caption tells the truth about where this particular code goes:
         the customer's own link ('OFFICIAL LINK' — a knockoff can't point at
         the real brand's site) or, by default, this seal's registry record
         ('SCAN TO VERIFY' — we DO host that lookup now, free and public).
         cap:0 = the layout had no room for a caption (landscape folded). */
      if (L.qr.cap) {
        /* The caption follows the CODE, on every layout. It used to be forced
           to SCAN TO VERIFY inside a bg zone, which was harmless while the
           verify record was the only destination a QR could have — but a
           customer may now point the code at their own site (owner 2026-08-05),
           and 'Your Background' is itself a bg zone, so that rule would have
           printed SCAN TO VERIFY over a code that opens an Instagram profile.
           A seal that says "verify" and does not verify reads as a fake.
           `usingVerify` is true on every default seal, so this only changes
           what a customer-supplied link prints. */
        /* A BUSINESS code takes 'OFFICIAL LINK' too, and deliberately the SAME
           string as a customer-supplied link rather than one of its own.
           'SCAN TO VERIFY' would be a lie — a Business code resolves to
           whatever its owner points it at today (a product page, a drop, a
           video), and a seal that promises verification and delivers a
           marketing page reads as a fake, which is the exact impression this
           product exists to prevent. And the difference a Business code DOES
           carry — that the destination is repointable — is invisible and
           irrelevant to the person holding the card: either way the code opens
           the brand's own page and a counterfeiter cannot make theirs do that.
           So the caption says the one true thing both modes share.
           Reusing the incumbent string also means the caption is already
           proven against the hard fit budget on every layout that renders one
           (it is centred on the QR and clipped to the narrower side — see
           capW below); a longer Business-only wording would have to earn that
           again on eight layouts for no buyer benefit. */
        /* A POS code is neither: it is not a link and it verifies nothing.
           'ITEM CODE' is the truth and it is also the roomiest of the three --
           measured with this file's own _BWB table and fitSize() against the
           capW budget below, it sets at 1.05mm on the tightest captioned
           layout (duo standard landscape half) where 'SCAN TO VERIFY' sets at
           0.69mm and 'OFFICIAL LINK' at 0.79mm, and it is uncompressed on five
           of the seven layouts that draw a caption. ('PRODUCT CODE' 0.73mm and
           'SCAN TO RING UP' 0.65mm both set SMALLER than the incumbent and
           were rejected on that basis.)
           NO THIRD-PARTY MARK GOES ON THE FOIL. 'SQUARE', 'SQUARE POS',
           'SQUARE READY' are trademark uses on a physical product and read as
           endorsement. The word belongs only in storefront copy, and only as a
           compatibility statement. */
        var qcap = usingVerify ? 'SCAN TO VERIFY' : isSku ? 'ITEM CODE' : 'OFFICIAL LINK';
        /* The caption is centred on the QR and is allowed to run wider than
           it, so its budget depends on WHERE that centre sits — not on the
           QR width alone. Landscape parks the QR 1.9mm off the right edge,
           so a width-only cap printed "SCAN TO VERIF" with the Y past the
           die. Take the narrower of the two sides, and mark the fit HARD so
           the floor tolerance in fit() can't hand the margin back. */
        var ccx = qx + qw / 2, capM = 1.2;
        var capW = Math.min(qw + 2.2,
                            2 * Math.min(ccx - capM, (W - capM) - ccx));
        if (artBehindText) pillRect(ccx, L.qr.cap - 1.55, Math.min(capW + 1.4, W - 2.6), 2.1);
        o.push('<text x="' + r2(ccx) + '" y="' + r2(L.qr.cap) + '" font-family="Helvetica" ' +
          'font-size="' + (L.bgz ? 1.25 : 1.6) + '" font-weight="bold" letter-spacing="0.22" ' +
          'text-anchor="middle" fill="' + TXT +
          '" data-fit="' + r2(capW) + '" data-fit-hard="1">' + qcap + '</text>');
        /* bg-zone footer: a thin accent rule between SCAN TO VERIFY and the
           SERIALSEAL.COM line — no pill (owner spec) */
        if (L.bgz && L.rule) {
          o.push('<path d="M' + r2(L.rule.cx - L.rule.w / 2) + ' ' + r2(L.rule.y) +
                 'h' + r2(L.rule.w) + '" stroke="' + accent +
                 '" stroke-width="0.14" opacity="0.9"/>');
        }
      }
    } else if (hasMotif && L.qr) {
      /* the niche mark takes the code's slot — same geometry, so every
         layout composes exactly as if a QR were there */
      var mps = L.qr.w * 0.92;
      o.push('<g transform="translate(' + r2(L.qr.x + (L.qr.w - mps) / 2) + ' ' +
             r2(L.qr.y + (L.qr.w - mps) / 2) + ') scale(' + r2(mps / 10) + ')">' +
             MOTIFS[motifKey].d(TXT) + '</g>');
      if (L.qr.cap) {
        o.push('<text x="' + r2(L.qr.x + L.qr.w / 2) + '" y="' + r2(L.qr.cap) +
          '" font-family="Helvetica" font-size="1.3" font-weight="bold" letter-spacing="0.18" ' +
          'text-anchor="middle" fill="' + TXT +
          '" data-fit="' + r2(L.qr.w + 3) + '" data-fit-hard="1">VOID IF DAMAGED</text>');
      }
    } else {
      if (L.backG) o.push('<g transform="' + L.backG + '">');
      /* size/fit from the layout when it names them (the banner bands carry
         far larger type than the 1.35 default) — every other layout keeps
         the defaults untouched */
      o.push('<text x="' + (L.voidCx ? r2(L.voidCx) : cx) + '" y="' + r2(L.footer.y) +
        '" font-family="Helvetica" font-size="' + (L.footerSize || 1.35) + '" ' +
        'font-weight="bold" letter-spacing="0.2" text-anchor="middle" fill="' + TXT +
        '" data-fit="' + r2(L.voidFit || L.numW) + '"' +
        (L.voidHard ? ' data-fit-hard="1"' : '') + '>VOID IF DAMAGED</text>');
      if (L.backG) o.push('</g>');
    }
    if (L.slotG && L.qr) o.push('</g>');
    if (L.topfold) {
      var _tf = '';
      if (hasQr || hasMotif) {
        /* the QR/motif took the primary branch; the banner still owes its
           back-band line */
        _tf += '<text x="' + r2(L.voidCx || L.numCx) + '" y="' + r2(L.footer.y) +
          '" font-family="Helvetica" font-size="' + (L.footerSize || 1.35) +
          '" font-weight="bold" letter-spacing="0.2"' +
          ' text-anchor="middle" fill="' + TXT + '" data-fit="' + r2(L.voidFit || L.numW) +
          '">VOID IF DAMAGED</text>';
      }
      if (L.serial2) {
        /* the freed spot: the serial repeats, so the number reads from the
           back without flipping the case (owner 2026-08-10) — unless there
           is no serial at all, in which case the back band carries only the
           domain below */
        if (!noSerial) {
          _tf += '<text x="' + r2(L.serial2.cx) + '" y="' + r2(L.serial2.y) +
            '" font-family="Helvetica" font-size="' + L.serial2.size +
            '" font-weight="bold" letter-spacing="0.05" text-anchor="middle" fill="' + TXT +
            '" data-fit="' + (L.serial2.fit || 12) + '" data-fit-hard="1">' + esc(serialLabel) + '</text>';
        }
        /* the domain, directly under the back-band serial (owner 2026-08-11:
           "serial seal needs to go under the OTHER serial, not the top") —
           same rotated group, so it reads upright from the back like the
           serial above it */
        if (L.serial2Micro) {
          _tf += G.microtext(r2(L.serial2.cx), r2(L.serial2Micro.y), L.serial2Micro.w,
            siteWord,
            { size: L.serial2Micro.size, spacing: 0.05, color: TXT, opacity: 1,
              bold: true, anchor: 'middle' });
        }
      }
      if (_tf) o.push('<g transform="' + L.backG + '">' + _tf + '</g>');
    }

    o.push('</g>');

    /* back-band QR whose patch bleeds to the fold line — outside the
       plate clip for the same reason as the banner logo below */
    if (_qrLate) o.push('<g transform="' + L.slotG + '">' + _qrLate + '</g>');
    /* banner end-cap logo — outside the plate clip on purpose, see
       L.logoBleed in the topfold zone block */
    if (logoInWindow && L.topfold && L.logoBleed) {
      markAnchor(L.logoBleed.x, L.logoBleed.y, L.logoBleed.w, L.logoBleed.h, 0.5, true);
    }

    /* (The rim-marker hairline that traced the plate edge is gone — the plate
       edge against live foil already draws that line for free, and doubling it
       looked like a rendering bug at screen scale.) */

    /* ——— printed fold guide (owner 2026-07-28): folded seals get a dotted
       line AT the crease so whoever applies the seal knows exactly where to
       wrap it. Printed in muted grey ink — a guide, not part of the design —
       with inward ticks at both ends for at-a-glance affordance. Drawn last,
       in die coordinates (never inside identG/artFlip), on the bare-foil fold
       band, so it overlays nothing. Plain stroke: the print-side mapping
       leaves it untouched. */
    /* ════ DESIGN FURNITURE (owner mockup 2026-08-14) ═══════════════════
       Everything below is drawn ONLY when the layout asked for it, so the
       other four seal shapes render byte-identically. Die coordinates
       throughout; the back-band pieces re-enter L.backG themselves, because
       that band is rotated 180 and its content is authored pre-rotation.

       ⚠ TWO STRINGS FROM THE MOCKUP ARE NOT REPRODUCED VERBATIM.
       "TAMPER EVIDENT SEAL" and "SCAN TO VERIFY AUTHENTICITY" are claims this
       product is deliberately positioned away from — and the second is simply
       not true of how the seal works now: the registry was retired, so the
       code opens the CUSTOMER'S OWN link (an eBay listing, on ss1022), not a
       verification page. A seal promising "scan to verify authenticity" that
       lands on a marketplace listing is a promise we cannot keep. The
       replacements say what the seal actually does; they are one string each
       in L.deco.voidSub / L.deco.sub if the owner wants the originals. */
    if (L.deco) {
      var D = L.deco;
      var DI = (window.SS && window.SS.inkSafe) ? window.SS.inkSafe(D.ink) : D.ink;

      /* — a bracket at each corner of the band this shape calls its hero —
         the toploader's front band, the slab's SPINE. D.corner may be the
         arm length on its own (the toploader's original form, whose frame is
         the hardcoded 1.5 / 10.9 / 17.95) or a box that names all four. */
      if (D.corner) {
        var ca = D.corner, cIn = 1.5, cTop = 10.9, cBot = 17.95;
        if (typeof ca === 'object') {
          ca = D.corner.arm;
          if (D.corner.inset != null) cIn = D.corner.inset;
          cTop = D.corner.top; cBot = D.corner.bot;
        }
        var cpts = [[cIn, cTop, 1, 1], [W - cIn, cTop, -1, 1],
                    [cIn, cBot, 1, -1], [W - cIn, cBot, -1, -1]];
        for (var ci = 0; ci < cpts.length; ci++) {
          var cp = cpts[ci];
          o.push('<path d="M' + r2(cp[0] + cp[2] * ca) + ' ' + r2(cp[1]) +
                 'H' + r2(cp[0]) + 'V' + r2(cp[1] + cp[3] * ca) +
                 '" fill="none" stroke="' + TXT + '" stroke-width="0.22" stroke-linecap="square"/>');
        }
      }

      /* — a diamond where each column rule crosses the band's optical centre —
         a 5th element on the rule names that centre, because a shape whose
         rules live in DIFFERENT bands (the slab: one on the spine, one on the
         front band) cannot share one y. Absent -> the toploader's 14.42. */
      if (L.rules) {
        for (var di = 0; di < L.rules.length; di++) {
          var dx = L.rules[di][0], dr = (D.diamond || 0.34);
          var dy = (L.rules[di][4] != null) ? L.rules[di][4] : 14.42;   /* band centre */
          o.push('<path d="M' + r2(dx) + ' ' + r2(dy - dr) + 'L' + r2(dx + dr) + ' ' + r2(dy) +
                 'L' + r2(dx) + ' ' + r2(dy + dr) + 'L' + r2(dx - dr) + ' ' + r2(dy) +
                 'Z" fill="' + DI + '"/>');
        }
      }

      /* — left column: the brand gets an eyebrow, a rule and a tagline — */
      if (D.lead) {
        o.push('<text x="' + r2(L.text.x) + '" y="' + r2(D.lead.y) + '" font-family="Helvetica" font-size="' +
               D.lead.size + '" font-weight="bold" letter-spacing="' + (D.lead.track || 0.16) + '" fill="' + DI +
               '" data-fit="' + r2(L.text.w) + '" data-fit-hard="1">' + esc(D.lead.text) + '</text>');
      }
      if (D.tag) {
        var tcx = L.text.x + L.text.w / 2, tr = D.tag.rule;
        o.push('<path d="M' + r2(L.text.x) + ' ' + r2(tr) + 'h' + r2(L.text.w) +
               '" stroke="' + TXT + '" stroke-width="0.14" opacity="0.5"/>');
        o.push('<path d="M' + r2(tcx) + ' ' + r2(tr - 0.3) + 'L' + r2(tcx + 0.3) + ' ' + r2(tr) +
               'L' + r2(tcx) + ' ' + r2(tr + 0.3) + 'L' + r2(tcx - 0.3) + ' ' + r2(tr) +
               'Z" fill="' + DI + '"/>');
        o.push('<text x="' + r2(L.text.x) + '" y="' + r2(D.tag.y) + '" font-family="Helvetica" font-size="' +
               D.tag.size + '" font-weight="bold" letter-spacing="' + (D.tag.track || 0.14) + '" fill="' + TXT +
               '" opacity="0.82" data-fit="' + r2(L.text.w) + '" data-fit-hard="1">' +
               esc(D.tag.text) + '</text>');
      }

      /* — right column: rules flanking the eyebrow, and the domain's bar — */
      /* NO flanking rules on the eyebrow. The mockup has them either side of
         "INDIVIDUALLY NUMBERED", but that word pair is 13mm set in a 11.9mm
         column — measured in the browser, the right-hand rule landed ON the
         last three letters. A rule that only fits when the text is short is
         not furniture, it is a bug waiting for a longer brand. */
      /* — the logo's tile: a dark plate with an accent keyline, so a bright
         or busy logo reads as a deliberate badge instead of a photo dropped
         on foil. Drawn BEFORE nothing — the mark is painted earlier, so the
         tile has to be an outline only, not a fill, or it would cover it. */
      /* only when something is actually IN the slot — with no logo the
         keyline framed a bare patch of the design's own artwork, which
         reads as a stray box rather than a badge (browser check,
         2026-08-14) */
      /* draw it when ANYTHING occupies the slot. hasMark alone was wrong:
         it means a CORNER mark, and the banner's logo arrives in window
         mode (logoInWindow) — which is every logo order this shape has
         ever taken, so the keyline disappeared in exactly the case it
         was built for. */
      if (D.tile && L.mark && (hasMark || logoInWindow || img)) {
        o.push('<rect x="' + r2(L.mark.x - D.tile.pad) + '" y="' + r2(L.mark.y - D.tile.pad) +
               '" width="' + r2(L.mark.w + D.tile.pad * 2) + '" height="' +
               r2(L.mark.h + D.tile.pad * 2) + '" rx="' + r2(D.tile.r) +
               '" fill="none" stroke="' + DI + '" stroke-width="' + D.tile.sw + '"/>');
      }
      if (D.bar && L.micro) {
        /* L.micro.g: the slab's domain rides the BACK band, which is authored
           pre-rotation, so the bar and the wordmark both have to be drawn
           inside that rotated group or the bar prints upside down under
           upright type. The toploader's micro is plain die space and sets no
           .g, so it takes the same path it always did. */
        /* ONE line or TWO (owner 2026-09-11). FOUR things have to be true
           before the bar grows: the ladder offered a split at all (siteLines
           — i.e. the link has a store-name segment to put on top), this
           layout declared the vertical room for it (L.micro.two), the ONE
           line is actually being squeezed in this box, and BOTH halves will
           still set at the declared floor inside it. Any one of them false
           and this paints exactly what it has always painted, character for
           character.
           `one` IS THE TRIGGER, AND IT IS A SIZE, NOT A LENGTH. The question
           the owner asked ("if the url is too long") is about the printed
           line, and only this site knows the box — 12.2mm on the standard
           die, 17.8 on the large. So it asks fitSize() what the ideal one
           line (siteFull, before the ladder degraded it to the bare host)
           would SET at, and splits only below `one`. A character count
           cannot do this: measured on the large die, EBAY.COM/W x19 is 28
           characters and sets at 0.73mm while EBAY.IO/Q6TR3P is 14 and sets
           at the full 1.02. fitSize is pessimistic by ~6-8% against the real
           fit pass, so `one` 0.93 catches everything squeezed below about a
           millimetre and leaves the protected cases — all of which set at the
           authored 1.02 — untouched.
           The slab banner deliberately declares no `two`: its bar rides the
           back band between the repeated serial and the crease reserve with
           0.16mm of slack above it and 0.12 below (0.19 / 0.27 on the large
           die). A second line needs 1.16. There is no version of that. The
           standard toploader declares none either — see the budget beside
           its L.micro: the grown bar closes on the corner bracket. */
        var _T2 = (siteLines && L.micro.two) ? L.micro.two : null;
        var _c2 = _T2 ? r2(L.micro.w - (_T2.pad == null ? 1.2 : _T2.pad)) : 0;
        /* `one` is the OPT-IN, not a tweak: a layout that declares `two`
           without it gets no split at all. siteLines is now offered for any
           link with a store-name segment (the ladder no longer pre-filters on
           length), so "no `one`" must mean "never", or a future layout that
           copied `two` would start splitting every marketplace link it saw. */
        if (_T2 && !(_T2.one > 0 && fitSize(siteFull || siteWord,
              r2(L.micro.w - 1.2), 1.02, 0.1) < _T2.one)) _T2 = null;
        /* LINE 2 FOLLOWS LINE 1. The host is the supporting line and must not
           out-set the store name above it — with a fixed size2 it did exactly
           that (PawPaw: a 0.97mm name under a 1.05mm EBAY.COM, the subject
           reading as the subordinate). So line 2 is capped at 0.9x whatever
           line 1 is predicted to land on, held up by floor2 (the host is the
           string a finder actually types, so it has a higher floor than the
           name) and never above its authored size. A short name still sets
           1.45 over 1.05; PawPaw now reads 0.97 over 0.95. */
        var _t2size = function (i) { return (i && _T2.size2 != null) ? _T2.size2 : _T2.size; };
        var _t2track = function (i) { return (i && _T2.track2 != null) ? _T2.track2 : _T2.track; };
        /* _w2* and not _s2: `var _s2 = f.serial2` already lives in this
           function further down, and `var` would hand both the same slot. */
        var _w2size = 0, _w2trk = 0, _w2lo = 0;
        if (_T2) {
          var _w1size = fitSize(siteLines[0], _c2, _T2.size, _T2.track);
          _w2lo = _T2.floor2 == null ? _T2.floor : _T2.floor2;
          _w2size = r3(Math.min(_t2size(1), Math.max(_w2lo, _w1size * 0.9)));
          _w2trk = r3(_t2track(1) * _w2size / _t2size(1));
        }
        if (_T2 && !(fitsAtSize(siteLines[0], _c2, _T2.floor, _t2size(0), _t2track(0)) &&
                     fitsAtSize(siteLines[1], _c2, _w2lo, _w2size, _w2trk))) _T2 = null;
        if (L.micro.g) o.push('<g transform="' + L.micro.g + '">');
        /* THE BAR'S OWN GROUND, on light artwork only. The wordmark sits hard
           against the top edge of this bar (0.03mm of clearance on the slab),
           so the band the eye reads the letterform against leaves the bar and
           lands on the picture - measured 3.31:1 on Cosmic before any of the
           light-ground work existed. A slightly larger ink rect BEHIND the bar
           bounds it with ink instead. Behind, not a stroke on it: a stroke
           would eat 0.2mm of royal blue off a 1.5mm bar, which is the defect
           this file already fixed once. Nothing is emitted on dark ground. */
        function _barMat(bx, by, bw, bh, br) {
          if (!backGround) return;
          /* 0.40, not a hairline: it has to reach past the band the contrast
             is read over (0.35 em of a 1.02mm wordmark = 0.36mm) or the ring
             still lands on artwork and nothing is gained. */
          var g = 0.4;
          o.push('<rect x="' + r2(bx - g) + '" y="' + r2(by - g) + '" width="' + r2(bw + g * 2) +
                 '" height="' + r2(bh + g * 2) + '" rx="' + r2(br + g) +
                 '" fill="' + INKT + '" fill-opacity="0.9"/>');
        }
        if (_T2) {
          _barMat(L.micro.x, _T2.bar.y, L.micro.w, _T2.bar.h,
                  _T2.bar.r == null ? D.bar.r : _T2.bar.r);
          o.push('<rect x="' + r2(L.micro.x) + '" y="' + r2(_T2.bar.y) + '" width="' + r2(L.micro.w) +
                 '" height="' + r2(_T2.bar.h) + '" rx="' +
                 r2(_T2.bar.r == null ? D.bar.r : _T2.bar.r) + '" fill="' + DI + '"/>');
          var _mx2 = r2(L.micro.x + L.micro.w / 2);
          for (var _li = 0; _li < 2; _li++) {
            o.push('<text x="' + _mx2 + '" y="' + r2(_li ? _T2.y2 : _T2.y1) +
                   '" font-family="Helvetica" font-size="' + (_li ? _w2size : _t2size(0)) +
                   '" font-weight="bold" letter-spacing="' + (_li ? _w2trk : _t2track(0)) +
                   '" text-anchor="middle" fill="#ffffff" data-onink="1" data-fit="' +
                   _c2 + '" data-fit-hard="1">' + esc(siteLines[_li]) + '</text>');
          }
        } else {
          _barMat(L.micro.x, D.bar.y, L.micro.w, D.bar.h, D.bar.r);
          o.push('<rect x="' + r2(L.micro.x) + '" y="' + r2(D.bar.y) + '" width="' + r2(L.micro.w) +
                 '" height="' + r2(D.bar.h) + '" rx="' + r2(D.bar.r) + '" fill="' + DI + '"/>');
          /* white on the bar — the one place on this seal where ink is the
             ground and the wordmark is the hole in it */
          o.push('<text x="' + r2(L.micro.x + L.micro.w / 2) + '" y="' + r2(L.micro.y) +
                 '" font-family="Helvetica" font-size="1.02" font-weight="bold" ' +
                 'letter-spacing="0.1" text-anchor="middle" fill="#ffffff" data-onink="1" data-fit="' +
                 r2(L.micro.w - 1.2) + '" data-fit-hard="1">' + esc(siteWord) + '</text>');
        }
        if (L.micro.g) o.push('</g>');
      }
      /* needsQr: a shape may want this line only when a real code is in the
         slot — "SCAN ..." over an empty slot, or over a niche MOTIF, names
         something that is not there. Only the real hasQr will do; layout()
         is handed hasQr||hasMotif and cannot tell them apart. */
      if (D.sub && (D.sub.needsQr !== true || hasQr)) {
        o.push('<text x="' + r2(L.numCx) + '" y="' + r2(D.sub.y) + '" font-family="Helvetica" font-size="' +
               D.sub.size + '" font-weight="bold" letter-spacing="' + (D.sub.track || 0.13) + '" text-anchor="middle" fill="' +
               TXT + '" opacity="0.82" data-fit="' + r2(L.numW) + '" data-fit-hard="1">' +
               esc(D.sub.text) + '</text>');
      }

      /* — back band: a panel border, and a sub-line under each of its texts — */
      /* the back panel is NOT drawn here — it is background and is painted
         with the plate, under the QR. See section 1. Drawing it from this
         block put a grey rule through the QR's top and bottom module rows,
         which still LOOKED fine and would not scan. */
      if (L.backG && (D.voidSub || D.serialSub)) {
        o.push('<g transform="' + L.backG + '">');
        if (D.voidSub && L.footer) {
          /* the SAME fallback the VOID text itself uses (L.voidCx, else the
             number column's centre) — W/2 put this straight through the QR */
          o.push('<text x="' + r2(L.voidCx != null ? L.voidCx : L.numCx) + '" y="' +
                 r2(L.footer.y + D.voidSub.dy) + '" font-family="Helvetica" font-size="' +
                 D.voidSub.size + '" font-weight="bold" letter-spacing="0.16" text-anchor="middle" fill="' +
                 TXT + '" opacity="0.8" data-fit="' + r2(L.voidFit || 12) + '" data-fit-hard="1">' +
                 esc(D.voidSub.text) + '</text>');
        }
        /* 'SERIAL NUMBER' names the serial above it — no serial, no caption */
        if (!noSerial && D.serialSub && L.serial2) {
          o.push('<text x="' + r2(L.serial2.cx) + '" y="' + r2(L.serial2.y + D.serialSub.dy) +
                 '" font-family="Helvetica" font-size="' + D.serialSub.size +
                 '" font-weight="bold" letter-spacing="0.16" text-anchor="middle" fill="' + DI +
                 '" data-fit="' + r2(L.serial2.fit || 12) + '" data-fit-hard="1">' +
                 esc(D.serialSub.text) + '</text>');
        }
        o.push('</g>');
      }

      /* — brackets round the QR, inside the SAME rotated group the QR uses — */
      if (D.qrFrame && hasQr && L.qr) {
        if (L.slotG) o.push('<g transform="' + L.slotG + '">');
        var qa = D.qrFrame, qp = (D.qrPad != null ? D.qrPad : 0.42);
        var qx0 = L.qr.x - qp, qy0 = (L.qr.py != null ? L.qr.py : L.qr.y) - qp;
        var qx1 = L.qr.x + L.qr.w + qp;
        var qy1 = (L.qr.py != null ? L.qr.py + L.qr.ph : L.qr.y + L.qr.w) + qp;
        var qc = [[qx0, qy0, 1, 1], [qx1, qy0, -1, 1], [qx0, qy1, 1, -1], [qx1, qy1, -1, -1]];
        for (var qi = 0; qi < qc.length; qi++) {
          var qt = qc[qi];
          o.push('<path d="M' + r2(qt[0] + qt[2] * qa) + ' ' + r2(qt[1]) + 'H' + r2(qt[0]) +
                 'V' + r2(qt[1] + qt[3] * qa) + '" fill="none" stroke="' + DI +
                 '" stroke-width="0.26" stroke-linecap="square"/>');
        }
        if (L.slotG) o.push('</g>');
      }

      /* — the crease says what it is for — */
      /* D.foldAt: an explicit list, for a shape with more than one crease.
         The derived single label below cannot serve the slab — it reads
         L.fold only (so L.fold2 would never get one) and it assumes the room
         is always on the far side of the line, which on this die is where the
         brand lives. Each entry is a finished box in die coordinates:
         y/h are the PILL (white = no ink, free to cross a crease reserve),
         ty is the type's baseline (ink, and held clear of the reserve). */
      if (D.foldAt) {
        for (var fli = 0; fli < D.foldAt.length; fli++) {
          var fl = D.foldAt[fli], flx = (fl.cx != null ? fl.cx : W / 2);
          o.push('<rect x="' + r2(flx - fl.w / 2) + '" y="' + r2(fl.y) +
                 '" width="' + r2(fl.w) + '" height="' + r2(fl.h) + '" rx="' + r2(fl.h / 2) +
                 '" fill="#ffffff" opacity="0.9"/>');
          o.push('<text x="' + r2(flx) + '" y="' + r2(fl.ty) + '" font-family="Helvetica" ' +
                 'font-size="' + fl.size + '" font-weight="bold" letter-spacing="0.16" ' +
                 'text-anchor="middle" fill="' + DI + '" data-fit="' + r2(fl.fit) +
                 '" data-fit-hard="1">' + esc(D.foldLabel) + '</text>');
        }
      } else if (D.foldLabel && L.fold && L.fold.x == null) {
        /* a pill sized to the label. The full-width white slab this used to
           be read as a printing fault on any design with artwork behind it. */
        /* BELOW the crease, not straddling it (owner: "sitting almost directly
           on the dashed line and feels crowded"). The pill used to be centred
           on fold.y, so the dashes ran straight through it. It now starts just
           under the line and stops at 10.6 — clear of the 10.9 the front band
           content begins at. */
        o.push('<rect x="' + r2(W / 2 - 6.4) + '" y="' + r2(L.fold.y + 0.04) +
               '" width="12.8" height="1.02" rx="0.51" fill="#ffffff" opacity="0.9"/>');
        o.push('<text x="' + r2(W / 2) + '" y="' + r2(L.fold.y + 0.78) + '" font-family="Helvetica" ' +
               'font-size="0.66" font-weight="bold" letter-spacing="0.16" text-anchor="middle" fill="' +
               DI + '" data-fit="12.2" data-fit-hard="1">' + esc(D.foldLabel) + '</text>');
      }
    }

    if (L.rules) {
      for (var ri = 0; ri < L.rules.length; ri++) {
        var rl = L.rules[ri];
        o.push('<line x1="' + r2(rl[0]) + '" y1="' + r2(rl[1]) + '" x2="' + r2(rl[2]) +
               '" y2="' + r2(rl[3]) + '" stroke="' + TXT +
               '" stroke-width="0.16" opacity="' + (L.deco ? 0.7 : 0.45) + '"/>');   /* the refreshed banner leans on these as real column separators,
                  so they carry more weight there than the hairline every other
                  shape wants */
      }
    }
    var _folds = [];
    if (L.fold) _folds.push(L.fold);
    if (L.fold2) _folds.push(L.fold2);
    for (var _fi = 0; _fi < _folds.length; _fi++) {
      var _f = _folds[_fi];
      L.fold = _f;   /* the block below reads L.fold */
      var FG = '#8a8f9c', FW = 0.18;
      if (_f.x != null) {
        var fx = L.fold.x;
        o.push('<line x1="' + r2(fx) + '" y1="1" x2="' + r2(fx) + '" y2="' + r2(H - 1) +
               '" stroke="' + FG + '" stroke-width="' + FW +
               '" stroke-dasharray="0.75 0.55" stroke-linecap="round"/>');
        o.push('<path d="M' + r2(fx - 0.85) + ' 0.55L' + r2(fx) + ' 1.35L' + r2(fx + 0.85) +
               ' 0.55" fill="none" stroke="' + FG + '" stroke-width="' + FW + '"/>');
        o.push('<path d="M' + r2(fx - 0.85) + ' ' + r2(H - 0.55) + 'L' + r2(fx) + ' ' + r2(H - 1.35) +
               'L' + r2(fx + 0.85) + ' ' + r2(H - 0.55) +
               '" fill="none" stroke="' + FG + '" stroke-width="' + FW + '"/>');
      } else {
        var fy = L.fold.y;
        o.push('<line x1="1" y1="' + r2(fy) + '" x2="' + r2(W - 1) + '" y2="' + r2(fy) +
               '" stroke="' + FG + '" stroke-width="' + FW +
               '" stroke-dasharray="0.75 0.55" stroke-linecap="round"/>');
        o.push('<path d="M0.55 ' + r2(fy - 0.85) + 'L1.35 ' + r2(fy) + 'L0.55 ' + r2(fy + 0.85) +
               '" fill="none" stroke="' + FG + '" stroke-width="' + FW + '"/>');
        o.push('<path d="M' + r2(W - 0.55) + ' ' + r2(fy - 0.85) + 'L' + r2(W - 1.35) + ' ' + r2(fy) +
               'L' + r2(W - 0.55) + ' ' + r2(fy + 0.85) +
               '" fill="none" stroke="' + FG + '" stroke-width="' + FW + '"/>');
      }
    }

    o.push('</svg>');
    var out = o.join('');
    if (zoneOutline) {
      /* a little lighter than the banner pass: definition, not a cartoon
         keyline. Guarded to white fills so pill/ink type keeps its weight. */
      out = out.replace(/<text ([^>]*?)>/g, function (m, attrs) {
        if (m.indexOf('stroke=') > -1) return m;
        if (!/fill="#fff(?:fff)?"/i.test(m)) return m;
        var fsM = /font-size="([0-9.]+)"/.exec(m);
        var fs2 = fsM ? parseFloat(fsM[1]) : 2;
        var w3 = Math.max(0.12, Math.min(0.32, fs2 * 0.075));
        return '<text ' + attrs + ' paint-order="stroke" stroke="' + INK +
          '" stroke-width="' + r2(w3) + '" stroke-linejoin="round" stroke-opacity="0.9">';
      });
    }
    if (bannerOutline) {
      /* accent-filled text (the INDIVIDUALLY NUMBERED eyebrow) ghosts on
         art — every text on a banner-over-art prints white */
      out = out.replace(/(<text[^>]*?fill=")[^"]+(")/g, '$1#ffffff$2');
      /* outline every text element, proportional to its size, stroke UNDER
         the fill (paint-order) so the letterforms stay crisp */
      out = out.replace(/<text ([^>]*?)font-size="([0-9.]+)"([^>]*?)>/g,
        function (m, a1, fs, a2) {
          if (m.indexOf('stroke=') > -1) return m;
          var w2 = Math.max(0.14, Math.min(0.4, parseFloat(fs) * 0.09));
          return '<text ' + a1 + 'font-size="' + fs + '"' + a2 +
            ' paint-order="stroke" stroke="' + INK + '" stroke-width="' + r2(w2) +
            '" stroke-linejoin="round">';
        });
    }
    /* LAST, so it sees the finished type - including the rows the banner pass
       has just forced to the knockout. Everything above this line is the
       byte-for-byte status quo; a measured-dark ground never reaches here.
       An UNMEASURED ground DOES reach here, with flipTo empty: it gets the
       local ink backing, which is invisible on dark art, and never the flip. */
    if (backGround)
      out = lightGroundPass(out, lightGround ? INKT : '',
                            (lightGround && zoneOutline) ? _backClassAt : null,
                            gLevel === 2);
    /* the marker has done its job here and has no consumer downstream, so it
       never ships. It also must not: leaving it on would change the emitted
       string for every DARK design too, and those are required to render byte
       for byte as they did before any of this existed. */
    out = out.replace(/ data-onink="1"/g, '');
        if (L.duo && !f._duoHalf) {
      /* duplicate the finished half across the die + the scissor guide.
         Landscape: halves side by side, vertical cut at x=W. Portrait:
         halves stacked, horizontal cut at y=H. Same ids in both copies is
         tolerated by every renderer we target (first def wins). */
      var _iO = out.indexOf('>') + 1;
      var _iC = out.lastIndexOf('</svg>');
      var _inner = out.slice(_iO, _iC);
      /* SEPARATE NUMBERS PER HALF (owner 2026-08-30: "no they need
         separate"): when a second serial rides in on f.serial2, the second
         half is REBUILT with it - never string-duplicated - so each cut
         seal carries its own number. The recursive call sees f._duoHalf and
         skips this wrap; layout() runs the same duo branch, so the two
         halves cannot drift geometrically. Without serial2 (legacy jobs,
         'none' runs) both halves legitimately share f.serial. */
      var _inner2 = _inner;
      var _s2 = f.serial2;
      if (_s2 && String(_s2) !== String(f.serial || '')) {
        var _f2 = {};
        for (var _dk in f) if (Object.prototype.hasOwnProperty.call(f, _dk)) _f2[_dk] = f[_dk];
        _f2._duoHalf = true;
        _f2.serial = _s2;
        var _h2 = build(design, _f2, q);
        _inner2 = _h2.slice(_h2.indexOf('>') + 1, _h2.lastIndexOf('</svg>'));
      }
      var _vw = L.land ? r2(W * 2) : W, _vh = L.land ? H : r2(H * 2);
      var _shift = L.land ? ('translate(' + r2(W) + ' 0)') : ('translate(0 ' + r2(H) + ')');
      var _guide;
      if (L.land) {
        var _gx = r2(W);
        _guide =
          '<path d="M' + _gx + ' 0.4 V' + r2(_vh - 0.4) + '" stroke="' + INK + '" stroke-width="0.5" opacity="0.35"/>' +
          '<path d="M' + _gx + ' 0.4 V' + r2(_vh - 0.4) + '" stroke="#ffffff" stroke-width="0.28" stroke-dasharray="1.15 0.85"/>' +
          '<circle cx="' + _gx + '" cy="3.0" r="1.3" fill="#ffffff"/>' +
          '<text x="' + _gx + '" y="3.55" font-family="Helvetica" font-size="1.6" text-anchor="middle" fill="' + INK + '">\u2702</text>';
      } else {
        var _gy = r2(H);
        _guide =
          '<path d="M0.4 ' + _gy + ' H' + r2(_vw - 0.4) + '" stroke="' + INK + '" stroke-width="0.5" opacity="0.35"/>' +
          '<path d="M0.4 ' + _gy + ' H' + r2(_vw - 0.4) + '" stroke="#ffffff" stroke-width="0.28" stroke-dasharray="1.15 0.85"/>' +
          '<circle cx="3.0" cy="' + _gy + '" r="1.3" fill="#ffffff"/>' +
          '<text x="3.0" y="' + r2(_gy + 0.55) + '" font-family="Helvetica" font-size="1.6" text-anchor="middle" fill="' + INK + '">\u2702</text>';
      }
      out = '<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 ' + _vw + ' ' + _vh + '">' +
        '<g>' + _inner + '</g>' +
        '<g transform="' + _shift + '">' + _inner2 + '</g>' +
        _guide + '</svg>';
    }
    return out;
  }

  return { build: build, layout: layout, LONG: LONG, SHORT: SHORT,
           sampleLuma: sampleLuma, warmLuma: warmLuma,
           groundIsLight: groundIsLight, groundLevel: groundLevel,
           groundHi: groundHi, needAlpha: needAlpha,
           LUMA: LUMA, LUMA_RUN: LUMA_RUN,
           SIZES: SIZES, sizeOf: sizeOf,
           INK: INK, LIGHT: LIGHT, roundRect: roundRect };
})();
